export type SummitScripture = {
  reference: string;   // "Daniel 6:1-3"
  text?: string;       // short verse excerpt (1-2 sentences max)
  note?: string;       // how the preacher used it
  timestamp?: string;  // "HH:MM:SS" position in the stream
};

export type SummitQuote = {
  quote: string;
  timestamp?: string;  // "HH:MM:SS"
};

/** Localized override of a message's display fields (fr/es/creole). Arrays are
 * parallel to the base message so timestamps can be carried over by index. */
export type SummitMessageTranslation = {
  title: string;
  preacher: string;
  summary: string;
  keyPoints: string[];
  scriptures: SummitScripture[];
  quotes: SummitQuote[];
};

export type SummitMessage = {
  slug: string;            // kebab-case, unique across all days, e.g. "day-1-the-god-of-revival"
  day: number;             // 1-4
  session: "morning" | "evening";
  date: string;            // ISO "2026-07-21"
  videoId: string;         // YouTube video ID of the live stream containing this message
  startTimestamp?: string; // "HH:MM:SS" where this preaching starts within the stream
  title: string;           // the message title (from the preaching itself, not the stream title)
  preacher: string;        // e.g. "Pastor John Doe"; use "Ministering" if genuinely unidentifiable
  summary: string;         // 120-200 words, warm and faithful to the message
  keyPoints: string[];     // 4-8 short key points
  scriptures: SummitScripture[]; // anchor text first
  quotes: SummitQuote[];   // 2-5 memorable direct quotes
  translations?: Record<string, SummitMessageTranslation>;
};

/** Message with locale content applied ("en" or unknown locale returns the base). */
export function localizeSummitMessage(
  message: SummitMessage,
  locale: string,
): SummitMessage {
  const t = message.translations?.[locale];
  return t ? { ...message, ...t } : message;
}

// Node's ICU has no Haitian Creole ("ht") data, so Creole dates use a small table.
const CREOLE_WEEKDAYS = ["dimanch", "lendi", "madi", "mèkredi", "jedi", "vandredi", "samdi"];
const CREOLE_MONTHS = ["janvye", "fevriye", "mas", "avril", "me", "jen", "jiyè", "out", "septanm", "oktòb", "novanm", "desanm"];
const LOCALE_TAGS: Record<string, string> = { en: "en-CA", fr: "fr-CA", es: "es" };

/** Localized "Thursday, July 23" / "jeudi 23 juillet" / "jedi 23 jiyè" for an ISO date. */
export function formatSummitDate(date: string, locale: string): string {
  const d = new Date(`${date}T12:00:00`);
  if (locale === "creole") {
    return `${CREOLE_WEEKDAYS[d.getDay()]} ${d.getDate()} ${CREOLE_MONTHS[d.getMonth()]}`;
  }
  return new Intl.DateTimeFormat(LOCALE_TAGS[locale] ?? "en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(d);
}

export type SummitBreakdownDay = {
  day: number;        // 1-4
  label: string;      // "Day 1"
  dateLabel: string;  // "Tuesday, July 21"
  date: string;       // "2026-07-21"
  messages: SummitMessage[];
};

export const summitBreakdownDays: SummitBreakdownDay[] = [
  {
    "day": 1,
    "label": "Day 1",
    "dateLabel": "Tuesday, July 21",
    "date": "2026-07-21",
    "messages": [
      {
        "slug": "day-1-understanding-the-ministry-of-the-holy-spirit-for-soaring",
        "day": 1,
        "session": "evening",
        "date": "2026-07-21",
        "videoId": "NkcoJmpjmRs",
        "startTimestamp": "01:32:01",
        "title": "Understanding the Ministry of the Holy Spirit for Soaring",
        "preacher": "The National Pastor, Winners Chapel International Canada",
        "summary": "Opening the Soaring Eagles summit from Isaiah 40:31, the national pastor showed why the eagle is God's picture of the believer who waits on the Lord: the one creature with command of the air, the land, and the sea. He walked through seven traits of the eagle. Eagles fly alone at high altitude, they carry piercing vision and never lose focus, they eat only fresh kills, they are the only bird that loves the storm, they test before they trust, they build a forever family and mate for life, and they are exceptionally strong. Then he brought it home: only one Personality can empower a young person to live like that, the Holy Spirit. The Spirit chooses our company, reveals God's plan, feeds us daily in the Word, glides us through storms, gives discernment in relationships, teaches us to build a home, and supplies unusual strength. To receive that empowerment, be born again, fellowship constantly with the Spirit, feed on the Word, and choose your company. The night closed with a passionate altar call as many came forward to receive Christ.",
        "keyPoints": [
          "The eagle is the only animal with command of the air, the land, and the sea, and it is God's picture of the believer who waits on Him.",
          "Eagles fly alone at high altitude: beware of the company you keep, and don't flock with birds that don't share your kind of wing.",
          "Eagles have powerful vision and never lose focus; your soaring will always be pegged at the level of your vision.",
          "Eagles eat only fresh kills: feed daily on the fresh word of God, because there is a word meant for every day.",
          "Eagles are the only bird that loves the storm; the storms of life are designed to lift you higher, not to bury you.",
          "Eagles test before they trust and mate for life: test commitment in relationships and never give yourself cheap.",
          "Only the Holy Spirit, the Spirit of truth, our Comforter, Teacher, Guide, and Empowerer, can empower a young person to soar like the eagle.",
          "To be empowered for soaring: receive Jesus as Lord, keep constant fellowship with the Holy Spirit, feed your spirit with the Word, and choose your company."
        ],
        "scriptures": [
          {
            "reference": "Isaiah 40:31",
            "text": "But they that wait upon the LORD shall renew their strength; they shall mount up with wings as eagles; they shall run, and not be weary; and they shall walk, and not faint.",
            "note": "Anchor text; strength and soaring belong to those who wait on the Lord, returned to repeatedly through the message.",
            "timestamp": "01:35:12"
          },
          {
            "reference": "Exodus 19:4",
            "text": "Ye have seen what I did unto the Egyptians, and how I bare you on eagles' wings, and brought you unto myself.",
            "note": "God Himself flies His people on eagles' wings; be careful whose wings you fly with.",
            "timestamp": "01:39:26"
          },
          {
            "reference": "Proverbs 30:18-19",
            "text": "There be three things which are too wonderful for me, yea, four which I know not: the way of an eagle in the air.",
            "note": "How the eagle glides through the storm is a wonder even to the human mind; storms are meant to elevate you.",
            "timestamp": "01:43:49"
          },
          {
            "reference": "Deuteronomy 32:11-12",
            "text": "As an eagle stirreth up her nest, fluttereth over her young, spreadeth abroad her wings, taketh them, beareth them on her wings: so the LORD alone did lead him.",
            "note": "Eagles build a forever family and raise their young together; a picture of a godly home.",
            "timestamp": "01:49:13"
          },
          {
            "reference": "John 16:13",
            "text": "Howbeit when he, the Spirit of truth, is come, he will guide you into all truth... and he will shew you things to come.",
            "note": "The Holy Spirit guides us into profitable company and shows us things to come.",
            "timestamp": "01:53:33"
          },
          {
            "reference": "Joel 2:28",
            "text": "And it shall come to pass afterward, that I will pour out my spirit upon all flesh... your young men shall see visions.",
            "note": "The Spirit opens young people up to God's plan and gives them vision like the eagle's sight.",
            "timestamp": "01:54:37"
          },
          {
            "reference": "Acts 1:8",
            "text": "But ye shall receive power, after that the Holy Ghost is come upon you: and ye shall be witnesses unto me.",
            "note": "Daily feeding on the Word by the Spirit comes with empowerment.",
            "timestamp": "01:57:47"
          },
          {
            "reference": "Isaiah 43:2",
            "text": "When thou passest through the waters, I will be with thee; and through the rivers, they shall not overflow thee.",
            "note": "The Holy Spirit sees us through storms the way the eagle glides through them; Joseph cited as an example.",
            "timestamp": "01:58:53"
          },
          {
            "reference": "Job 28:7",
            "text": "There is a path which no fowl knoweth, and which the vulture's eye hath not seen.",
            "note": "The Spirit makes you stand out among your peers; only the eagle's eye perceives the hidden path.",
            "timestamp": "02:05:18"
          },
          {
            "reference": "Genesis 39:9",
            "text": "How then can I do this great wickedness, and sin against God?",
            "note": "Joseph chose purity over pleasure and ended up prime minister; purity gave him a place.",
            "timestamp": "02:08:34"
          },
          {
            "reference": "Daniel 1:8",
            "text": "But Daniel purposed in his heart that he would not defile himself with the portion of the king's meat, nor with the wine which he drank.",
            "note": "By the Spirit's help you can say no to the evil norm and become ten times better.",
            "timestamp": "02:09:41"
          },
          {
            "reference": "Zechariah 4:6",
            "text": "Not by might, nor by power, but by my spirit, saith the LORD of hosts.",
            "note": "Soaring is by the Spirit alone, not human strength.",
            "timestamp": "02:10:45"
          },
          {
            "reference": "John 3:3",
            "text": "Verily, verily, I say unto thee, Except a man be born again, he cannot see the kingdom of God.",
            "note": "Empowerment for soaring begins with the new birth; the basis of the closing altar call.",
            "timestamp": "02:11:49"
          },
          {
            "reference": "Romans 8:14",
            "text": "For as many as are led by the Spirit of God, they are the sons of God.",
            "note": "Constant fellowship with the Holy Spirit keeps you constantly guided.",
            "timestamp": "02:12:54"
          },
          {
            "reference": "John 6:63",
            "text": "It is the spirit that quickeneth; the flesh profiteth nothing: the words that I speak unto you, they are spirit, and they are life.",
            "note": "Feed your spirit daily with the word of God, because we look like what we eat.",
            "timestamp": "02:13:58"
          }
        ],
        "quotes": [
          {
            "quote": "Never lose focus. Without vision, you cannot soar. Your soaring will be pegged at your vision.",
            "timestamp": "01:41:39"
          },
          {
            "quote": "Eagles actually love storms. The storms of life are meant to elevate you. Don't hide from them.",
            "timestamp": "01:42:43"
          },
          {
            "quote": "If he catches you simply, he will use you simply. If he sweats to get you, it is a sign of commitment.",
            "timestamp": "01:48:08"
          },
          {
            "quote": "Young ladies, young men, don't eat the honey before the moon.",
            "timestamp": "02:06:23"
          },
          {
            "quote": "To soar like the eagle, the Holy Spirit is your only option. There is no other personality that can make you soar like the eagle.",
            "timestamp": "02:13:58"
          }
        ],
        "translations": {
          "creole": {
            "title": "Konprann Ministè Lespri Sen an pou Vole Wo",
            "preacher": "Pastè Nasyonal la, Winners Chapel International Canada",
            "summary": "Pou louvri somè Malfini k ap Vole Wo a apati Ezayi 40:31, pastè nasyonal la te montre poukisa malfini an se imaj Bondye bay pou kwayan k ap tann Seyè a: sèl kreyati ki gen kontwòl anlè, sou tè a ak sou lanmè a. Li te pase sou sèt karakteristik malfini an. Malfini vole pou kont yo byen wo nan syèl la, yo gen yon vizyon pèsan e yo pa janm pèdi konsantrasyon, yo manje sèlman vyann frè yo fenk pran, se sèl zwazo ki renmen tanpèt, yo teste anvan yo fè konfyans, yo bati yon fanmi pou tout lavi e yo rete ak yon sèl patnè, epi yo gen yon fòs estrawòdinè. Apre sa, li mennen li lakay nou: se yon sèl Pèsonalite ki ka bay yon jèn pouvwa pou viv konsa, se Lespri Sen an. Lespri a chwazi konpayi nou, li revele plan Bondye, li nouri nou chak jou nan Pawòl la, li fè nou plane atravè tanpèt yo, li bay disènman nan relasyon, li anseye nou bati yon fwaye, epi li bay yon fòs ki pa òdinè. Pou resevwa pouvwa sa a: fèt yon lòt fwa, rete nan kominyon san rete ak Lespri a, nouri ou ak Pawòl la, epi chwazi konpayi ou. Sware a te fèmen ak yon apèl sou lotèl plen pasyon, kote anpil moun te vin bay Kris lavi yo.",
            "keyPoints": [
              "Malfini an se sèl bèt ki gen kontwòl anlè, sou tè a ak sou lanmè a, epi se imaj Bondye bay pou kwayan k ap tann li.",
              "Malfini vole pou kont yo byen wo: veye ki moun w ap mache avèk yo, epi pa vole ak zwazo ki pa gen menm kalite zèl avèk ou.",
              "Malfini gen yon vizyon pwisan e yo pa janm pèdi konsantrasyon; wotè w ap vole a ap toujou mare ak nivo vizyon ou.",
              "Malfini manje sèlman vyann frè: nouri ou chak jou ak pawòl Bondye ki frè a, paske gen yon pawòl ki fèt pou chak jou.",
              "Malfini se sèl zwazo ki renmen tanpèt; tanpèt lavi yo fèt pou leve ou pi wo, yo pa fèt pou antere ou.",
              "Malfini teste anvan yo fè konfyans e yo rete ak yon sèl patnè pou lavi: teste angajman nan relasyon yo, epi pa janm lage tèt ou bon mache.",
              "Se sèl Lespri Sen an, Lespri verite a, Konsolatè nou, Pwofesè nou, Gid nou ak Sous fòs nou, ki ka bay yon jèn pouvwa pou vole wo tankou malfini an.",
              "Pou resevwa pouvwa pou vole wo: resevwa Jezi kòm Seyè, kenbe yon kominyon konstan ak Lespri Sen an, nouri lespri ou ak Pawòl la, epi chwazi konpayi ou."
            ],
            "scriptures": [
              {
                "reference": "Ezayi 40:31",
                "text": "Men moun k ap tann Seyè a ap jwenn fòs nouvo; y ap monte anlè ak zèl tankou malfini; y ap kouri san yo pa bouke, y ap mache san yo pa febli.",
                "note": "Tèks prensipal la; fòs ak vole wo se pou moun k ap tann Seyè a; mesaj la te retounen sou li plizyè fwa.",
                "timestamp": "01:35:12"
              },
              {
                "reference": "Egzòd 19:4",
                "text": "Nou wè sa mwen te fè moun peyi Lejip yo, ak kijan mwen te pote nou sou zèl malfini pou mwen mennen nou vin jwenn mwen.",
                "note": "Bondye li menm pote pèp li sou zèl malfini; fè atansyon ak ki zèl w ap vole.",
                "timestamp": "01:39:26"
              },
              {
                "reference": "Pwovèb 30:18-19",
                "text": "Gen twa bagay ki depase mwen, wi kat mwen pa ka konprann: chemen malfini an nan syèl la.",
                "note": "Jan malfini an plane nan mitan tanpèt la se yon mèvèy menm pou lespri lòm; tanpèt yo fèt pou leve ou.",
                "timestamp": "01:43:49"
              },
              {
                "reference": "Detewonòm 32:11-12",
                "text": "Tankou yon malfini k ap souke nich li, k ap plane sou pitit li yo, ki louvri zèl li pou li pran yo, ki pote yo sou zèl li: se konsa Seyè a pou kont li te mennen pèp li a.",
                "note": "Malfini bati yon fanmi pou tout tan e yo elve pitit yo ansanm; yon imaj yon fwaye ki mache ak Bondye.",
                "timestamp": "01:49:13"
              },
              {
                "reference": "Jan 16:13",
                "text": "Men lè li menm, Lespri verite a, va vini, l ap gide nou nan tout verite... epi l ap fè nou konnen bagay ki gen pou rive.",
                "note": "Lespri Sen an gide nou nan konpayi ki itil epi li montre nou bagay ki gen pou vini.",
                "timestamp": "01:53:33"
              },
              {
                "reference": "Jowèl 2:28",
                "text": "Apre sa, m ap vide Lespri mwen sou tout moun... jenn gason nou yo va gen vizyon.",
                "note": "Lespri a ouvri jèn yo sou plan Bondye epi li ba yo vizyon tankou je malfini an.",
                "timestamp": "01:54:37"
              },
              {
                "reference": "Travay 1:8",
                "text": "Men n ap resevwa pouvwa lè Lespri Sen an va desann sou nou, epi n ap sèvi mwen temwen.",
                "note": "Manje Pawòl la chak jou nan men Lespri a vini ak pouvwa.",
                "timestamp": "01:57:47"
              },
              {
                "reference": "Ezayi 43:2",
                "text": "Lè w ap pase nan dlo, m ap avèk ou; lè w ap janbe rivyè, yo p ap kouvri ou.",
                "note": "Lespri Sen an fè nou travèse tanpèt yo menm jan malfini an plane nan mitan yo; Jozèf site kòm egzanp.",
                "timestamp": "01:58:53"
              },
              {
                "reference": "Jòb 28:7",
                "text": "Gen yon chemen okenn zwazo pa konnen, yon chemen je votou pa janm wè.",
                "note": "Lespri a fè ou kanpe apa nan mitan kanmarad ou yo; se sèl je malfini an ki wè chemen kache a.",
                "timestamp": "02:05:18"
              },
              {
                "reference": "Jenèz 39:9",
                "text": "Kijan mwen ta fè yon gwo mechanste konsa, pou mwen fè peche kont Bondye?",
                "note": "Jozèf te chwazi pite olye de plezi, epi li fini premye minis; pite a te ba li yon plas.",
                "timestamp": "02:08:34"
              },
              {
                "reference": "Danyèl 1:8",
                "text": "Men Danyèl te pran desizyon nan kè li pou li pa avili tèt li ak manje wa a, ni ak diven wa a t ap bwè a.",
                "note": "Ak èd Lespri a, ou ka di non a move abitid tout moun ap swiv la, epi vin dis fwa pi bon.",
                "timestamp": "02:09:41"
              },
              {
                "reference": "Zakari 4:6",
                "text": "Se pa ak fòs, ni ak pouvwa, men se ak Lespri mwen, se Seyè ki gen tout pouvwa a ki di sa.",
                "note": "Vole wo fèt pa Lespri a sèlman, pa ak fòs lòm.",
                "timestamp": "02:10:45"
              },
              {
                "reference": "Jan 3:3",
                "text": "Sa m ap di ou la a, se vre wi: si yon moun pa fèt yon lòt fwa, li pa ka wè wayòm Bondye a.",
                "note": "Pouvwa pou vole wo kòmanse ak nouvo nesans lan; se sou baz sa a apèl sou lotèl la te fèt.",
                "timestamp": "02:11:49"
              },
              {
                "reference": "Women 8:14",
                "text": "Paske tout moun Lespri Bondye ap dirije, se pitit Bondye yo ye.",
                "note": "Kominyon konstan ak Lespri Sen an kenbe ou anba direksyon li tout tan.",
                "timestamp": "02:12:54"
              },
              {
                "reference": "Jan 6:63",
                "text": "Se Lespri a ki bay lavi; lachè pa itil anyen. Pawòl mwen di nou yo, se lespri yo ye, se lavi yo ye.",
                "note": "Nouri lespri ou chak jou ak pawòl Bondye a, paske nou sanble ak sa nou manje.",
                "timestamp": "02:13:58"
              }
            ],
            "quotes": [
              {
                "quote": "Pa janm pèdi konsantrasyon. San vizyon, ou pa ka vole wo. Wotè w ap vole a ap mare ak vizyon ou.",
                "timestamp": "01:41:39"
              },
              {
                "quote": "Malfini yo renmen tanpèt toutbon. Tanpèt lavi yo fèt pou leve ou pi wo. Pa kache pou yo.",
                "timestamp": "01:42:43"
              },
              {
                "quote": "Si li jwenn ou fasil, l ap sèvi avèk ou nenpòt jan. Si li swe pou li jwenn ou, se yon siy angajman.",
                "timestamp": "01:48:08"
              },
              {
                "quote": "Jenn fi, jenn gason, pa manje siwo myèl la anvan lin myèl la.",
                "timestamp": "02:06:23"
              },
              {
                "quote": "Pou vole wo tankou malfini an, Lespri Sen an se sèl opsyon ou. Pa gen okenn lòt pèsonalite ki ka fè ou vole tankou malfini an.",
                "timestamp": "02:13:58"
              }
            ]
          },
          "es": {
            "title": "Entendiendo el ministerio del Espíritu Santo para remontar vuelo",
            "preacher": "El Pastor Nacional, Winners Chapel International Canada",
            "summary": "Abriendo el summit Águilas que Vuelan Alto desde Isaías 40:31, el pastor nacional mostró por qué el águila es el retrato que Dios usa del creyente que espera en el Señor: la única criatura con dominio del aire, la tierra y el mar. Recorrió siete rasgos del águila. Las águilas vuelan solas a gran altura, poseen una vista penetrante y nunca pierden el enfoque, comen solo presas frescas, son la única ave que ama la tormenta, prueban antes de confiar, forman una familia para siempre y se emparejan de por vida, y son excepcionalmente fuertes. Luego lo aterrizó: solo una Personalidad puede capacitar a un joven para vivir así, el Espíritu Santo. El Espíritu escoge nuestras compañías, revela el plan de Dios, nos alimenta a diario en la Palabra, nos hace planear a través de las tormentas, da discernimiento en las relaciones, nos enseña a edificar un hogar y suministra fuerza fuera de lo común. Para recibir esa capacitación: nace de nuevo, mantén comunión constante con el Espíritu, aliméntate de la Palabra y escoge tus compañías. La noche cerró con un apasionado llamado al altar mientras muchos pasaban al frente a recibir a Cristo.",
            "keyPoints": [
              "El águila es el único animal con dominio del aire, la tierra y el mar, y es el retrato que Dios usa del creyente que espera en Él.",
              "Las águilas vuelan solas a gran altura: cuídate de las compañías que mantienes, y no vueles en bandada con aves que no comparten tu clase de alas.",
              "Las águilas tienen una vista poderosa y nunca pierden el enfoque; tu vuelo siempre estará limitado al nivel de tu visión.",
              "Las águilas comen solo presas frescas: aliméntate a diario de la palabra fresca de Dios, porque hay una palabra destinada para cada día.",
              "Las águilas son la única ave que ama la tormenta; las tormentas de la vida están diseñadas para elevarte, no para sepultarte.",
              "Las águilas prueban antes de confiar y se emparejan de por vida: pon a prueba el compromiso en las relaciones y nunca te entregues barato.",
              "Solo el Espíritu Santo, el Espíritu de verdad, nuestro Consolador, Maestro, Guía y Capacitador, puede empoderar a un joven para volar como el águila.",
              "Para ser capacitado para remontar vuelo: recibe a Jesús como Señor, mantén comunión constante con el Espíritu Santo, alimenta tu espíritu con la Palabra y escoge tus compañías."
            ],
            "scriptures": [
              {
                "reference": "Isaías 40:31",
                "text": "Mas los que esperan a Jehová tendrán nuevas fuerzas; levantarán las alas como águilas; correrán, y no se cansarán, caminarán, y no se fatigarán.",
                "note": "Texto ancla; la fuerza y el vuelo pertenecen a los que esperan en el Señor, retomado una y otra vez a lo largo del mensaje.",
                "timestamp": "01:35:12"
              },
              {
                "reference": "Éxodo 19:4",
                "text": "Vosotros visteis lo que hice a los Egipcios, y cómo os tomé sobre alas de águilas, y os he traído a mí.",
                "note": "Dios mismo lleva a su pueblo sobre alas de águilas; cuídate de sobre qué alas vuelas.",
                "timestamp": "01:39:26"
              },
              {
                "reference": "Proverbios 30:18-19",
                "text": "Tres cosas me son ocultas; aun tampoco sé la cuarta: el rastro del águila en el aire.",
                "note": "Cómo el águila planea a través de la tormenta es un asombro aun para la mente humana; las tormentas están destinadas a elevarte.",
                "timestamp": "01:43:49"
              },
              {
                "reference": "Deuteronomio 32:11-12",
                "text": "Como el águila despierta su nidada, revolotea sobre sus pollos, extiende sus alas, los toma, los lleva sobre sus plumas: Jehová solo le guió.",
                "note": "Las águilas forman una familia para siempre y crían juntas a sus polluelos; un retrato de un hogar piadoso.",
                "timestamp": "01:49:13"
              },
              {
                "reference": "Juan 16:13",
                "text": "Pero cuando viniere aquel Espíritu de verdad, él os guiará a toda verdad... y os hará saber las cosas que han de venir.",
                "note": "El Espíritu Santo nos guía hacia compañías provechosas y nos muestra las cosas que han de venir.",
                "timestamp": "01:53:33"
              },
              {
                "reference": "Joel 2:28",
                "text": "Y será que después de aquesto, derramaré mi Espíritu sobre toda carne... y vuestros mancebos verán visiones.",
                "note": "El Espíritu abre a los jóvenes al plan de Dios y les da visión como la vista del águila.",
                "timestamp": "01:54:37"
              },
              {
                "reference": "Hechos 1:8",
                "text": "Mas recibiréis la virtud del Espíritu Santo que vendrá sobre vosotros; y me seréis testigos.",
                "note": "Alimentarse a diario de la Palabra por el Espíritu viene acompañado de poder.",
                "timestamp": "01:57:47"
              },
              {
                "reference": "Isaías 43:2",
                "text": "Cuando pasares por las aguas, yo seré contigo; y por los ríos, no te anegarán.",
                "note": "El Espíritu Santo nos hace atravesar las tormentas como el águila planea a través de ellas; José citado como ejemplo.",
                "timestamp": "01:58:53"
              },
              {
                "reference": "Job 28:7",
                "text": "Senda que nunca la conoció ave, ni ojo de buitre la vio.",
                "note": "El Espíritu te hace sobresalir entre tus pares; solo el ojo del águila percibe la senda escondida.",
                "timestamp": "02:05:18"
              },
              {
                "reference": "Génesis 39:9",
                "text": "¿Cómo, pues, haría yo este grande mal, y pecaría contra Dios?",
                "note": "José escogió la pureza por encima del placer y terminó como primer ministro; la pureza le dio un lugar.",
                "timestamp": "02:08:34"
              },
              {
                "reference": "Daniel 1:8",
                "text": "Y Daniel propuso en su corazón de no contaminarse en la ración de la comida del rey, ni en el vino de su beber.",
                "note": "Con la ayuda del Espíritu puedes decir no a la norma corrupta y llegar a ser diez veces mejor.",
                "timestamp": "02:09:41"
              },
              {
                "reference": "Zacarías 4:6",
                "text": "No con ejército, ni con fuerza, sino con mi espíritu, ha dicho Jehová de los ejércitos.",
                "note": "Remontar vuelo es solo por el Espíritu, no por fuerza humana.",
                "timestamp": "02:10:45"
              },
              {
                "reference": "Juan 3:3",
                "text": "De cierto, de cierto te digo, que el que no naciere otra vez, no puede ver el reino de Dios.",
                "note": "La capacitación para remontar vuelo comienza con el nuevo nacimiento; la base del llamado al altar final.",
                "timestamp": "02:11:49"
              },
              {
                "reference": "Romanos 8:14",
                "text": "Porque todos los que son guiados por el Espíritu de Dios, los tales son hijos de Dios.",
                "note": "La comunión constante con el Espíritu Santo te mantiene constantemente guiado.",
                "timestamp": "02:12:54"
              },
              {
                "reference": "Juan 6:63",
                "text": "El espíritu es el que da vida; la carne nada aprovecha: las palabras que yo os he hablado, son espíritu y son vida.",
                "note": "Alimenta tu espíritu a diario con la palabra de Dios, porque nos parecemos a lo que comemos.",
                "timestamp": "02:13:58"
              }
            ],
            "quotes": [
              {
                "quote": "Nunca pierdas el enfoque. Sin visión no puedes volar alto. Tu vuelo estará limitado a tu visión.",
                "timestamp": "01:41:39"
              },
              {
                "quote": "A las águilas en realidad les encantan las tormentas. Las tormentas de la vida están destinadas a elevarte. No te escondas de ellas.",
                "timestamp": "01:42:43"
              },
              {
                "quote": "Si te consigue fácil, te usará fácil. Si suda para conseguirte, es señal de compromiso.",
                "timestamp": "01:48:08"
              },
              {
                "quote": "Señoritas, jóvenes: no se coman la miel antes de la luna.",
                "timestamp": "02:06:23"
              },
              {
                "quote": "Para volar como el águila, el Espíritu Santo es tu única opción. No hay otra personalidad que pueda hacerte volar como el águila.",
                "timestamp": "02:13:58"
              }
            ]
          },
          "fr": {
            "title": "Comprendre le ministère du Saint-Esprit pour s'élever",
            "preacher": "Le pasteur national, Winners Chapel International Canada",
            "summary": "Ouvrant le sommet Soaring Eagles à partir d'Ésaïe 40:31, le pasteur national a montré pourquoi l'aigle est l'image que Dieu donne du croyant qui se confie en lui : la seule créature qui domine les airs, la terre et la mer. Il a parcouru sept traits de l'aigle. Les aigles volent seuls à haute altitude, ils possèdent une vision perçante et ne perdent jamais leur concentration, ils ne mangent que des proies fraîches, ils sont le seul oiseau qui aime la tempête, ils éprouvent avant de faire confiance, ils bâtissent une famille pour toujours et restent fidèles à vie, et ils sont exceptionnellement forts. Puis il a conclu : une seule Personnalité peut rendre un jeune capable de vivre ainsi, le Saint-Esprit. L'Esprit choisit nos fréquentations, révèle le plan de Dieu, nous nourrit chaque jour dans la Parole, nous fait planer à travers les tempêtes, donne le discernement dans les relations, nous apprend à bâtir un foyer et fournit une force hors du commun. Pour recevoir cette puissance : naître de nouveau, entretenir une communion constante avec l'Esprit, se nourrir de la Parole et choisir ses fréquentations. La soirée s'est achevée par un appel vibrant, et beaucoup se sont avancés pour recevoir Christ.",
            "keyPoints": [
              "L'aigle est le seul animal qui domine les airs, la terre et la mer, et il est l'image que Dieu donne du croyant qui se confie en lui.",
              "Les aigles volent seuls à haute altitude : méfiez-vous de vos fréquentations, et ne volez pas avec des oiseaux qui n'ont pas vos ailes.",
              "Les aigles ont une vision puissante et ne perdent jamais leur concentration; votre envol sera toujours à la mesure de votre vision.",
              "Les aigles ne mangent que des proies fraîches : nourrissez-vous chaque jour de la parole fraîche de Dieu, car il y a une parole prévue pour chaque jour.",
              "Les aigles sont le seul oiseau qui aime la tempête; les tempêtes de la vie sont conçues pour vous élever, pas pour vous ensevelir.",
              "Les aigles éprouvent avant de faire confiance et restent fidèles à vie : éprouvez l'engagement dans les relations et ne vous donnez jamais à bas prix.",
              "Seul le Saint-Esprit, l'Esprit de vérité, notre Consolateur, Enseignant, Guide et Source de puissance, peut rendre un jeune capable de s'élever comme l'aigle.",
              "Pour être rendu capable de s'élever : recevoir Jésus comme Seigneur, entretenir une communion constante avec le Saint-Esprit, nourrir son esprit de la Parole et choisir ses fréquentations."
            ],
            "scriptures": [
              {
                "reference": "Ésaïe 40:31",
                "text": "Mais ceux qui se confient en l'Éternel renouvellent leur force. Ils prennent le vol comme les aigles; ils courent, et ne se lassent point; ils marchent, et ne se fatiguent point.",
                "note": "Texte d'ancrage; la force et l'envol appartiennent à ceux qui se confient en l'Éternel, repris tout au long du message.",
                "timestamp": "01:35:12"
              },
              {
                "reference": "Exode 19:4",
                "text": "Vous avez vu ce que j'ai fait à l'Égypte, et comment je vous ai portés sur des ailes d'aigle et amenés vers moi.",
                "note": "Dieu lui-même porte son peuple sur des ailes d'aigle; faites attention aux ailes avec lesquelles vous volez.",
                "timestamp": "01:39:26"
              },
              {
                "reference": "Proverbes 30:18-19",
                "text": "Il y a trois choses qui sont au-dessus de ma portée, même quatre que je ne puis comprendre : la trace de l'aigle dans les cieux.",
                "note": "La façon dont l'aigle plane à travers la tempête est une merveille même pour l'esprit humain; les tempêtes sont destinées à vous élever.",
                "timestamp": "01:43:49"
              },
              {
                "reference": "Deutéronome 32:11-12",
                "text": "Pareil à l'aigle qui éveille sa couvée, voltige sur ses petits, déploie ses ailes, les prend, les porte sur ses plumes. L'Éternel seul a conduit son peuple.",
                "note": "Les aigles bâtissent une famille pour toujours et élèvent leurs petits ensemble; une image du foyer selon Dieu.",
                "timestamp": "01:49:13"
              },
              {
                "reference": "Jean 16:13",
                "text": "Quand le consolateur sera venu, l'Esprit de vérité, il vous conduira dans toute la vérité... et il vous annoncera les choses à venir.",
                "note": "Le Saint-Esprit nous conduit vers des fréquentations profitables et nous annonce les choses à venir.",
                "timestamp": "01:53:33"
              },
              {
                "reference": "Joël 2:28",
                "text": "Après cela, je répandrai mon esprit sur toute chair... et vos jeunes gens auront des visions.",
                "note": "L'Esprit ouvre les jeunes au plan de Dieu et leur donne une vision comme celle de l'aigle.",
                "timestamp": "01:54:37"
              },
              {
                "reference": "Actes 1:8",
                "text": "Mais vous recevrez une puissance, le Saint-Esprit survenant sur vous, et vous serez mes témoins.",
                "note": "Se nourrir chaque jour de la Parole par l'Esprit s'accompagne de puissance.",
                "timestamp": "01:57:47"
              },
              {
                "reference": "Ésaïe 43:2",
                "text": "Si tu traverses les eaux, je serai avec toi; et les fleuves, ils ne te submergeront point.",
                "note": "Le Saint-Esprit nous fait traverser les tempêtes comme l'aigle plane à travers elles; Joseph cité en exemple.",
                "timestamp": "01:58:53"
              },
              {
                "reference": "Job 28:7",
                "text": "L'oiseau de proie n'en connaît pas le sentier, l'œil du vautour ne l'a point aperçu.",
                "note": "L'Esprit vous fait sortir du lot parmi vos pairs; seul l'œil de l'aigle perçoit le sentier caché.",
                "timestamp": "02:05:18"
              },
              {
                "reference": "Genèse 39:9",
                "text": "Comment ferais-je un aussi grand mal et pécherais-je contre Dieu?",
                "note": "Joseph a choisi la pureté plutôt que le plaisir et a fini premier ministre; la pureté lui a donné une place.",
                "timestamp": "02:08:34"
              },
              {
                "reference": "Daniel 1:8",
                "text": "Daniel résolut de ne pas se souiller par les mets du roi et par le vin dont le roi buvait.",
                "note": "Avec l'aide de l'Esprit, vous pouvez dire non à la norme mauvaise et devenir dix fois supérieur.",
                "timestamp": "02:09:41"
              },
              {
                "reference": "Zacharie 4:6",
                "text": "Ce n'est ni par la puissance ni par la force, mais c'est par mon esprit, dit l'Éternel des armées.",
                "note": "S'élever se fait par l'Esprit seul, non par la force humaine.",
                "timestamp": "02:10:45"
              },
              {
                "reference": "Jean 3:3",
                "text": "En vérité, en vérité, je te le dis, si un homme ne naît de nouveau, il ne peut voir le royaume de Dieu.",
                "note": "La puissance pour s'élever commence par la nouvelle naissance; fondement de l'appel de clôture.",
                "timestamp": "02:11:49"
              },
              {
                "reference": "Romains 8:14",
                "text": "Car tous ceux qui sont conduits par l'Esprit de Dieu sont fils de Dieu.",
                "note": "La communion constante avec le Saint-Esprit vous garde constamment guidé.",
                "timestamp": "02:12:54"
              },
              {
                "reference": "Jean 6:63",
                "text": "C'est l'esprit qui vivifie; la chair ne sert de rien. Les paroles que je vous ai dites sont esprit et vie.",
                "note": "Nourrissez chaque jour votre esprit de la parole de Dieu, car nous ressemblons à ce que nous mangeons.",
                "timestamp": "02:13:58"
              }
            ],
            "quotes": [
              {
                "quote": "Ne perdez jamais votre concentration. Sans vision, vous ne pouvez pas vous élever. Votre envol sera à la mesure de votre vision.",
                "timestamp": "01:41:39"
              },
              {
                "quote": "Les aigles aiment réellement les tempêtes. Les tempêtes de la vie sont destinées à vous élever. Ne vous cachez pas d'elles.",
                "timestamp": "01:42:43"
              },
              {
                "quote": "S'il vous obtient facilement, il vous traitera avec légèreté. S'il se donne du mal pour vous gagner, c'est un signe d'engagement.",
                "timestamp": "01:48:08"
              },
              {
                "quote": "Jeunes filles, jeunes hommes, ne mangez pas le miel avant la lune.",
                "timestamp": "02:06:23"
              },
              {
                "quote": "Pour s'élever comme l'aigle, le Saint-Esprit est votre seule option. Il n'existe aucune autre personnalité capable de vous faire voler comme l'aigle.",
                "timestamp": "02:13:58"
              }
            ]
          }
        }
      }
    ]
  },
  {
    "day": 2,
    "label": "Day 2",
    "dateLabel": "Wednesday, July 22",
    "date": "2026-07-22",
    "messages": [
      {
        "slug": "day-2-morning-divine-wisdom-for-soaring-high",
        "day": 2,
        "session": "morning",
        "date": "2026-07-22",
        "videoId": "bWm-d0we_Rw",
        "startTimestamp": "01:15:42",
        "title": "Exploring the Wonders of Divine Wisdom for Soaring High",
        "preacher": "Ministering",
        "summary": "Opening the morning word sessions of Day 2, the minister declared that every believer is ordained for a life of ever-increasing glory and soaring, and that soaring is never accidental but intentional: eagles soar because they apply principles other birds ignore. From Proverbs 3:19-20 he showed that the Lord founded the earth by wisdom, so nothing sustainable is built without it; it is easy to climb, but staying at the top requires wisdom. He defined wisdom as knowing from God's word which way to go and how to handle situations for desired results, and doing it, because knowing without doing is foolishness. Distinguishing divine wisdom from earthly, sensual, and devilish wisdom, he showed how it lets you build without stress, preserves you from destructive choices, and positions you for divine favor, illustrating richly from his own journey of obeying God from an airline job to Canada. Pointing to Solomon, Joseph, Daniel, and David, he closed with practical steps: crave the spirit of wisdom, live in the Word, seek godly mentors, and keep learning.",
        "keyPoints": [
          "Every believer is ordained for a life of ever-increasing glory and soaring; soaring is not accidental but intentional, for eagles soar because they understand and apply principles other birds ignore.",
          "No exploit is sustained without the wisdom of God; it is easy to climb, but staying at the top requires wisdom, for the Lord Himself founded the earth by wisdom.",
          "Wisdom is knowing from God's word which way to go and how to handle situations for desired results, and doing it; knowing what to do without doing it is the opposite of wisdom.",
          "There are four types of wisdom (James 3:15-17): earthly, sensual, and devilish wisdom, and the divine wisdom from above, which alone is pure, peaceable, and full of good fruit.",
          "Why you need wisdom to soar: it helps you build and scale without stress, it preserves you from destructive choices, and it positions you for divine favor, as with Joseph before Pharaoh.",
          "Wisdom empowers you to make right and timely decisions, build a life of value, structure, and excellence, and stay relevant spiritually and in the marketplace, as Daniel was across four kings.",
          "Evidence of a wisdom-rooted life: God's word becomes your standard and guides your decisions, wise and accurate speech, a disciplined lifestyle of excellence and diligence, and abundant creativity so you are never stranded.",
          "To grow in divine wisdom: crave and ask for the spirit of wisdom, study and apply God's word consistently, seek godly mentors and counsel, and be a diligent, continuous learner."
        ],
        "scriptures": [
          {
            "reference": "Proverbs 3:19-20",
            "text": "The LORD by wisdom hath founded the earth; by understanding hath he established the heavens.",
            "note": "Anchor text: if God founded the earth by wisdom, anything sustainable in your life must be built by wisdom.",
            "timestamp": "01:19:59"
          },
          {
            "reference": "Jeremiah 6:16",
            "text": "Thus saith the LORD, Stand ye in the ways, and see, and ask for the old paths, where is the good way, and walk therein, and ye shall find rest for your souls.",
            "note": "Wisdom is not only asking for and knowing the way, but walking in it.",
            "timestamp": "01:22:08"
          },
          {
            "reference": "James 3:15-17",
            "text": "This wisdom descendeth not from above, but is earthly, sensual, devilish. But the wisdom that is from above is first pure, then peaceable, gentle, and easy to be intreated, full of mercy and good fruits.",
            "note": "The four types of wisdom: earthly, sensual, devilish, and divine, and only divine wisdom is for soaring.",
            "timestamp": "01:24:17"
          },
          {
            "reference": "Ecclesiastes 10:10",
            "text": "If the iron be blunt, and he do not whet the edge, then must he put to more strength: but wisdom is profitable to direct.",
            "note": "Wisdom helps you build and scale without stress.",
            "timestamp": "01:25:23"
          },
          {
            "reference": "Proverbs 3:21-23",
            "text": "My son, let not them depart from thine eyes: keep sound wisdom and discretion... Then shalt thou walk in thy way safely, and thy foot shall not stumble.",
            "note": "Wisdom preserves you from destructive choices, illustrated by his testimony of leaving an airline job at God's instruction before the airline folded.",
            "timestamp": "01:27:31"
          },
          {
            "reference": "Genesis 41:39-41",
            "text": "And Pharaoh said unto Joseph, Forasmuch as God hath shewed thee all this, there is none so discreet and wise as thou art.",
            "note": "Wisdom positions you for divine favor: Joseph went from prison to prime minister.",
            "timestamp": "01:32:53"
          },
          {
            "reference": "Ecclesiastes 8:5",
            "text": "Whoso keepeth the commandment shall feel no evil thing: and a wise man's heart discerneth both time and judgment.",
            "note": "Wisdom empowers right and timely decisions.",
            "timestamp": "01:34:58"
          },
          {
            "reference": "Daniel 6:3",
            "text": "Then this Daniel was preferred above the presidents and princes, because an excellent spirit was in him; and the king thought to set him over the whole realm.",
            "note": "Wisdom empowers a life of value, structure, and excellence.",
            "timestamp": "01:34:58"
          },
          {
            "reference": "Daniel 5:11-14",
            "text": "There is a man in thy kingdom, in whom is the spirit of the holy gods; and in the days of thy father light and understanding and wisdom, like the wisdom of the gods, was found in him.",
            "note": "Wisdom makes you relevant spiritually and in the marketplace; Daniel stayed relevant across four kings.",
            "timestamp": "01:34:58"
          },
          {
            "reference": "John 2:5",
            "text": "His mother saith unto the servants, Whatsoever he saith unto you, do it.",
            "note": "Evidence of wisdom: God's word becomes your standard of living, even when the instruction does not yet make sense.",
            "timestamp": "01:38:09"
          },
          {
            "reference": "Ecclesiastes 10:12",
            "text": "The words of a wise man's mouth are gracious; but the lips of a fool will swallow up himself.",
            "note": "Evidence of wisdom: wise and accurate speech, saying the right thing at the right time.",
            "timestamp": "01:42:30"
          },
          {
            "reference": "Proverbs 22:29",
            "text": "Seest thou a man diligent in his business? he shall stand before kings; he shall not stand before mean men.",
            "note": "Evidence of wisdom: a disciplined lifestyle of excellence and diligence.",
            "timestamp": "01:42:30"
          },
          {
            "reference": "Proverbs 8:12",
            "text": "I wisdom dwell with prudence, and find out knowledge of witty inventions.",
            "note": "Evidence of wisdom: abundance of creativity, so you are never stranded.",
            "timestamp": "01:45:38"
          },
          {
            "reference": "1 Kings 3:9-12",
            "text": "Behold, I have done according to thy words: lo, I have given thee a wise and an understanding heart.",
            "note": "Solomon, empowered by wisdom, reigned in peace and unfathomable prosperity for forty years.",
            "timestamp": "01:46:40"
          },
          {
            "reference": "1 Samuel 18:14",
            "text": "And David behaved himself wisely in all his ways; and the LORD was with him.",
            "note": "David, his favorite Bible character, was a wise leader and strategic king.",
            "timestamp": "01:49:49"
          },
          {
            "reference": "James 1:5",
            "text": "If any of you lack wisdom, let him ask of God, that giveth to all men liberally, and upbraideth not; and it shall be given him.",
            "note": "First step to growing in wisdom: crave and ask for the spirit of wisdom.",
            "timestamp": "01:50:52"
          },
          {
            "reference": "Ephesians 1:17",
            "text": "That the God of our Lord Jesus Christ, the Father of glory, may give unto you the spirit of wisdom and revelation in the knowledge of him.",
            "note": "Pray for the spirit of wisdom and revelation.",
            "timestamp": "01:50:52"
          },
          {
            "reference": "Proverbs 11:14",
            "text": "Where no counsel is, the people fall: but in the multitude of counsellors there is safety.",
            "note": "Seek godly mentors and counsel, illustrated by his property-buying testimonies in Nigeria and Canada.",
            "timestamp": "01:54:01"
          },
          {
            "reference": "Proverbs 18:15",
            "text": "The heart of the prudent getteth knowledge; and the ear of the wise seeketh knowledge.",
            "note": "Be a diligent and continuous learner.",
            "timestamp": "01:54:01"
          }
        ],
        "quotes": [
          {
            "quote": "Soaring is not accidental. It is intentional. Eagles soar because they understand and apply principles that other birds ignore.",
            "timestamp": "01:16:48"
          },
          {
            "quote": "It is easy to climb, but to stay at the top requires wisdom.",
            "timestamp": "01:19:59"
          },
          {
            "quote": "Wisdom is knowing from God's word which way to go and how to handle situations for desired results, and doing it.",
            "timestamp": "01:21:02"
          },
          {
            "quote": "You don't have to choose between God and a source of income. All you need is wisdom.",
            "timestamp": "01:27:31"
          },
          {
            "quote": "God is not a waster. He gives you what you can carry, so you have to increase your capacity.",
            "timestamp": "01:44:36"
          }
        ],
        "translations": {
          "creole": {
            "title": "Dekouvri Mèvèy Sajès Diven an pou Vole Byen Wo",
            "preacher": "Predikatè envite",
            "summary": "Pou louvri sesyon pawòl maten jou 2 a, predikatè a te deklare chak kwayan òdone pou yon lavi glwa k ap ogmante san rete ak pou vole wo, epi vole wo pa janm yon aksidan; se yon bagay entansyonèl: malfini vole wo paske yo aplike prensip lòt zwazo yo neglije. Apati Pwovèb 3:19-20, li te montre se ak sajès Seyè a te fonde tè a, kidonk anyen ki dirab pa ka bati san li; li fasil pou monte, men pou rete anwo a mande sajès. Li te defini sajès kòm konnen nan pawòl Bondye a ki chemen pou pran ak kijan pou jere sitiyasyon yo pou jwenn rezilta ou vle, epi fè li, paske konnen san fè se foli. Li te fè diferans ant sajès diven an ak sajès latè, sajès lachè ak sajès dyabolik, epi li te montre kijan sajès diven an pèmèt ou bati san estrès, kijan li prezève ou de move chwa ki ka detwi ou, ak kijan li mete ou nan pozisyon pou favè diven, ak anpil egzanp nan pwòp vwayaj pa li: jan li te obeyi Bondye depi yon travay nan yon konpayi avyon jouk li rive Kanada. Li te lonje dwèt sou Salomon, Jozèf, Danyèl ak David, epi li fèmen ak etap pratik: anvi lespri sajès la, viv nan Pawòl la, chèche bon mantò, epi kontinye aprann.",
            "keyPoints": [
              "Chak kwayan òdone pou yon lavi glwa k ap ogmante san rete ak pou vole wo; vole wo pa aksidantèl, li entansyonèl, paske malfini vole wo paske yo konprann epi yo aplike prensip lòt zwazo yo neglije.",
              "Pa gen gwo reyalizasyon ki kenbe san sajès Bondye a; li fasil pou monte, men pou rete anwo a mande sajès, paske se ak sajès Seyè a li menm te fonde tè a.",
              "Sajès se konnen nan pawòl Bondye a ki chemen pou pran ak kijan pou jere sitiyasyon yo pou jwenn rezilta ou vle, epi fè li; konnen sa pou ou fè san ou pa fè li, se kontrè sajès.",
              "Gen kat kalite sajès (Jak 3:15-17): sajès latè, sajès lachè ak sajès dyabolik, ak sajès diven ki soti anwo a, ki sèl pou li pwòp, ki bay lapè, e ki chaje ak bon fwi.",
              "Poukisa ou bezwen sajès pou vole wo: li ede ou bati epi grandi san estrès, li prezève ou de move chwa ki ka detwi ou, epi li mete ou nan pozisyon pou favè diven, tankou Jozèf devan Farawon.",
              "Sajès ba ou pouvwa pou pran bon desizyon nan bon moman, bati yon lavi ki gen valè, estrikti ak ekselans, epi rete enpòtan ni espirityèlman ni nan mache travay la, jan Danyèl te ye sou kat wa.",
              "Prèv yon lavi ki chita sou sajès: pawòl Bondye a vin estanda ou e li gide desizyon ou yo, yon pale ki saj e ki egzak, yon vi disipline ki plen ekselans ak dilijans, ak yon kreyativite an abondans pou ou pa janm bloke.",
              "Pou grandi nan sajès diven: anvi epi mande lespri sajès la, etidye epi aplike pawòl Bondye a san rete, chèche bon mantò ak bon konsèy, epi rete yon moun dilijan k ap aprann tout tan."
            ],
            "scriptures": [
              {
                "reference": "Pwovèb 3:19-20",
                "text": "Se ak sajès Seyè a te fonde tè a; se ak entèlijans li te tabli syèl la.",
                "note": "Tèks prensipal la: si Bondye te fonde tè a ak sajès, tout sa ki dirab nan lavi ou dwe bati ak sajès.",
                "timestamp": "01:19:59"
              },
              {
                "reference": "Jeremi 6:16",
                "text": "Men sa Seyè a di: Kanpe sou chemen yo, gade, mande pou ansyen chemen yo, kote bon wout la ye, epi mache ladan li, epi n a jwenn repo pou nanm nou.",
                "note": "Sajès se pa sèlman mande ak konnen chemen an, se mache ladan li tou.",
                "timestamp": "01:22:08"
              },
              {
                "reference": "Jak 3:15-17",
                "text": "Sajès sa a pa soti anwo a; se yon sajès latè, yon sajès lachè, yon sajès dyabolik. Men sajès ki soti anwo a, li pwòp anvan tout bagay, apre sa li bay lapè, li dou, li kite yo pale avè li, li chaje ak mizerikòd ak bon fwi.",
                "note": "Kat kalite sajès yo: latè, lachè, dyabolik ak diven; se sèl sajès diven an ki fèt pou vole wo.",
                "timestamp": "01:24:17"
              },
              {
                "reference": "Eklezyas 10:10",
                "text": "Si zouti fè a pèdi fil li epi yo pa file li, fòk ou sèvi ak plis fòs; men sajès bay avantaj pou fè bagay yo reyisi.",
                "note": "Sajès ede ou bati epi grandi san estrès.",
                "timestamp": "01:25:23"
              },
              {
                "reference": "Pwovèb 3:21-23",
                "text": "Pitit mwen, pa kite yo soti devan je ou: kenbe bon konprann ak disènman... Lè sa a w a mache sou chemen ou an sekirite, epi pye ou p ap bite.",
                "note": "Sajès prezève ou de move chwa ki ka detwi ou; li te bay temwayaj jan li te kite yon travay nan yon konpayi avyon sou enstriksyon Bondye anvan konpayi an te fèmen.",
                "timestamp": "01:27:31"
              },
              {
                "reference": "Jenèz 41:39-41",
                "text": "Farawon di Jozèf: Piske se Bondye ki fè ou konnen tout bagay sa yo, pa gen pèsonn ki gen disènman ak sajès tankou ou.",
                "note": "Sajès mete ou nan pozisyon pou favè diven: Jozèf soti nan prizon rive premye minis.",
                "timestamp": "01:32:53"
              },
              {
                "reference": "Eklezyas 8:5",
                "text": "Moun ki kenbe kòmandman an p ap konnen malè; kè yon moun saj konnen ni bon moman an ni bon fason an.",
                "note": "Sajès ba ou pouvwa pou pran bon desizyon nan bon moman.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Danyèl 6:3",
                "text": "Danyèl sa a te depase tout lòt chèf ak gouvènè yo, paske li te gen yon lespri ekselan nan li; epi wa a t ap kalkile pou li mete li sou tout wayòm nan.",
                "note": "Sajès ba ou pouvwa pou yon lavi ki gen valè, estrikti ak ekselans.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Danyèl 5:11-14",
                "text": "Gen yon nonm nan wayòm ou a ki gen lespri bondye ki sen yo nan li; nan tan papa ou, yo te jwenn nan li limyè, entèlijans ak yon sajès tankou sajès bondye yo.",
                "note": "Sajès fè ou rete enpòtan ni espirityèlman ni nan mache travay la; Danyèl te rete enpòtan sou kat wa.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Jan 2:5",
                "text": "Manman li di sèvitè yo: Fè tout sa li di nou fè.",
                "note": "Prèv sajès: pawòl Bondye a vin estanda lavi ou, menm lè enstriksyon an poko fè sans.",
                "timestamp": "01:38:09"
              },
              {
                "reference": "Eklezyas 10:12",
                "text": "Pawòl ki soti nan bouch yon moun saj plen favè; men bouch yon moun fou ap vale li tou vivan.",
                "note": "Prèv sajès: yon pale ki saj e ki egzak, di bon bagay la nan bon moman an.",
                "timestamp": "01:42:30"
              },
              {
                "reference": "Pwovèb 22:29",
                "text": "Ou wè yon moun ki dilijan nan travay li? L ap kanpe devan wa; li p ap kanpe devan moun òdinè.",
                "note": "Prèv sajès: yon vi disipline ki plen ekselans ak dilijans.",
                "timestamp": "01:42:30"
              },
              {
                "reference": "Pwovèb 8:12",
                "text": "Mwen menm sajès, mwen rete ak pridans, epi mwen jwenn konesans ak lide ki gen lespri.",
                "note": "Prèv sajès: kreyativite an abondans, pou ou pa janm bloke.",
                "timestamp": "01:45:38"
              },
              {
                "reference": "1 Wa 3:9-12",
                "text": "Gade, mwen fè jan ou mande a: mwen ba ou yon kè ki saj e ki gen bon konprann.",
                "note": "Salomon, ak pouvwa sajès la, te gouvène nan lapè ak yon richès moun pa ka konprann pandan karant ane.",
                "timestamp": "01:46:40"
              },
              {
                "reference": "1 Samyèl 18:14",
                "text": "David te mennen tèt li ak sajès nan tout sa li t ap fè, epi Seyè a te avèk li.",
                "note": "David, pèsonaj Bib li pi renmen an, te yon lidè saj ak yon wa estratejik.",
                "timestamp": "01:49:49"
              },
              {
                "reference": "Jak 1:5",
                "text": "Si yon moun nan mitan nou manke sajès, se pou li mande Bondye, ki bay tout moun san kalkile e san repwòch, epi l ap ba li li.",
                "note": "Premye etap pou grandi nan sajès: anvi epi mande lespri sajès la.",
                "timestamp": "01:50:52"
              },
              {
                "reference": "Efezyen 1:17",
                "text": "Pou Bondye Seyè nou an Jezikri, Papa laglwa a, ka ban nou lespri sajès ak revelasyon pou nou konnen li.",
                "note": "Priye pou lespri sajès ak revelasyon an.",
                "timestamp": "01:50:52"
              },
              {
                "reference": "Pwovèb 11:14",
                "text": "Kote ki pa gen konsèy, pèp la tonbe; men kote ki gen anpil konseye, gen sekirite.",
                "note": "Chèche bon mantò ak bon konsèy; li te bay temwayaj sou jan li te achte pwopriyete an Nijerya ak Kanada.",
                "timestamp": "01:54:01"
              },
              {
                "reference": "Pwovèb 18:15",
                "text": "Kè moun ki gen pridans ap chèche konesans; zòrèy moun saj ap chèche konnen.",
                "note": "Rete yon moun dilijan k ap aprann tout tan.",
                "timestamp": "01:54:01"
              }
            ],
            "quotes": [
              {
                "quote": "Vole wo pa yon aksidan. Se yon bagay entansyonèl. Malfini vole wo paske yo konprann epi yo aplike prensip lòt zwazo yo neglije.",
                "timestamp": "01:16:48"
              },
              {
                "quote": "Li fasil pou monte, men pou rete anwo a mande sajès.",
                "timestamp": "01:19:59"
              },
              {
                "quote": "Sajès se konnen nan pawòl Bondye a ki chemen pou pran ak kijan pou jere sitiyasyon yo pou jwenn rezilta ou vle, epi fè li.",
                "timestamp": "01:21:02"
              },
              {
                "quote": "Ou pa oblije chwazi ant Bondye ak yon sous revni. Tout sa ou bezwen se sajès.",
                "timestamp": "01:27:31"
              },
              {
                "quote": "Bondye pa yon gaspiyè. Li ba ou sa ou ka pote, kidonk fòk ou ogmante kapasite ou.",
                "timestamp": "01:44:36"
              }
            ]
          },
          "es": {
            "title": "Explorando las maravillas de la sabiduría divina para volar alto",
            "preacher": "Predicador invitado",
            "summary": "Abriendo las sesiones de la palabra matutina del día 2, el ministro declaró que todo creyente está destinado a una vida de gloria siempre creciente y de vuelo en las alturas, y que volar alto nunca es accidental sino intencional: las águilas vuelan alto porque aplican principios que otras aves ignoran. Desde Proverbios 3:19-20 mostró que el Señor fundó la tierra con sabiduría, de modo que nada sostenible se edifica sin ella; es fácil subir, pero permanecer en la cima requiere sabiduría. Definió la sabiduría como saber, a partir de la palabra de Dios, qué camino tomar y cómo manejar las situaciones para obtener los resultados deseados, y hacerlo, porque saber sin hacer es necedad. Distinguiendo la sabiduría divina de la terrenal, la sensual y la diabólica, mostró cómo te permite edificar sin estrés, te preserva de decisiones destructivas y te posiciona para el favor divino, ilustrándolo generosamente con su propio recorrido de obedecer a Dios desde un empleo en una aerolínea hasta Canadá. Señalando a Salomón, José, Daniel y David, cerró con pasos prácticos: anhela el espíritu de sabiduría, vive en la Palabra, busca mentores piadosos y nunca dejes de aprender.",
            "keyPoints": [
              "Todo creyente está destinado a una vida de gloria siempre creciente y de vuelo en las alturas; volar alto no es accidental sino intencional, pues las águilas vuelan alto porque entienden y aplican principios que otras aves ignoran.",
              "Ninguna proeza se sostiene sin la sabiduría de Dios; es fácil subir, pero permanecer en la cima requiere sabiduría, pues el Señor mismo fundó la tierra con sabiduría.",
              "La sabiduría es saber, a partir de la palabra de Dios, qué camino tomar y cómo manejar las situaciones para obtener los resultados deseados, y hacerlo; saber qué hacer sin hacerlo es lo contrario de la sabiduría.",
              "Hay cuatro tipos de sabiduría (Santiago 3:15-17): la terrenal, la sensual y la diabólica, y la sabiduría divina que viene de lo alto, la única que es pura, pacífica y llena de buenos frutos.",
              "Por qué necesitas sabiduría para volar alto: te ayuda a edificar y crecer sin estrés, te preserva de decisiones destructivas y te posiciona para el favor divino, como a José delante de Faraón.",
              "La sabiduría te capacita para tomar decisiones correctas y oportunas, edificar una vida de valor, estructura y excelencia, y mantenerte vigente en lo espiritual y en el mercado laboral, como Daniel a lo largo de cuatro reyes.",
              "Evidencias de una vida arraigada en la sabiduría: la palabra de Dios se vuelve tu estándar y guía tus decisiones, un hablar sabio y certero, un estilo de vida disciplinado de excelencia y diligencia, y creatividad abundante para que nunca quedes varado.",
              "Para crecer en la sabiduría divina: anhela y pide el espíritu de sabiduría, estudia y aplica la palabra de Dios con constancia, busca mentores y consejo piadosos, y sé un aprendiz diligente y continuo."
            ],
            "scriptures": [
              {
                "reference": "Proverbios 3:19-20",
                "text": "Jehová con sabiduría fundó la tierra; afirmó los cielos con inteligencia.",
                "note": "Texto ancla: si Dios fundó la tierra con sabiduría, cualquier cosa sostenible en tu vida debe edificarse con sabiduría.",
                "timestamp": "01:19:59"
              },
              {
                "reference": "Jeremías 6:16",
                "text": "Así dijo Jehová: Paraos en los caminos, y mirad, y preguntad por las sendas antiguas, cuál sea el buen camino, y andad por él, y hallaréis descanso para vuestra alma.",
                "note": "La sabiduría no es solo preguntar por el camino y conocerlo, sino andar por él.",
                "timestamp": "01:22:08"
              },
              {
                "reference": "Santiago 3:15-17",
                "text": "Que esta sabiduría no es la que desciende de lo alto, sino terrena, animal, diabólica. Mas la sabiduría que es de lo alto, primeramente es pura, después pacífica, modesta, benigna, llena de misericordia y de buenos frutos.",
                "note": "Los cuatro tipos de sabiduría: terrenal, sensual, diabólica y divina, y solo la sabiduría divina sirve para volar alto.",
                "timestamp": "01:24:17"
              },
              {
                "reference": "Eclesiastés 10:10",
                "text": "Si se embotare el hierro, y su filo no fuere amolado, hay que añadir entonces más fuerza: empero excede la bondad de la sabiduría.",
                "note": "La sabiduría te ayuda a edificar y crecer sin estrés.",
                "timestamp": "01:25:23"
              },
              {
                "reference": "Proverbios 3:21-23",
                "text": "Hijo mío, no se aparten estas cosas de tus ojos; guarda la ley y el consejo... Entonces andarás por tu camino confiadamente, y tu pie no tropezará.",
                "note": "La sabiduría te preserva de decisiones destructivas, ilustrado con su testimonio de dejar un empleo en una aerolínea por instrucción de Dios antes de que la aerolínea quebrara.",
                "timestamp": "01:27:31"
              },
              {
                "reference": "Génesis 41:39-41",
                "text": "Y dijo Faraón a José: Pues que Dios te ha hecho saber todo esto, no hay entendido ni sabio como tú.",
                "note": "La sabiduría te posiciona para el favor divino: José pasó de la cárcel a primer ministro.",
                "timestamp": "01:32:53"
              },
              {
                "reference": "Eclesiastés 8:5",
                "text": "El que guarda el mandamiento no experimentará mal; y el tiempo y el juicio conoce el corazón del sabio.",
                "note": "La sabiduría capacita para decisiones correctas y oportunas.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Daniel 6:3",
                "text": "Pero el mismo Daniel era superior a estos gobernadores y presidentes, porque había en él más abundancia de espíritu: y el rey pensaba de ponerlo sobre todo el reino.",
                "note": "La sabiduría capacita para una vida de valor, estructura y excelencia.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Daniel 5:11-14",
                "text": "En tu reino hay un varón, en el cual mora el espíritu de los dioses santos; y en los días de tu padre se halló en él luz e inteligencia y sabiduría, como ciencia de los dioses.",
                "note": "La sabiduría te mantiene vigente en lo espiritual y en el mercado laboral; Daniel se mantuvo vigente a lo largo de cuatro reyes.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Juan 2:5",
                "text": "Su madre dice a los que servían: Haced todo lo que os dijere.",
                "note": "Evidencia de sabiduría: la palabra de Dios se vuelve tu estándar de vida, aun cuando la instrucción todavía no tenga sentido.",
                "timestamp": "01:38:09"
              },
              {
                "reference": "Eclesiastés 10:12",
                "text": "Las palabras de la boca del sabio son gracia; mas los labios del necio causan su propia ruina.",
                "note": "Evidencia de sabiduría: un hablar sabio y certero, decir lo correcto en el momento correcto.",
                "timestamp": "01:42:30"
              },
              {
                "reference": "Proverbios 22:29",
                "text": "¿Has visto hombre solícito en su obra? delante de los reyes estará; no estará delante de los de baja suerte.",
                "note": "Evidencia de sabiduría: un estilo de vida disciplinado de excelencia y diligencia.",
                "timestamp": "01:42:30"
              },
              {
                "reference": "Proverbios 8:12",
                "text": "Yo, la sabiduría, habito con la cordura, y hallo la ciencia de los consejos.",
                "note": "Evidencia de sabiduría: abundancia de creatividad, para que nunca quedes varado.",
                "timestamp": "01:45:38"
              },
              {
                "reference": "1 Reyes 3:9-12",
                "text": "He aquí lo he hecho conforme a tus palabras: he aquí que te he dado corazón sabio y entendido.",
                "note": "Salomón, capacitado por la sabiduría, reinó en paz y en una prosperidad insondable durante cuarenta años.",
                "timestamp": "01:46:40"
              },
              {
                "reference": "1 Samuel 18:14",
                "text": "Y David se conducía prudentemente en todos sus negocios, y Jehová era con él.",
                "note": "David, su personaje favorito de la Biblia, fue un líder sabio y un rey estratégico.",
                "timestamp": "01:49:49"
              },
              {
                "reference": "Santiago 1:5",
                "text": "Y si alguno de vosotros tiene falta de sabiduría, demándela a Dios, el cual da a todos abundantemente, y no zahiere; y le será dada.",
                "note": "Primer paso para crecer en sabiduría: anhela y pide el espíritu de sabiduría.",
                "timestamp": "01:50:52"
              },
              {
                "reference": "Efesios 1:17",
                "text": "Que el Dios del Señor nuestro Jesucristo, el Padre de gloria, os dé espíritu de sabiduría y de revelación para su conocimiento.",
                "note": "Ora por el espíritu de sabiduría y de revelación.",
                "timestamp": "01:50:52"
              },
              {
                "reference": "Proverbios 11:14",
                "text": "Cuando faltaren las industrias, caerá el pueblo: mas en la multitud de consejeros hay salud.",
                "note": "Busca mentores y consejo piadosos, ilustrado con sus testimonios de compra de propiedades en Nigeria y Canadá.",
                "timestamp": "01:54:01"
              },
              {
                "reference": "Proverbios 18:15",
                "text": "El corazón del entendido adquiere sabiduría; y el oído de los sabios busca la ciencia.",
                "note": "Sé un aprendiz diligente y continuo.",
                "timestamp": "01:54:01"
              }
            ],
            "quotes": [
              {
                "quote": "Volar alto no es accidental. Es intencional. Las águilas vuelan alto porque entienden y aplican principios que otras aves ignoran.",
                "timestamp": "01:16:48"
              },
              {
                "quote": "Es fácil subir, pero permanecer en la cima requiere sabiduría.",
                "timestamp": "01:19:59"
              },
              {
                "quote": "La sabiduría es saber, a partir de la palabra de Dios, qué camino tomar y cómo manejar las situaciones para obtener los resultados deseados, y hacerlo.",
                "timestamp": "01:21:02"
              },
              {
                "quote": "No tienes que elegir entre Dios y una fuente de ingresos. Todo lo que necesitas es sabiduría.",
                "timestamp": "01:27:31"
              },
              {
                "quote": "Dios no es un desperdiciador. Te da lo que puedes cargar, así que tienes que aumentar tu capacidad.",
                "timestamp": "01:44:36"
              }
            ]
          },
          "fr": {
            "title": "Explorer les merveilles de la sagesse divine pour s'élever haut",
            "preacher": "Prédicateur invité",
            "summary": "Ouvrant les sessions de la parole du matin du jour 2, le ministre a déclaré que chaque croyant est destiné à une vie de gloire toujours croissante et d'envol, et que s'élever n'est jamais accidentel mais intentionnel : les aigles planent parce qu'ils appliquent des principes que les autres oiseaux ignorent. À partir de Proverbes 3:19-20, il a montré que l'Éternel a fondé la terre par la sagesse, si bien que rien de durable ne se bâtit sans elle; il est facile de monter, mais rester au sommet exige la sagesse. Il a défini la sagesse comme le fait de savoir, par la parole de Dieu, quel chemin prendre et comment gérer les situations pour obtenir les résultats désirés, et de le faire, car savoir sans faire est folie. Distinguant la sagesse divine des sagesses terrestre, charnelle et diabolique, il a montré comment elle permet de bâtir sans stress, préserve des choix destructeurs et positionne pour la faveur divine, illustrant richement par son propre parcours d'obéissance à Dieu, d'un emploi dans une compagnie aérienne jusqu'au Canada. Pointant vers Salomon, Joseph, Daniel et David, il a conclu par des étapes pratiques : désirer ardemment l'esprit de sagesse, vivre dans la Parole, rechercher des mentors pieux et ne jamais cesser d'apprendre.",
            "keyPoints": [
              "Chaque croyant est destiné à une vie de gloire toujours croissante et d'envol; s'élever n'est pas accidentel mais intentionnel, car les aigles planent parce qu'ils comprennent et appliquent des principes que les autres oiseaux ignorent.",
              "Aucun exploit ne se maintient sans la sagesse de Dieu; il est facile de monter, mais rester au sommet exige la sagesse, car l'Éternel lui-même a fondé la terre par la sagesse.",
              "La sagesse, c'est savoir par la parole de Dieu quel chemin prendre et comment gérer les situations pour obtenir les résultats désirés, et le faire; savoir quoi faire sans le faire est le contraire de la sagesse.",
              "Il existe quatre types de sagesse (Jacques 3:15-17) : la sagesse terrestre, charnelle et diabolique, et la sagesse divine d'en haut, seule pure, pacifique et pleine de bons fruits.",
              "Pourquoi il faut la sagesse pour s'élever : elle aide à bâtir et à grandir sans stress, elle préserve des choix destructeurs et elle positionne pour la faveur divine, comme Joseph devant Pharaon.",
              "La sagesse rend capable de prendre des décisions justes et opportunes, de bâtir une vie de valeur, de structure et d'excellence, et de rester pertinent spirituellement et sur le marché du travail, comme Daniel sous quatre rois.",
              "Preuves d'une vie enracinée dans la sagesse : la parole de Dieu devient votre standard et guide vos décisions, un parler sage et juste, un mode de vie discipliné d'excellence et de diligence, et une créativité abondante qui fait que vous n'êtes jamais à court de ressources.",
              "Pour croître dans la sagesse divine : désirer ardemment et demander l'esprit de sagesse, étudier et appliquer la parole de Dieu avec constance, rechercher des mentors et des conseils pieux, et être un apprenant diligent et continuel."
            ],
            "scriptures": [
              {
                "reference": "Proverbes 3:19-20",
                "text": "L'Éternel a fondé la terre par la sagesse, il a affermi les cieux par l'intelligence.",
                "note": "Texte d'ancrage : si Dieu a fondé la terre par la sagesse, tout ce qui est durable dans votre vie doit être bâti par la sagesse.",
                "timestamp": "01:19:59"
              },
              {
                "reference": "Jérémie 6:16",
                "text": "Ainsi parle l'Éternel : Placez-vous sur les chemins, regardez, et demandez quels sont les anciens sentiers, quelle est la bonne voie; marchez-y, et vous trouverez le repos de vos âmes!",
                "note": "La sagesse, ce n'est pas seulement demander et connaître le chemin, c'est y marcher.",
                "timestamp": "01:22:08"
              },
              {
                "reference": "Jacques 3:15-17",
                "text": "Cette sagesse n'est point celle qui vient d'en haut; mais elle est terrestre, charnelle, diabolique. La sagesse d'en haut est premièrement pure, ensuite pacifique, modérée, conciliante, pleine de miséricorde et de bons fruits.",
                "note": "Les quatre types de sagesse : terrestre, charnelle, diabolique et divine, et seule la sagesse divine fait s'élever.",
                "timestamp": "01:24:17"
              },
              {
                "reference": "Ecclésiaste 10:10",
                "text": "S'il a émoussé le fer, et s'il n'en a pas aiguisé le tranchant, il devra redoubler de force; mais la sagesse a l'avantage du succès.",
                "note": "La sagesse aide à bâtir et à grandir sans stress.",
                "timestamp": "01:25:23"
              },
              {
                "reference": "Proverbes 3:21-23",
                "text": "Mon fils, que ces enseignements ne s'éloignent pas de tes yeux, garde la sagesse et la réflexion... Alors tu marcheras avec assurance dans ton chemin, et ton pied ne heurtera pas.",
                "note": "La sagesse préserve des choix destructeurs, illustrée par son témoignage : il a quitté un emploi dans une compagnie aérienne sur instruction de Dieu avant que la compagnie ne ferme.",
                "timestamp": "01:27:31"
              },
              {
                "reference": "Genèse 41:39-41",
                "text": "Et Pharaon dit à Joseph : Puisque Dieu t'a fait connaître toutes ces choses, il n'y a personne qui soit aussi intelligent et aussi sage que toi.",
                "note": "La sagesse positionne pour la faveur divine : Joseph est passé de la prison à premier ministre.",
                "timestamp": "01:32:53"
              },
              {
                "reference": "Ecclésiaste 8:5",
                "text": "Celui qui observe le commandement ne connaît point de chose mauvaise, et le cœur du sage connaît le temps et le jugement.",
                "note": "La sagesse rend capable de décisions justes et opportunes.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Daniel 6:3",
                "text": "Daniel surpassait les chefs et les satrapes, parce qu'il y avait en lui un esprit supérieur; et le roi pensait à l'établir sur tout le royaume.",
                "note": "La sagesse rend capable d'une vie de valeur, de structure et d'excellence.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Daniel 5:11-14",
                "text": "Il y a dans ton royaume un homme qui a en lui l'esprit des dieux saints; et du temps de ton père, on trouva chez lui des lumières, de l'intelligence, et une sagesse semblable à la sagesse des dieux.",
                "note": "La sagesse vous rend pertinent spirituellement et sur le marché du travail; Daniel est resté pertinent sous quatre rois.",
                "timestamp": "01:34:58"
              },
              {
                "reference": "Jean 2:5",
                "text": "Sa mère dit aux serviteurs : Faites ce qu'il vous dira.",
                "note": "Preuve de sagesse : la parole de Dieu devient votre standard de vie, même quand l'instruction n'a pas encore de sens.",
                "timestamp": "01:38:09"
              },
              {
                "reference": "Ecclésiaste 10:12",
                "text": "Les paroles de la bouche du sage sont pleines de grâce; mais les lèvres de l'insensé causent sa perte.",
                "note": "Preuve de sagesse : un parler sage et juste, dire la bonne chose au bon moment.",
                "timestamp": "01:42:30"
              },
              {
                "reference": "Proverbes 22:29",
                "text": "Si tu vois un homme habile dans son ouvrage, il se tient auprès des rois; il ne se tient pas auprès des gens obscurs.",
                "note": "Preuve de sagesse : un mode de vie discipliné d'excellence et de diligence.",
                "timestamp": "01:42:30"
              },
              {
                "reference": "Proverbes 8:12",
                "text": "Moi, la sagesse, j'ai pour demeure le discernement, et je possède la science de la réflexion.",
                "note": "Preuve de sagesse : une abondance de créativité, pour n'être jamais à court de ressources.",
                "timestamp": "01:45:38"
              },
              {
                "reference": "1 Rois 3:9-12",
                "text": "Voici, j'agirai selon ta parole. Je te donnerai un cœur sage et intelligent.",
                "note": "Salomon, rendu puissant par la sagesse, a régné dans la paix et une prospérité insondable pendant quarante ans.",
                "timestamp": "01:46:40"
              },
              {
                "reference": "1 Samuel 18:14",
                "text": "Il réussissait dans toutes ses entreprises, et l'Éternel était avec lui.",
                "note": "David, son personnage biblique préféré, était un dirigeant sage et un roi stratège.",
                "timestamp": "01:49:49"
              },
              {
                "reference": "Jacques 1:5",
                "text": "Si quelqu'un d'entre vous manque de sagesse, qu'il la demande à Dieu, qui donne à tous simplement et sans reproche, et elle lui sera donnée.",
                "note": "Première étape pour croître en sagesse : désirer ardemment et demander l'esprit de sagesse.",
                "timestamp": "01:50:52"
              },
              {
                "reference": "Éphésiens 1:17",
                "text": "Afin que le Dieu de notre Seigneur Jésus-Christ, le Père de gloire, vous donne un esprit de sagesse et de révélation, dans sa connaissance.",
                "note": "Priez pour l'esprit de sagesse et de révélation.",
                "timestamp": "01:50:52"
              },
              {
                "reference": "Proverbes 11:14",
                "text": "Quand la prudence fait défaut, le peuple tombe; et le salut est dans le grand nombre des conseillers.",
                "note": "Recherchez des mentors et des conseils pieux, illustré par ses témoignages d'achats immobiliers au Nigeria et au Canada.",
                "timestamp": "01:54:01"
              },
              {
                "reference": "Proverbes 18:15",
                "text": "Un cœur intelligent acquiert la science, et l'oreille des sages cherche la science.",
                "note": "Soyez un apprenant diligent et continuel.",
                "timestamp": "01:54:01"
              }
            ],
            "quotes": [
              {
                "quote": "S'élever n'est pas accidentel. C'est intentionnel. Les aigles planent parce qu'ils comprennent et appliquent des principes que les autres oiseaux ignorent.",
                "timestamp": "01:16:48"
              },
              {
                "quote": "Il est facile de monter, mais rester au sommet exige la sagesse.",
                "timestamp": "01:19:59"
              },
              {
                "quote": "La sagesse, c'est savoir par la parole de Dieu quel chemin prendre et comment gérer les situations pour obtenir les résultats désirés, et le faire.",
                "timestamp": "01:21:02"
              },
              {
                "quote": "Vous n'avez pas à choisir entre Dieu et une source de revenus. Tout ce qu'il vous faut, c'est la sagesse.",
                "timestamp": "01:27:31"
              },
              {
                "quote": "Dieu n'est pas un gaspilleur. Il vous donne ce que vous pouvez porter; vous devez donc augmenter votre capacité.",
                "timestamp": "01:44:36"
              }
            ]
          }
        }
      },
      {
        "slug": "day-2-morning-living-the-word-for-soaring",
        "day": 2,
        "session": "morning",
        "date": "2026-07-22",
        "videoId": "bWm-d0we_Rw",
        "startTimestamp": "02:19:32",
        "title": "Living the Word for Soaring",
        "preacher": "Ministering",
        "summary": "In this second word of the Day 2 morning session, the minister opened from Daniel 11:32 and Luke 8:11 to show that in a generation filled with noise, trends, and shifting values, the word of God remains the only source of direction, stability, and soaring. He taught that every true vision and true success must align with God's Word, and that while a person can be successful without it, only the Word produces fulfillment, because word increase is value, wisdom, and worth increase. Echoing the national pastor, he declared that what is written is superior to what is happening: facts change, but the truth of Scripture stands forever. He unpacked why the Word is key to soaring, as our foundation for exploits, a spiritual seed, a creative force, and a compass for divine purpose, and what it empowers us to do: grow to maturity, resist conformity, weather storms, and birth faith. He closed with practical steps for a word-filled life, an altar call, and a season of testimonies.",
        "keyPoints": [
          "In a generation of noise, trends, and shifting values, the word of God is the only source of direction, stability, and soaring.",
          "Every true vision and true success must align with God's Word; success and fulfillment are not the same, and only the Word makes you fulfilled.",
          "Word increase is value increase, wisdom increase, and worth increase; a man who stays true to the Word carries worth.",
          "What is written is superior to what is happening: facts are temporal and can change, but the truth of Scripture is eternal and overrides circumstances.",
          "The Word is key to soaring because it is our foundation for commanding exploits, a spiritual seed that reproduces in whoever engages it, has unlimited capacity to create and recreate, and is our compass for divine purpose.",
          "The Word empowers us to grow into spiritual maturity, transforms our minds so we do not conform to the world, secures our destiny against the storms of life, and births faith, for faith comes by hearing.",
          "Evidence of a word-rooted life: you make decisions by Scripture rather than emotions, your words carry power, you carry answers instead of opinions, and you enjoy great peace in seasons of uncertainty.",
          "To be empowered by the Word: study it daily, ask the Holy Spirit to reveal it, meditate until light and revelation come, and surround yourself with a word-filled environment."
        ],
        "scriptures": [
          {
            "reference": "Daniel 11:32",
            "text": "But the people that do know their God shall be strong, and do exploits.",
            "note": "Anchor text: the Word is our foundation for commanding exploits.",
            "timestamp": "02:20:49"
          },
          {
            "reference": "Luke 8:11",
            "text": "Now the parable is this: The seed is the word of God.",
            "note": "Second anchor: the Word is a spiritual seed that reproduces in anyone who engages it.",
            "timestamp": "02:20:49"
          },
          {
            "reference": "Psalm 119:89",
            "text": "For ever, O LORD, thy word is settled in heaven.",
            "note": "The Word of God is eternal.",
            "timestamp": "02:28:30"
          },
          {
            "reference": "Psalm 11:3",
            "text": "If the foundations be destroyed, what can the righteous do?",
            "note": "Everything found in God's Word is a strong foundation that cannot fail.",
            "timestamp": "02:31:23"
          },
          {
            "reference": "Luke 1:45",
            "text": "And blessed is she that believed: for there shall be a performance of those things which were told her from the Lord.",
            "note": "There are no idle words in this kingdom; every word of God performs.",
            "timestamp": "02:31:23"
          },
          {
            "reference": "John 1:3",
            "text": "All things were made by him; and without him was not any thing made that was made.",
            "note": "The Word has unlimited capacity to create and recreate whatever is not working.",
            "timestamp": "02:32:39"
          },
          {
            "reference": "Jeremiah 1:5",
            "text": "Before I formed thee in the belly I knew thee; and before thou camest forth out of the womb I sanctified thee, and I ordained thee a prophet unto the nations.",
            "note": "The Word is our compass for discovering and fulfilling divine purpose.",
            "timestamp": "02:34:17"
          },
          {
            "reference": "Hebrews 5:13-14",
            "text": "For every one that useth milk is unskilful in the word of righteousness: for he is a babe. But strong meat belongeth to them that are of full age.",
            "note": "The Word enhances our spiritual growth and maturity.",
            "timestamp": "02:35:41"
          },
          {
            "reference": "2 Timothy 3:16-17",
            "text": "All scripture is given by inspiration of God, and is profitable for doctrine, for reproof, for correction, for instruction in righteousness: that the man of God may be perfect, throughly furnished unto all good works.",
            "note": "Scripture furnishes us thoroughly for profitable living and good works.",
            "timestamp": "02:37:03"
          },
          {
            "reference": "Romans 12:2",
            "text": "And be not conformed to this world: but be ye transformed by the renewing of your mind, that ye may prove what is that good, and acceptable, and perfect, will of God.",
            "note": "The Word transforms our minds and empowers us not to conform to the world.",
            "timestamp": "02:38:27"
          },
          {
            "reference": "Matthew 7:24-25",
            "text": "Therefore whosoever heareth these sayings of mine, and doeth them, I will liken him unto a wise man, which built his house upon a rock.",
            "note": "Doing the Word, not just hearing it, secures our destiny against the storms of life.",
            "timestamp": "02:41:17"
          },
          {
            "reference": "Romans 10:17",
            "text": "So then faith cometh by hearing, and hearing by the word of God.",
            "note": "The Word births faith, and when faith comes alive, victory comes along.",
            "timestamp": "02:41:17"
          },
          {
            "reference": "Philippians 4:8",
            "text": "Finally, brethren, whatsoever things are true, whatsoever things are honest, whatsoever things are just, whatsoever things are pure, whatsoever things are lovely, whatsoever things are of good report; if there be any virtue, and if there be any praise, think on these things.",
            "note": "A word-rooted life makes decisions based on Scripture, not emotions.",
            "timestamp": "02:42:46"
          },
          {
            "reference": "John 6:63",
            "text": "It is the spirit that quickeneth; the flesh profiteth nothing: the words that I speak unto you, they are spirit, and they are life.",
            "note": "When the Word lives in you, your own words carry power.",
            "timestamp": "02:44:11"
          },
          {
            "reference": "Isaiah 33:6",
            "text": "And wisdom and knowledge shall be the stability of thy times, and strength of salvation: the fear of the LORD is his treasure.",
            "note": "A word-rooted life carries answers, not just opinions.",
            "timestamp": "02:45:30"
          },
          {
            "reference": "Psalm 119:165",
            "text": "Great peace have they which love thy law: and nothing shall offend them.",
            "note": "The word-rooted enjoy great peace and confidence in seasons of uncertainty.",
            "timestamp": "02:45:30"
          },
          {
            "reference": "Matthew 4:4",
            "text": "But he answered and said, It is written, Man shall not live by bread alone, but by every word that proceedeth out of the mouth of God.",
            "note": "Jesus answered Satan's temptations with 'It is written'; you must know what is written.",
            "timestamp": "02:48:01"
          },
          {
            "reference": "Joshua 1:8",
            "text": "This book of the law shall not depart out of thy mouth; but thou shalt meditate therein day and night... for then thou shalt make thy way prosperous, and then thou shalt have good success.",
            "note": "Joshua became unstoppable by meditating on the Word day and night; there is success, and there is good success.",
            "timestamp": "02:50:18"
          },
          {
            "reference": "2 Timothy 3:15",
            "text": "And that from a child thou hast known the holy scriptures, which are able to make thee wise unto salvation through faith which is in Christ Jesus.",
            "note": "Timothy grew from childhood in the Scriptures; none of us is too young to reign.",
            "timestamp": "02:51:32"
          },
          {
            "reference": "2 Timothy 2:15",
            "text": "Study to shew thyself approved unto God, a workman that needeth not to be ashamed, rightly dividing the word of truth.",
            "note": "First step to being empowered by the Word: study it daily.",
            "timestamp": "02:52:50"
          },
          {
            "reference": "Psalm 1:1-3",
            "text": "But his delight is in the law of the LORD; and in his law doth he meditate day and night... and whatsoever he doeth shall prosper.",
            "note": "Meditate on the Word until you attain light and revelation.",
            "timestamp": "02:54:13"
          },
          {
            "reference": "Colossians 3:16",
            "text": "Let the word of Christ dwell in you richly in all wisdom; teaching and admonishing one another in psalms and hymns and spiritual songs, singing with grace in your hearts to the Lord.",
            "note": "Surround yourself with a word-filled environment: books, messages, and soul-lifting songs.",
            "timestamp": "02:55:25"
          }
        ],
        "quotes": [
          {
            "quote": "What is written is superior to what is happening. What is happening is temporal; what is written is eternal.",
            "timestamp": "02:25:39"
          },
          {
            "quote": "We can make a living by following our own ways, but we can only make a life by following God's ways. And God's ways are contained in His Word.",
            "timestamp": "02:23:16"
          },
          {
            "quote": "Word increase is value increase. Word increase is wisdom increase. Word increase is worth increase.",
            "timestamp": "02:23:16"
          },
          {
            "quote": "The fact is that you are broke today, but the truth is you are made rich in Christ. Facts can change based on available circumstances, but what is written will always override what is happening.",
            "timestamp": "02:27:01"
          },
          {
            "quote": "The word of God is your greatest weapon and the ultimate key to soaring. Engage it daily, meditate on it daily, obey it daily, and your life shall be transformed.",
            "timestamp": "02:56:41"
          }
        ],
        "translations": {
          "creole": {
            "title": "Viv Pawòl la pou Vole Wo",
            "preacher": "Predikatè envite",
            "summary": "Nan dezyèm pawòl sesyon maten jou 2 a, predikatè a te louvri apati Danyèl 11:32 ak Lik 8:11 pou montre nan yon jenerasyon ki plen bri, mòd ak valè k ap chanje, pawòl Bondye a rete sèl sous direksyon, estabilite ak vole wo. Li te anseye chak vrè vizyon ak chak vrè siksè dwe aliyen ak Pawòl Bondye a, epi pandan yon moun ka gen siksè san li, se sèl Pawòl la ki pote satisfaksyon, paske ogmantasyon nan Pawòl la se ogmantasyon nan valè, nan sajès ak nan sa ou vo. Menm jan ak pastè nasyonal la, li te deklare sa ki ekri a pi fò pase sa k ap pase a: reyalite yo chanje, men verite Ekriti a kanpe pou tout tan. Li te devlope poukisa Pawòl la se kle pou vole wo, kòm fondasyon nou pou gwo reyalizasyon, yon semans espirityèl, yon fòs kreyatif, ak yon konpa pou objektif diven nou; ak sa li ban nou pouvwa pou fè: grandi rive nan matirite, kenbe tèt ak konfòmite, travèse tanpèt yo, epi fè lafwa fèt. Li te fèmen ak etap pratik pou yon lavi ki plen Pawòl, yon apèl sou lotèl, ak yon moman temwayaj.",
            "keyPoints": [
              "Nan yon jenerasyon ki plen bri, mòd ak valè k ap chanje, pawòl Bondye a se sèl sous direksyon, estabilite ak vole wo.",
              "Chak vrè vizyon ak chak vrè siksè dwe aliyen ak Pawòl Bondye a; siksè ak satisfaksyon se pa menm bagay, e se sèl Pawòl la ki fè ou jwenn satisfaksyon.",
              "Ogmantasyon nan Pawòl la se ogmantasyon nan valè, ogmantasyon nan sajès ak ogmantasyon nan sa ou vo; yon moun ki rete fidèl a Pawòl la pote valè.",
              "Sa ki ekri a pi fò pase sa k ap pase a: reyalite yo pasaje e yo ka chanje, men verite Ekriti a etènèl e li pase pi devan sikonstans yo.",
              "Pawòl la se kle pou vole wo paske se fondasyon nou pou kòmande gwo reyalizasyon, yon semans espirityèl k ap repwodui nan nenpòt moun ki angaje li, li gen yon kapasite san limit pou kreye ak rekreye, epi se konpa nou pou objektif diven nou.",
              "Pawòl la ban nou pouvwa pou grandi rive nan matirite espirityèl, li transfòme lespri nou pou nou pa konfòme ak lemonn, li pwoteje destine nou kont tanpèt lavi yo, epi li fè lafwa fèt, paske lafwa soti nan tande.",
              "Prèv yon lavi ki chita sou Pawòl la: ou pran desizyon dapre Ekriti a olye de emosyon, pawòl ou pote pouvwa, ou pote repons olye de opinyon, epi ou jwi yon gwo lapè nan sezon ensètitid.",
              "Pou Pawòl la ba ou pouvwa: etidye li chak jou, mande Lespri Sen an revele li, medite li jouk limyè ak revelasyon vini, epi antoure tèt ou ak yon anviwònman ki plen Pawòl."
            ],
            "scriptures": [
              {
                "reference": "Danyèl 11:32",
                "text": "Men pèp ki konnen Bondye yo a ap gen fòs, epi y ap fè gwo bagay.",
                "note": "Tèks prensipal la: Pawòl la se fondasyon nou pou kòmande gwo reyalizasyon.",
                "timestamp": "02:20:49"
              },
              {
                "reference": "Lik 8:11",
                "text": "Men sans parabòl la: semans lan se pawòl Bondye a.",
                "note": "Dezyèm tèks prensipal la: Pawòl la se yon semans espirityèl k ap repwodui nan nenpòt moun ki angaje li.",
                "timestamp": "02:20:49"
              },
              {
                "reference": "Sòm 119:89",
                "text": "Pou tout tan, o Seyè, pawòl ou kanpe fèm nan syèl la.",
                "note": "Pawòl Bondye a etènèl.",
                "timestamp": "02:28:30"
              },
              {
                "reference": "Sòm 11:3",
                "text": "Si fondasyon yo detwi, kisa moun ki jis la ka fè?",
                "note": "Tout sa ki nan Pawòl Bondye a se yon fondasyon solid ki pa ka tonbe.",
                "timestamp": "02:31:23"
              },
              {
                "reference": "Lik 1:45",
                "text": "Benediksyon pou fi ki te kwè a, paske sa Seyè a te fè li konnen an gen pou akonpli.",
                "note": "Pa gen pawòl anlè nan wayòm sa a; chak pawòl Bondye akonpli.",
                "timestamp": "02:31:23"
              },
              {
                "reference": "Jan 1:3",
                "text": "Se pa mwayen li tout bagay te fèt; anyen nan sa ki fèt pa t fèt san li.",
                "note": "Pawòl la gen yon kapasite san limit pou kreye ak rekreye tout sa ki pa mache.",
                "timestamp": "02:32:39"
              },
              {
                "reference": "Jeremi 1:5",
                "text": "Anvan mwen te fòme ou nan vant manman ou, mwen te konnen ou; anvan ou te fèt, mwen te mete ou apa, mwen te etabli ou pwofèt pou nasyon yo.",
                "note": "Pawòl la se konpa nou pou dekouvri ak akonpli objektif diven nou.",
                "timestamp": "02:34:17"
              },
              {
                "reference": "Ebre 5:13-14",
                "text": "Nenpòt moun k ap bwè lèt toujou se yon tibebe li ye, li poko konn anyen nan pawòl jistis la. Men manje solid se pou granmoun.",
                "note": "Pawòl la fè nou grandi espirityèlman rive nan matirite.",
                "timestamp": "02:35:41"
              },
              {
                "reference": "2 Timote 3:16-17",
                "text": "Tout Ekriti a soti nan souf Bondye, epi li itil pou anseye, pou korije, pou remete moun sou wout, pou fòme nan jistis: konsa moun Bondye a ka konplè, byen ekipe pou tout bon zèv.",
                "note": "Ekriti a ekipe nou nèt pou yon lavi ki itil ak pou bon zèv.",
                "timestamp": "02:37:03"
              },
              {
                "reference": "Women 12:2",
                "text": "Pa konfòme nou ak lemonn sa a, men kite Bondye transfòme nou nan renouvèlman lespri nou, pou nou ka konnen sa ki bon, sa ki fè Bondye plezi, ak sa ki pafè nan volonte li.",
                "note": "Pawòl la transfòme lespri nou epi li ban nou pouvwa pou nou pa konfòme ak lemonn.",
                "timestamp": "02:38:27"
              },
              {
                "reference": "Matye 7:24-25",
                "text": "Se poutèt sa, nenpòt moun ki tande pawòl mwen yo epi ki fè yo, m ap konpare li ak yon moun saj ki bati kay li sou wòch.",
                "note": "Se fè Pawòl la, pa sèlman tande li, ki pwoteje destine nou kont tanpèt lavi yo.",
                "timestamp": "02:41:17"
              },
              {
                "reference": "Women 10:17",
                "text": "Konsa, lafwa soti nan tande, epi tande a soti nan pawòl Bondye a.",
                "note": "Pawòl la fè lafwa fèt, e lè lafwa vin vivan, viktwa a mache avè li.",
                "timestamp": "02:41:17"
              },
              {
                "reference": "Filipyen 4:8",
                "text": "Pou fini, frè m yo, tout sa ki vre, tout sa ki onèt, tout sa ki jis, tout sa ki pwòp, tout sa ki emab, tout sa ki gen bon repitasyon; si gen yon vèti, si gen yon bagay ki merite lwanj, se sou bagay sa yo pou nou kenbe panse nou.",
                "note": "Yon lavi ki chita sou Pawòl la pran desizyon dapre Ekriti a, pa dapre emosyon.",
                "timestamp": "02:42:46"
              },
              {
                "reference": "Jan 6:63",
                "text": "Se Lespri a ki bay lavi; lachè pa itil anyen. Pawòl mwen di nou yo, se lespri yo ye, se lavi yo ye.",
                "note": "Lè Pawòl la ap viv nan ou, pwòp pawòl ou vin pote pouvwa.",
                "timestamp": "02:44:11"
              },
              {
                "reference": "Ezayi 33:6",
                "text": "Sajès ak konesans va estabilite tan ou yo, ak yon fòs delivrans; krent Seyè a se trezò li.",
                "note": "Yon lavi ki chita sou Pawòl la pote repons, pa sèlman opinyon.",
                "timestamp": "02:45:30"
              },
              {
                "reference": "Sòm 119:165",
                "text": "Moun ki renmen lalwa ou jwi yon gwo lapè, epi anyen pa ka fè yo bite.",
                "note": "Moun ki anrasinen nan Pawòl la jwi yon gwo lapè ak konfyans nan sezon ensètitid.",
                "timestamp": "02:45:30"
              },
              {
                "reference": "Matye 4:4",
                "text": "Men li reponn: Men sa ki ekri: Se pa pen sèlman k ap fè moun viv, men se chak pawòl ki soti nan bouch Bondye.",
                "note": "Jezi te reponn tantasyon Satan yo ak 'Men sa ki ekri'; fòk ou konnen sa ki ekri a.",
                "timestamp": "02:48:01"
              },
              {
                "reference": "Jozye 1:8",
                "text": "Liv lalwa sa a pa dwe janm kite bouch ou; medite li lajounen kou lannwit... Se konsa w ap fè chemen ou pwospere, se konsa w ap gen bon siksè.",
                "note": "Jozye te vin san rival paske li te medite Pawòl la lajounen kou lannwit; gen siksè, epi gen bon siksè.",
                "timestamp": "02:50:18"
              },
              {
                "reference": "2 Timote 3:15",
                "text": "Depi ou timoun ou konnen Ekriti sen yo, ki kapab ba ou sajès ki mennen nan sali atravè lafwa nan Jezikri.",
                "note": "Timote te grandi nan Ekriti yo depi li timoun; pèsonn nan nou pa twò jèn pou reye.",
                "timestamp": "02:51:32"
              },
              {
                "reference": "2 Timote 2:15",
                "text": "Fè tout efò ou pou ou prezante tèt ou devan Bondye tankou yon moun ki fè prèv li, yon ouvriye ki pa gen wont, k ap separe pawòl verite a jan sa dwe fèt.",
                "note": "Premye etap pou Pawòl la ba ou pouvwa: etidye li chak jou.",
                "timestamp": "02:52:50"
              },
              {
                "reference": "Sòm 1:1-3",
                "text": "Men tout plezi li se nan lalwa Seyè a, epi l ap medite lalwa a lajounen kou lannwit... tout sa l ap fè ap mache byen.",
                "note": "Medite Pawòl la jouk ou jwenn limyè ak revelasyon.",
                "timestamp": "02:54:13"
              },
              {
                "reference": "Kolosyen 3:16",
                "text": "Se pou pawòl Kris la rete nan nou an abondans ak tout sajès; se pou nou anseye epi ankouraje youn lòt ak sòm, kantik ak chan espirityèl, pandan n ap chante pou Seyè a ak rekonesans nan kè nou.",
                "note": "Antoure tèt ou ak yon anviwònman ki plen Pawòl: liv, mesaj ak chan k ap leve nanm ou.",
                "timestamp": "02:55:25"
              }
            ],
            "quotes": [
              {
                "quote": "Sa ki ekri a pi fò pase sa k ap pase a. Sa k ap pase a pasaje; sa ki ekri a etènèl.",
                "timestamp": "02:25:39"
              },
              {
                "quote": "Nou ka fè lavi nou mache lè nou swiv pwòp chemen pa nou, men se sèlman lè nou swiv chemen Bondye yo nou ka bati yon vrè lavi. E chemen Bondye yo nan Pawòl li.",
                "timestamp": "02:23:16"
              },
              {
                "quote": "Ogmantasyon nan Pawòl la se ogmantasyon nan valè. Ogmantasyon nan Pawòl la se ogmantasyon nan sajès. Ogmantasyon nan Pawòl la se ogmantasyon nan sa ou vo.",
                "timestamp": "02:23:16"
              },
              {
                "quote": "Reyalite a se ke ou razè jodi a, men verite a se ke ou fèt rich nan Kris. Reyalite yo ka chanje selon sikonstans yo, men sa ki ekri a ap toujou pase pi devan sa k ap pase a.",
                "timestamp": "02:27:01"
              },
              {
                "quote": "Pawòl Bondye a se pi gwo zam ou ak kle siprèm pou vole wo. Angaje li chak jou, medite li chak jou, obeyi li chak jou, epi lavi ou ap transfòme.",
                "timestamp": "02:56:41"
              }
            ]
          },
          "es": {
            "title": "Viviendo la Palabra para remontar vuelo",
            "preacher": "Predicador invitado",
            "summary": "En esta segunda palabra de la sesión matutina del día 2, el ministro abrió desde Daniel 11:32 y Lucas 8:11 para mostrar que, en una generación llena de ruido, tendencias y valores cambiantes, la palabra de Dios sigue siendo la única fuente de dirección, estabilidad y vuelo en las alturas. Enseñó que toda visión verdadera y todo éxito verdadero deben alinearse con la Palabra de Dios, y que aunque una persona puede tener éxito sin ella, solo la Palabra produce plenitud, porque crecer en la Palabra es crecer en valor, en sabiduría y en valía. Haciendo eco del pastor nacional, declaró que lo que está escrito es superior a lo que está sucediendo: los hechos cambian, pero la verdad de la Escritura permanece para siempre. Desglosó por qué la Palabra es clave para volar alto, como nuestro fundamento para las proezas, una semilla espiritual, una fuerza creadora y una brújula para el propósito divino, y lo que nos capacita a hacer: crecer a la madurez, resistir la conformidad, atravesar las tormentas y dar a luz la fe. Cerró con pasos prácticos para una vida llena de la Palabra, un llamado al altar y un tiempo de testimonios.",
            "keyPoints": [
              "En una generación de ruido, tendencias y valores cambiantes, la palabra de Dios es la única fuente de dirección, estabilidad y vuelo en las alturas.",
              "Toda visión verdadera y todo éxito verdadero deben alinearse con la Palabra de Dios; el éxito y la plenitud no son lo mismo, y solo la Palabra te hace pleno.",
              "Crecer en la Palabra es crecer en valor, crecer en sabiduría y crecer en valía; un hombre que se mantiene fiel a la Palabra lleva valía consigo.",
              "Lo que está escrito es superior a lo que está sucediendo: los hechos son temporales y pueden cambiar, pero la verdad de la Escritura es eterna y prevalece sobre las circunstancias.",
              "La Palabra es clave para volar alto porque es nuestro fundamento para comandar proezas, una semilla espiritual que se reproduce en quien la trabaja, tiene capacidad ilimitada para crear y recrear, y es nuestra brújula para el propósito divino.",
              "La Palabra nos capacita para crecer a la madurez espiritual, transforma nuestra mente para que no nos conformemos al mundo, asegura nuestro destino contra las tormentas de la vida y da a luz la fe, porque la fe viene por el oír.",
              "Evidencias de una vida arraigada en la Palabra: tomas decisiones por la Escritura y no por las emociones, tus palabras llevan poder, llevas respuestas en lugar de opiniones, y disfrutas de gran paz en temporadas de incertidumbre.",
              "Para ser capacitado por la Palabra: estúdiala a diario, pide al Espíritu Santo que te la revele, medita en ella hasta que vengan la luz y la revelación, y rodéate de un ambiente lleno de la Palabra."
            ],
            "scriptures": [
              {
                "reference": "Daniel 11:32",
                "text": "Mas el pueblo que conoce a su Dios, se esforzará, y hará.",
                "note": "Texto ancla: la Palabra es nuestro fundamento para comandar proezas.",
                "timestamp": "02:20:49"
              },
              {
                "reference": "Lucas 8:11",
                "text": "Es pues ésta la parábola: La simiente es la palabra de Dios.",
                "note": "Segundo texto ancla: la Palabra es una semilla espiritual que se reproduce en cualquiera que la trabaja.",
                "timestamp": "02:20:49"
              },
              {
                "reference": "Salmos 119:89",
                "text": "Para siempre, oh Jehová, permanece tu palabra en los cielos.",
                "note": "La Palabra de Dios es eterna.",
                "timestamp": "02:28:30"
              },
              {
                "reference": "Salmos 11:3",
                "text": "Si fueren destruidos los fundamentos, ¿qué ha de hacer el justo?",
                "note": "Todo lo que se encuentra en la Palabra de Dios es un fundamento firme que no puede fallar.",
                "timestamp": "02:31:23"
              },
              {
                "reference": "Lucas 1:45",
                "text": "Y bienaventurada la que creyó, porque se cumplirán las cosas que le fueron dichas de parte del Señor.",
                "note": "En este reino no hay palabras ociosas; toda palabra de Dios se cumple.",
                "timestamp": "02:31:23"
              },
              {
                "reference": "Juan 1:3",
                "text": "Todas las cosas por él fueron hechas; y sin él nada de lo que es hecho, fue hecho.",
                "note": "La Palabra tiene capacidad ilimitada para crear y recrear todo lo que no está funcionando.",
                "timestamp": "02:32:39"
              },
              {
                "reference": "Jeremías 1:5",
                "text": "Antes que te formase en el vientre te conocí, y antes que salieses de la matriz te santifiqué, te di por profeta a las gentes.",
                "note": "La Palabra es nuestra brújula para descubrir y cumplir el propósito divino.",
                "timestamp": "02:34:17"
              },
              {
                "reference": "Hebreos 5:13-14",
                "text": "Que cualquiera que participa de la leche, es inhábil para la palabra de la justicia, porque es niño; mas la vianda firme es para los perfectos.",
                "note": "La Palabra impulsa nuestro crecimiento y madurez espiritual.",
                "timestamp": "02:35:41"
              },
              {
                "reference": "2 Timoteo 3:16-17",
                "text": "Toda Escritura es inspirada divinamente y útil para enseñar, para redargüir, para corregir, para instituir en justicia, para que el hombre de Dios sea perfecto, enteramente instruido para toda buena obra.",
                "note": "La Escritura nos equipa por completo para una vida provechosa y para toda buena obra.",
                "timestamp": "02:37:03"
              },
              {
                "reference": "Romanos 12:2",
                "text": "Y no os conforméis a este siglo; mas reformaos por la renovación de vuestro entendimiento, para que experimentéis cuál sea la buena voluntad de Dios, agradable y perfecta.",
                "note": "La Palabra transforma nuestra mente y nos capacita para no conformarnos al mundo.",
                "timestamp": "02:38:27"
              },
              {
                "reference": "Mateo 7:24-25",
                "text": "Cualquiera, pues, que me oye estas palabras, y las hace, le compararé a un hombre prudente, que edificó su casa sobre la peña.",
                "note": "Hacer la Palabra, no solo oírla, asegura nuestro destino contra las tormentas de la vida.",
                "timestamp": "02:41:17"
              },
              {
                "reference": "Romanos 10:17",
                "text": "Luego la fe es por el oír; y el oír por la palabra de Dios.",
                "note": "La Palabra da a luz la fe, y cuando la fe cobra vida, la victoria viene con ella.",
                "timestamp": "02:41:17"
              },
              {
                "reference": "Filipenses 4:8",
                "text": "Por lo demás, hermanos, todo lo que es verdadero, todo lo honesto, todo lo justo, todo lo puro, todo lo amable, todo lo que es de buen nombre; si hay virtud alguna, si alguna alabanza, en esto pensad.",
                "note": "Una vida arraigada en la Palabra toma decisiones basadas en la Escritura, no en las emociones.",
                "timestamp": "02:42:46"
              },
              {
                "reference": "Juan 6:63",
                "text": "El espíritu es el que da vida; la carne nada aprovecha: las palabras que yo os he hablado, son espíritu y son vida.",
                "note": "Cuando la Palabra vive en ti, tus propias palabras llevan poder.",
                "timestamp": "02:44:11"
              },
              {
                "reference": "Isaías 33:6",
                "text": "Y reinarán en tus tiempos la sabiduría y la ciencia, y abundancia de salvación: el temor de Jehová será su tesoro.",
                "note": "Una vida arraigada en la Palabra lleva respuestas, no solo opiniones.",
                "timestamp": "02:45:30"
              },
              {
                "reference": "Salmos 119:165",
                "text": "Mucha paz tienen los que aman tu ley; y no hay para ellos tropiezo.",
                "note": "Los arraigados en la Palabra disfrutan de gran paz y confianza en temporadas de incertidumbre.",
                "timestamp": "02:45:30"
              },
              {
                "reference": "Mateo 4:4",
                "text": "Mas él respondiendo, dijo: Escrito está: No con solo el pan vivirá el hombre, mas con toda palabra que sale de la boca de Dios.",
                "note": "Jesús respondió a las tentaciones de Satanás con 'Escrito está'; tienes que conocer lo que está escrito.",
                "timestamp": "02:48:01"
              },
              {
                "reference": "Josué 1:8",
                "text": "El libro de aquesta ley nunca se apartará de tu boca: antes de día y de noche meditarás en él... porque entonces harás prosperar tu camino, y todo te saldrá bien.",
                "note": "Josué se volvió imparable meditando en la Palabra de día y de noche; hay éxito, y hay buen éxito.",
                "timestamp": "02:50:18"
              },
              {
                "reference": "2 Timoteo 3:15",
                "text": "Y que desde la niñez has sabido las Sagradas Escrituras, las cuales te pueden hacer sabio para la salud por la fe que es en Cristo Jesús.",
                "note": "Timoteo creció desde la niñez en las Escrituras; ninguno de nosotros es demasiado joven para reinar.",
                "timestamp": "02:51:32"
              },
              {
                "reference": "2 Timoteo 2:15",
                "text": "Procura con diligencia presentarte a Dios aprobado, como obrero que no tiene de qué avergonzarse, que traza bien la palabra de verdad.",
                "note": "Primer paso para ser capacitado por la Palabra: estúdiala a diario.",
                "timestamp": "02:52:50"
              },
              {
                "reference": "Salmos 1:1-3",
                "text": "Antes en la ley de Jehová está su delicia, y en su ley medita de día y de noche... y todo lo que hace, prosperará.",
                "note": "Medita en la Palabra hasta que alcances luz y revelación.",
                "timestamp": "02:54:13"
              },
              {
                "reference": "Colosenses 3:16",
                "text": "La palabra de Cristo habite en vosotros en abundancia en toda sabiduría, enseñándoos y exhortándoos los unos a los otros con salmos e himnos y canciones espirituales, con gracia cantando en vuestros corazones al Señor.",
                "note": "Rodéate de un ambiente lleno de la Palabra: libros, mensajes y canciones que eleven el alma.",
                "timestamp": "02:55:25"
              }
            ],
            "quotes": [
              {
                "quote": "Lo que está escrito es superior a lo que está sucediendo. Lo que está sucediendo es temporal; lo que está escrito es eterno.",
                "timestamp": "02:25:39"
              },
              {
                "quote": "Podemos ganarnos la vida siguiendo nuestros propios caminos, pero solo podemos construir una vida siguiendo los caminos de Dios. Y los caminos de Dios están contenidos en su Palabra.",
                "timestamp": "02:23:16"
              },
              {
                "quote": "Crecer en la Palabra es crecer en valor. Crecer en la Palabra es crecer en sabiduría. Crecer en la Palabra es crecer en valía.",
                "timestamp": "02:23:16"
              },
              {
                "quote": "El hecho es que hoy estás en quiebra, pero la verdad es que en Cristo fuiste hecho rico. Los hechos pueden cambiar según las circunstancias, pero lo que está escrito siempre prevalecerá sobre lo que está sucediendo.",
                "timestamp": "02:27:01"
              },
              {
                "quote": "La palabra de Dios es tu arma más poderosa y la clave suprema para remontar vuelo. Trabájala a diario, medita en ella a diario, obedécela a diario, y tu vida será transformada.",
                "timestamp": "02:56:41"
              }
            ]
          },
          "fr": {
            "title": "Vivre la Parole pour s'élever",
            "preacher": "Prédicateur invité",
            "summary": "Dans cette seconde parole de la matinée du jour 2, le ministre a ouvert avec Daniel 11:32 et Luc 8:11 pour montrer que dans une génération remplie de bruit, de tendances et de valeurs mouvantes, la parole de Dieu demeure la seule source de direction, de stabilité et d'envol. Il a enseigné que toute vraie vision et tout vrai succès doivent s'aligner sur la Parole de Dieu, et que si l'on peut réussir sans elle, seule la Parole produit l'épanouissement, car croître dans la Parole, c'est croître en valeur, en sagesse et en prix. Faisant écho au pasteur national, il a déclaré que ce qui est écrit est supérieur à ce qui arrive : les faits changent, mais la vérité de l'Écriture demeure éternellement. Il a expliqué pourquoi la Parole est la clé de l'envol, comme fondement de nos exploits, semence spirituelle, force créatrice et boussole du dessein divin, et ce qu'elle nous rend capables de faire : grandir vers la maturité, résister au conformisme, traverser les tempêtes et enfanter la foi. Il a conclu par des étapes pratiques pour une vie remplie de la Parole, un appel et un temps de témoignages.",
            "keyPoints": [
              "Dans une génération de bruit, de tendances et de valeurs mouvantes, la parole de Dieu est la seule source de direction, de stabilité et d'envol.",
              "Toute vraie vision et tout vrai succès doivent s'aligner sur la Parole de Dieu; succès et épanouissement ne sont pas la même chose, et seule la Parole vous rend épanoui.",
              "Croître dans la Parole, c'est croître en valeur, en sagesse et en prix; l'homme qui reste fidèle à la Parole porte du prix.",
              "Ce qui est écrit est supérieur à ce qui arrive : les faits sont temporels et peuvent changer, mais la vérité de l'Écriture est éternelle et l'emporte sur les circonstances.",
              "La Parole est la clé de l'envol parce qu'elle est notre fondement pour commander des exploits, une semence spirituelle qui se reproduit en quiconque l'engage, une capacité illimitée de créer et de recréer, et notre boussole pour le dessein divin.",
              "La Parole nous rend capables de grandir vers la maturité spirituelle, transforme nos intelligences pour que nous ne nous conformions pas au monde, met notre destinée à l'abri des tempêtes de la vie et enfante la foi, car la foi vient de ce qu'on entend.",
              "Preuves d'une vie enracinée dans la Parole : vous prenez vos décisions selon l'Écriture plutôt que selon les émotions, vos paroles portent de la puissance, vous portez des réponses plutôt que des opinions, et vous jouissez d'une grande paix dans les saisons d'incertitude.",
              "Pour être rendu puissant par la Parole : l'étudier chaque jour, demander au Saint-Esprit de la révéler, la méditer jusqu'à recevoir lumière et révélation, et s'entourer d'un environnement rempli de la Parole."
            ],
            "scriptures": [
              {
                "reference": "Daniel 11:32",
                "text": "Mais ceux du peuple qui connaîtront leur Dieu agiront avec fermeté.",
                "note": "Texte d'ancrage : la Parole est notre fondement pour commander des exploits.",
                "timestamp": "02:20:49"
              },
              {
                "reference": "Luc 8:11",
                "text": "Voici ce que signifie cette parabole : La semence, c'est la parole de Dieu.",
                "note": "Second texte d'ancrage : la Parole est une semence spirituelle qui se reproduit en quiconque l'engage.",
                "timestamp": "02:20:49"
              },
              {
                "reference": "Psaume 119:89",
                "text": "À toujours, ô Éternel! ta parole subsiste dans les cieux.",
                "note": "La Parole de Dieu est éternelle.",
                "timestamp": "02:28:30"
              },
              {
                "reference": "Psaume 11:3",
                "text": "Quand les fondements sont renversés, le juste, que ferait-il?",
                "note": "Tout ce qui se trouve dans la Parole de Dieu est un fondement solide qui ne peut faillir.",
                "timestamp": "02:31:23"
              },
              {
                "reference": "Luc 1:45",
                "text": "Heureuse celle qui a cru, parce que les choses qui lui ont été dites de la part du Seigneur auront leur accomplissement.",
                "note": "Il n'y a pas de paroles vaines dans ce royaume; chaque parole de Dieu s'accomplit.",
                "timestamp": "02:31:23"
              },
              {
                "reference": "Jean 1:3",
                "text": "Toutes choses ont été faites par elle, et rien de ce qui a été fait n'a été fait sans elle.",
                "note": "La Parole a une capacité illimitée de créer et de recréer tout ce qui ne fonctionne pas.",
                "timestamp": "02:32:39"
              },
              {
                "reference": "Jérémie 1:5",
                "text": "Avant que je t'eusse formé dans le ventre de ta mère, je te connaissais, et avant que tu fusses sorti de son sein, je t'avais consacré, je t'avais établi prophète des nations.",
                "note": "La Parole est notre boussole pour découvrir et accomplir le dessein divin.",
                "timestamp": "02:34:17"
              },
              {
                "reference": "Hébreux 5:13-14",
                "text": "Or, quiconque en est au lait n'a pas l'expérience de la parole de justice; car il est un enfant. Mais la nourriture solide est pour les hommes faits.",
                "note": "La Parole favorise notre croissance et notre maturité spirituelles.",
                "timestamp": "02:35:41"
              },
              {
                "reference": "2 Timothée 3:16-17",
                "text": "Toute Écriture est inspirée de Dieu, et utile pour enseigner, pour convaincre, pour corriger, pour instruire dans la justice, afin que l'homme de Dieu soit accompli et propre à toute bonne œuvre.",
                "note": "L'Écriture nous équipe pleinement pour une vie profitable et de bonnes œuvres.",
                "timestamp": "02:37:03"
              },
              {
                "reference": "Romains 12:2",
                "text": "Ne vous conformez pas au siècle présent, mais soyez transformés par le renouvellement de l'intelligence, afin que vous discerniez quelle est la volonté de Dieu, ce qui est bon, agréable et parfait.",
                "note": "La Parole transforme nos intelligences et nous rend capables de ne pas nous conformer au monde.",
                "timestamp": "02:38:27"
              },
              {
                "reference": "Matthieu 7:24-25",
                "text": "C'est pourquoi, quiconque entend ces paroles que je dis et les met en pratique, sera semblable à un homme prudent qui a bâti sa maison sur le roc.",
                "note": "Mettre la Parole en pratique, et pas seulement l'entendre, met notre destinée à l'abri des tempêtes de la vie.",
                "timestamp": "02:41:17"
              },
              {
                "reference": "Romains 10:17",
                "text": "Ainsi la foi vient de ce qu'on entend, et ce qu'on entend vient de la parole de Christ.",
                "note": "La Parole enfante la foi, et quand la foi s'éveille, la victoire suit.",
                "timestamp": "02:41:17"
              },
              {
                "reference": "Philippiens 4:8",
                "text": "Au reste, frères, que tout ce qui est vrai, tout ce qui est honorable, tout ce qui est juste, tout ce qui est pur, tout ce qui est aimable, tout ce qui mérite l'approbation, ce qui est vertueux et digne de louange, soit l'objet de vos pensées.",
                "note": "Une vie enracinée dans la Parole prend ses décisions selon l'Écriture, non selon les émotions.",
                "timestamp": "02:42:46"
              },
              {
                "reference": "Jean 6:63",
                "text": "C'est l'esprit qui vivifie; la chair ne sert de rien. Les paroles que je vous ai dites sont esprit et vie.",
                "note": "Quand la Parole vit en vous, vos propres paroles portent de la puissance.",
                "timestamp": "02:44:11"
              },
              {
                "reference": "Ésaïe 33:6",
                "text": "Tes jours seront en sûreté; la sagesse et l'intelligence sont une source de salut; la crainte de l'Éternel, c'est là le trésor de Sion.",
                "note": "Une vie enracinée dans la Parole porte des réponses, pas seulement des opinions.",
                "timestamp": "02:45:30"
              },
              {
                "reference": "Psaume 119:165",
                "text": "Il y a beaucoup de paix pour ceux qui aiment ta loi, et il ne leur arrive aucun malheur.",
                "note": "Ceux qui sont enracinés dans la Parole jouissent d'une grande paix et d'une grande assurance dans les saisons d'incertitude.",
                "timestamp": "02:45:30"
              },
              {
                "reference": "Matthieu 4:4",
                "text": "Jésus répondit : Il est écrit : L'homme ne vivra pas de pain seulement, mais de toute parole qui sort de la bouche de Dieu.",
                "note": "Jésus a répondu aux tentations de Satan par « Il est écrit »; vous devez connaître ce qui est écrit.",
                "timestamp": "02:48:01"
              },
              {
                "reference": "Josué 1:8",
                "text": "Que ce livre de la loi ne s'éloigne point de ta bouche; médite-le jour et nuit... car c'est alors que tu auras du succès dans tes entreprises, c'est alors que tu réussiras.",
                "note": "Josué est devenu inarrêtable en méditant la Parole jour et nuit; il y a le succès, et il y a le bon succès.",
                "timestamp": "02:50:18"
              },
              {
                "reference": "2 Timothée 3:15",
                "text": "Dès ton enfance, tu connais les saintes lettres, qui peuvent te rendre sage à salut par la foi en Jésus-Christ.",
                "note": "Timothée a grandi dès l'enfance dans les Écritures; aucun de nous n'est trop jeune pour régner.",
                "timestamp": "02:51:32"
              },
              {
                "reference": "2 Timothée 2:15",
                "text": "Efforce-toi de te présenter devant Dieu comme un homme éprouvé, un ouvrier qui n'a point à rougir, qui dispense droitement la parole de la vérité.",
                "note": "Première étape pour être rendu puissant par la Parole : l'étudier chaque jour.",
                "timestamp": "02:52:50"
              },
              {
                "reference": "Psaume 1:1-3",
                "text": "Mais qui trouve son plaisir dans la loi de l'Éternel, et qui la médite jour et nuit!... Tout ce qu'il fait lui réussit.",
                "note": "Méditez la Parole jusqu'à recevoir lumière et révélation.",
                "timestamp": "02:54:13"
              },
              {
                "reference": "Colossiens 3:16",
                "text": "Que la parole de Christ habite parmi vous abondamment; instruisez-vous et exhortez-vous les uns les autres en toute sagesse, par des psaumes, par des hymnes, par des cantiques spirituels, chantant à Dieu dans vos cœurs sous l'inspiration de la grâce.",
                "note": "Entourez-vous d'un environnement rempli de la Parole : livres, messages et chants qui élèvent l'âme.",
                "timestamp": "02:55:25"
              }
            ],
            "quotes": [
              {
                "quote": "Ce qui est écrit est supérieur à ce qui arrive. Ce qui arrive est temporaire; ce qui est écrit est éternel.",
                "timestamp": "02:25:39"
              },
              {
                "quote": "Nous pouvons gagner notre vie en suivant nos propres voies, mais nous ne pouvons bâtir une vie qu'en suivant les voies de Dieu. Et les voies de Dieu sont contenues dans sa Parole.",
                "timestamp": "02:23:16"
              },
              {
                "quote": "Quand la Parole augmente en vous, votre valeur augmente. Quand la Parole augmente, la sagesse augmente. Quand la Parole augmente, votre prix augmente.",
                "timestamp": "02:23:16"
              },
              {
                "quote": "Le fait, c'est que vous êtes fauché aujourd'hui, mais la vérité, c'est que vous êtes rendu riche en Christ. Les faits peuvent changer selon les circonstances, mais ce qui est écrit l'emportera toujours sur ce qui arrive.",
                "timestamp": "02:27:01"
              },
              {
                "quote": "La parole de Dieu est votre plus grande arme et la clé ultime de l'envol. Engagez-la chaque jour, méditez-la chaque jour, obéissez-lui chaque jour, et votre vie sera transformée.",
                "timestamp": "02:56:41"
              }
            ]
          }
        }
      }
    ]
  },
  {
    "day": 3,
    "label": "Day 3",
    "dateLabel": "Thursday, July 23",
    "date": "2026-07-23",
    "messages": [
      {
        "slug": "day-3-morning-engaging-the-power-of-faith",
        "day": 3,
        "session": "morning",
        "date": "2026-07-23",
        "videoId": "1yiqpDwJTH0",
        "startTimestamp": "01:08:03",
        "title": "Engaging the Power of Faith for Soaring Like an Eagle",
        "preacher": "Ministering",
        "summary": "Building on the summit's teaching about the characteristics of eagles, this first word of the morning shows how to engage faith so those characteristics begin to show up in our lives. From Isaiah 40:30-31, the preacher points out that the promise of mounting up with wings has a condition: waiting on the Lord, and that faith is developed in the waiting season, never on the day of battle. Faith is not an elective course but a must-have, because storms of life are constant. He defines faith as being fully persuaded of the truth of God's Word regardless of prevailing circumstances, and as putting God's Word to work, since faith without works is dead. Faith is not wishful thinking, mere optimism, or positive thinking; it is often contrary to fact. He walks through why faith matters, how to engage it through the Word, confession, action, patience, and prayer, and points to Abraham, David, the woman with the issue of blood, Daniel, and Peter as believers who soared by faith, closing with the cry, 'Lord, help my unbelief.'",
        "keyPoints": [
          "The promise of Isaiah 40:31 has a condition: waiting upon the Lord. Faith is built in the waiting time, not on the day of battle (Proverbs 21:31).",
          "Faith is not an elective course; it is a must-have virtue, because the storms of life are constant.",
          "Faith is being fully persuaded of the truth of God's Word, the prevailing circumstances notwithstanding, and putting God's Word to work through obedience.",
          "Faith is not wishful thinking, mere optimism, or positive thinking; it is usually contrary to fact, looking at unseen eternal things rather than temporal facts.",
          "Faith makes nothing impossible, shifts our attention from challenges to God, empowers bold action, and provokes supernatural intervention.",
          "How to engage the power of faith: study and listen to God's Word continually, believe God's promises, speak faith-filled words, act on the Word, remain patient, and live a life of prayer.",
          "Biblical examples of soaring by faith: Abraham believed against hope, David defeated Goliath with a stone, the woman with the issue of blood pressed through, Daniel chose the lions' den, Peter stepped out of the boat.",
          "Hindrances to soaring by faith: unbelief, doubt, fear of failure, sin, negative associations, lack of spiritual nourishment, and impatience."
        ],
        "scriptures": [
          {
            "reference": "Mark 9:23-24",
            "text": "Jesus said unto him, If thou canst believe, all things are possible to him that believeth. And straightway the father of the child cried out, and said with tears, Lord, I believe; help thou mine unbelief.",
            "note": "Anchor scripture for the message; the closing prayer echoed the father's cry, 'Lord, help my unbelief.'",
            "timestamp": "01:10:14"
          },
          {
            "reference": "Isaiah 40:30-31",
            "text": "Even the youths shall faint and be weary, and the young men shall utterly fall: but they that wait upon the LORD shall renew their strength; they shall mount up with wings as eagles.",
            "note": "Opening text; even the strength of youth fails, but waiting on the Lord is the condition for soaring.",
            "timestamp": "01:09:08"
          },
          {
            "reference": "John 16:33",
            "text": "These things I have spoken unto you, that in me ye might have peace. In the world ye shall have tribulation: but be of good cheer; I have overcome the world.",
            "note": "Storms will come, but the moment we run into Christ, we run into peace.",
            "timestamp": "01:12:22"
          },
          {
            "reference": "Proverbs 21:31",
            "text": "The horse is prepared against the day of battle: but safety is of the LORD.",
            "note": "The horse is prepared for the day of battle, not on it; faith is developed during the waiting time.",
            "timestamp": "01:16:38"
          },
          {
            "reference": "Mark 11:23",
            "text": "Whosoever shall say unto this mountain, Be thou removed, and be thou cast into the sea; and shall not doubt in his heart, but shall believe that those things which he saith shall come to pass; he shall have whatsoever he saith.",
            "note": "Faith is hard work, not cheap talk; faith-filled words cast mountains into the sea.",
            "timestamp": "01:21:55"
          },
          {
            "reference": "2 Corinthians 4:18",
            "text": "While we look not at the things which are seen, but at the things which are not seen: for the things which are seen are temporal; but the things which are not seen are eternal.",
            "note": "Facts are temporal and can change; faith holds to the eternal unseen.",
            "timestamp": "01:22:59"
          },
          {
            "reference": "Matthew 17:20",
            "text": "If ye have faith as a grain of mustard seed, ye shall say unto this mountain, Remove hence to yonder place; and it shall remove; and nothing shall be impossible unto you.",
            "note": "With faith, nothing is impossible: set a career, business, or kingdom-advancement target and hit it.",
            "timestamp": "01:25:14"
          },
          {
            "reference": "James 2:18",
            "text": "Yea, a man may say, Thou hast faith, and I have works: shew me thy faith without thy works, and I will shew thee my faith by my works.",
            "note": "Faith is putting God's Word to work; soaring comes by practising what has been taught, not just attending the summit.",
            "timestamp": "01:21:55"
          },
          {
            "reference": "Hebrews 11:8",
            "text": "By faith Abraham, when he was called to go out into a place which he should after receive for an inheritance, obeyed; and he went out, not knowing whither he went.",
            "note": "Faith empowers bold action; Abraham departed at once without knowing where he was going.",
            "timestamp": "01:28:26"
          },
          {
            "reference": "Numbers 23:19",
            "text": "God is not a man, that he should lie; neither the son of man, that he should repent: hath he said, and shall he not do it? or hath he spoken, and shall he not make it good?",
            "note": "Believe God's promises; God cannot lie because He has no reputation to defend with anyone.",
            "timestamp": "01:32:44"
          }
        ],
        "quotes": [
          {
            "quote": "You don't build and develop your faith on the day of the battle. Faith is developed during the waiting time.",
            "timestamp": "01:15:35"
          },
          {
            "quote": "If you and I must soar high in life, faith is not an option. It is a must-have virtue, because the storms of life are constant.",
            "timestamp": "01:17:41"
          },
          {
            "quote": "It doesn't matter what the doctor says. It doesn't matter what the immigration expert says. Faith says: God said it, I believe it, that settles it.",
            "timestamp": "01:20:51"
          },
          {
            "quote": "Faith is not fact. In fact, if it is fact, it is not faith. Faith is usually contrary to fact.",
            "timestamp": "01:24:04"
          },
          {
            "quote": "Faith does not ignore challenges, but sees them as opportunities for God to display His power.",
            "timestamp": "01:42:25"
          }
        ],
        "translations": {
          "creole": {
            "title": "Angaje Pouvwa Lafwa pou Vole Wo tankou yon Malfini",
            "preacher": "Predikatè envite",
            "summary": "Apre ansèyman somè a sou karakteristik malfini yo, premye pawòl maten an montre kijan pou nou angaje lafwa pou karakteristik sa yo kòmanse parèt nan lavi nou. Apati Ezayi 40:30-31, predikatè a fè nou wè pwomès pou nou monte anlè ak zèl la gen yon kondisyon: tann Seyè a, epi lafwa devlope nan sezon tann nan, li pa janm devlope jou batay la. Lafwa se pa yon kou opsyonèl; se yon bagay nou dwe genyen, paske tanpèt lavi yo la tout tan. Li defini lafwa kòm yon konviksyon total nan verite Pawòl Bondye a, kèlkeswa sikonstans k ap pase yo, epi kòm mete Pawòl Bondye a an aksyon, paske lafwa san zèv mouri. Lafwa se pa lespwa anlè, ni yon senp optimis, ni panse pozitif; byen souvan li kontrè ak reyalite je wè a. Li esplike poukisa lafwa enpòtan, kijan pou nou angaje l atravè Pawòl la, konfesyon, aksyon, pasyans ak lapriyè, epi li montre Abraram, David, fanm ki te gen pèt san an, Danyèl ak Pyè kòm kwayan ki te vole wo pa lafwa, anvan li fèmen ak kri sa a: 'Seyè, ede enkredilite m.'",
            "keyPoints": [
              "Pwomès Ezayi 40:31 la gen yon kondisyon: tann Seyè a. Lafwa bati nan tan tann nan, pa jou batay la (Pwovèb 21:31).",
              "Lafwa se pa yon kou opsyonèl; se yon vèti nou dwe genyen, paske tanpèt lavi yo la tout tan.",
              "Lafwa se lè ou konvenki nèt de verite Pawòl Bondye a, kèlkeswa sikonstans yo, epi ou mete Pawòl Bondye a an aksyon nan obeyisans.",
              "Lafwa se pa lespwa anlè, ni senp optimis, ni panse pozitif; byen souvan li kontrè ak reyalite je wè, li gade bagay etènèl nou pa wè yo olye de reyalite pasajè yo.",
              "Lafwa fè anyen pa enposib, li vire atansyon nou sot sou defi yo pou mete l sou Bondye, li bay kouraj pou aji ak odas, epi li pwovoke entèvansyon sinatirèl.",
              "Kijan pou angaje pouvwa lafwa: etidye epi koute Pawòl Bondye a san rete, kwè pwomès Bondye yo, pale pawòl ki chaje ak lafwa, aji sou Pawòl la, rete pasyan, epi viv yon lavi lapriyè.",
              "Egzanp biblik moun ki te vole wo pa lafwa: Abraram te kwè kont tout espwa, David te kraze Golyat ak yon wòch, fanm ki te gen pèt san an te fòse pase, Danyèl te chwazi twou lyon yo, Pyè te soti nan kannòt la.",
              "Sa k ap anpeche nou vole wo pa lafwa: enkredilite, dout, pè echèk, peche, move zanmi, mank manje espirityèl, ak enpasyans."
            ],
            "scriptures": [
              {
                "reference": "Mak 9:23-24",
                "text": "Jezi di li: Si ou ka kwè, tout bagay posib pou moun ki kwè. Menm lè a, papa timoun nan rele byen fò, ak dlo nan je li: Seyè, mwen kwè; ede enkredilite mwen!",
                "note": "Ekriti prensipal mesaj la; lapriyè fèmti a te repete kri papa a: 'Seyè, ede enkredilite m.'",
                "timestamp": "01:10:14"
              },
              {
                "reference": "Ezayi 40:30-31",
                "text": "Menm jèn yo ap febli, y ap bouke, epi jenn gason yo ap tonbe nèt. Men moun k ap tann Seyè a ap jwenn fòs nouvo; y ap monte anlè ak zèl tankou malfini.",
                "note": "Tèks ouvèti a; menm fòs jenès la konn fini, men tann Seyè a se kondisyon pou vole wo.",
                "timestamp": "01:09:08"
              },
              {
                "reference": "Jan 16:33",
                "text": "Mwen di nou bagay sa yo pou nou ka gen lapè nan mwen. Nan lemonn, n ap jwenn tribilasyon; men pran kouraj: mwen venk lemonn.",
                "note": "Tanpèt yo ap vini, men depi nou kouri antre nan Kris, nou antre nan lapè.",
                "timestamp": "01:12:22"
              },
              {
                "reference": "Pwovèb 21:31",
                "text": "Yo prepare chwal la pou jou batay la, men se nan men Seyè a delivrans lan soti.",
                "note": "Yo prepare chwal la pou jou batay la, yo pa prepare l jou a menm; lafwa devlope pandan tan tann nan.",
                "timestamp": "01:16:38"
              },
              {
                "reference": "Mak 11:23",
                "text": "Nenpòt moun ki di mòn sa a: Wete kò ou la, al jete ou nan lanmè, epi ki pa doute nan kè li, men ki kwè sa li di a ap rive, l ap jwenn sa li di a.",
                "note": "Lafwa se travay serye, se pa pale anlè; pawòl ki chaje ak lafwa jete mòn nan lanmè.",
                "timestamp": "01:21:55"
              },
              {
                "reference": "2 Korentyen 4:18",
                "text": "Nou pa fikse je nou sou bagay nou wè yo, men sou bagay nou pa wè yo; paske bagay nou wè yo pasaje, men bagay nou pa wè yo etènèl.",
                "note": "Reyalite je wè yo pasaje e yo ka chanje; lafwa kenbe fèm nan sa ki etènèl nou pa wè a.",
                "timestamp": "01:22:59"
              },
              {
                "reference": "Matye 17:20",
                "text": "Si nou gen lafwa gwosè yon grenn moutad, n a di mòn sa a: Deplase, al lòt bò, epi l ap deplase; anyen p ap enposib pou nou.",
                "note": "Ak lafwa, anyen pa enposib: fikse yon objektif nan karyè, nan biznis, oswa pou avansman wayòm nan, epi rive ladan li.",
                "timestamp": "01:25:14"
              },
              {
                "reference": "Jak 2:18",
                "text": "Yon moun ka di: Ou menm ou gen lafwa, mwen menm mwen gen zèv. Montre m lafwa ou san zèv, epi mwen menm m ap montre ou lafwa mwen nan zèv mwen yo.",
                "note": "Lafwa se mete Pawòl Bondye a an aksyon; se pratike sa yo anseye a ki fè nou vole wo, pa sèlman asiste somè a.",
                "timestamp": "01:21:55"
              },
              {
                "reference": "Ebre 11:8",
                "text": "Se pa lafwa Abraram te obeyi lè Bondye te rele l pou li soti al nan yon peyi li ta pral resevwa kòm eritaj; li pati san li pa t konnen kote li t aprale.",
                "note": "Lafwa bay kouraj pou aji ak odas; Abraram pati lapoula san li pa t konnen kote li t aprale.",
                "timestamp": "01:28:26"
              },
              {
                "reference": "Resansman 23:19",
                "text": "Bondye pa yon moun pou li ta bay manti, ni yon pitit lòm pou li ta chanje lide. Èske l ap di yon bagay san li pa fè l? Èske l ap pwomèt san li pa kenbe pawòl li?",
                "note": "Kwè pwomès Bondye yo; Bondye pa ka bay manti paske li pa gen okenn repitasyon pou li defann devan pèsonn.",
                "timestamp": "01:32:44"
              }
            ],
            "quotes": [
              {
                "quote": "Ou pa bati ni devlope lafwa ou jou batay la. Lafwa devlope pandan tan tann nan.",
                "timestamp": "01:15:35"
              },
              {
                "quote": "Si ou menm ak mwen dwe vole byen wo nan lavi a, lafwa se pa yon opsyon. Se yon vèti nou dwe genyen, paske tanpèt lavi yo la tout tan.",
                "timestamp": "01:17:41"
              },
              {
                "quote": "Sa doktè a di pa enpòtan. Sa ekspè imigrasyon an di pa enpòtan. Lafwa di: Bondye di li, mwen kwè li, sa regle sa.",
                "timestamp": "01:20:51"
              },
              {
                "quote": "Lafwa se pa reyalite je wè. An reyalite, si se reyalite je wè, se pa lafwa. Byen souvan, lafwa kontrè ak reyalite je wè a.",
                "timestamp": "01:24:04"
              },
              {
                "quote": "Lafwa pa inyore defi yo, men li wè yo tankou okazyon pou Bondye montre pouvwa li.",
                "timestamp": "01:42:25"
              }
            ]
          },
          "es": {
            "title": "Activando el poder de la fe para remontar vuelo como el águila",
            "preacher": "Predicador invitado",
            "summary": "Partiendo de la enseñanza del summit sobre las características de las águilas, esta primera palabra de la mañana muestra cómo activar la fe para que esas características comiencen a manifestarse en nuestra vida. Desde Isaías 40:30-31, el predicador señala que la promesa de levantar las alas tiene una condición: esperar en el Señor, y que la fe se desarrolla en la temporada de espera, nunca en el día de la batalla. La fe no es una materia optativa sino indispensable, porque las tormentas de la vida son constantes. Define la fe como estar plenamente persuadido de la verdad de la Palabra de Dios sin importar las circunstancias presentes, y como poner la Palabra de Dios en acción, pues la fe sin obras es muerta. La fe no es un deseo ilusorio, simple optimismo ni pensamiento positivo; muchas veces es contraria a los hechos. Recorre por qué importa la fe, cómo activarla a través de la Palabra, la confesión, la acción, la paciencia y la oración, y señala a Abraham, David, la mujer con el flujo de sangre, Daniel y Pedro como creyentes que volaron alto por la fe, cerrando con el clamor: 'Señor, ayuda mi incredulidad'.",
            "keyPoints": [
              "La promesa de Isaías 40:31 tiene una condición: esperar en el Señor. La fe se edifica en el tiempo de espera, no en el día de la batalla (Proverbios 21:31).",
              "La fe no es una materia optativa; es una virtud indispensable, porque las tormentas de la vida son constantes.",
              "La fe es estar plenamente persuadido de la verdad de la Palabra de Dios, sin importar las circunstancias presentes, y poner la Palabra de Dios en acción a través de la obediencia.",
              "La fe no es un deseo ilusorio, simple optimismo ni pensamiento positivo; suele ser contraria a los hechos, mirando las cosas eternas que no se ven en lugar de los hechos temporales.",
              "La fe hace que nada sea imposible, traslada nuestra atención de los problemas a Dios, nos impulsa a actuar con valentía y provoca la intervención sobrenatural.",
              "Cómo activar el poder de la fe: estudia y escucha la Palabra de Dios continuamente, cree las promesas de Dios, habla palabras llenas de fe, actúa conforme a la Palabra, mantén la paciencia y vive una vida de oración.",
              "Ejemplos bíblicos de volar alto por la fe: Abraham creyó en esperanza contra esperanza, David venció a Goliat con una piedra, la mujer con el flujo de sangre se abrió paso, Daniel eligió el foso de los leones, Pedro salió de la barca.",
              "Obstáculos para volar alto por la fe: la incredulidad, la duda, el temor al fracaso, el pecado, las malas compañías, la falta de alimento espiritual y la impaciencia."
            ],
            "scriptures": [
              {
                "reference": "Marcos 9:23-24",
                "text": "Y Jesús le dijo: Si puedes creer, al que cree todo es posible. Y luego el padre del muchacho dijo clamando con lágrimas: Creo, Señor, ayuda mi incredulidad.",
                "note": "Texto ancla del mensaje; la oración final hizo eco del clamor del padre: 'Señor, ayuda mi incredulidad'.",
                "timestamp": "01:10:14"
              },
              {
                "reference": "Isaías 40:30-31",
                "text": "Los mancebos se fatigan y se cansan, los mozos flaquean y caen: mas los que esperan a Jehová tendrán nuevas fuerzas; levantarán las alas como águilas.",
                "note": "Texto de apertura; hasta la fuerza de la juventud falla, pero esperar en el Señor es la condición para remontar vuelo.",
                "timestamp": "01:09:08"
              },
              {
                "reference": "Juan 16:33",
                "text": "Estas cosas os he hablado, para que en mí tengáis paz. En el mundo tendréis aflicción: mas confiad, yo he vencido al mundo.",
                "note": "Las tormentas vendrán, pero en el momento en que corremos hacia Cristo, corremos hacia la paz.",
                "timestamp": "01:12:22"
              },
              {
                "reference": "Proverbios 21:31",
                "text": "El caballo se apareja para el día de la batalla: mas de Jehová es el salvar.",
                "note": "El caballo se prepara para el día de la batalla, no en ese día; la fe se desarrolla durante el tiempo de espera.",
                "timestamp": "01:16:38"
              },
              {
                "reference": "Marcos 11:23",
                "text": "Cualquiera que dijere a este monte: Quítate, y échate en la mar, y no dudare en su corazón, mas creyere que será hecho lo que dice, lo que dijere le será hecho.",
                "note": "La fe es trabajo arduo, no palabrería barata; las palabras llenas de fe echan los montes al mar.",
                "timestamp": "01:21:55"
              },
              {
                "reference": "2 Corintios 4:18",
                "text": "No mirando nosotros a las cosas que se ven, sino a las que no se ven: porque las cosas que se ven son temporales, mas las que no se ven son eternas.",
                "note": "Los hechos son temporales y pueden cambiar; la fe se aferra a lo eterno que no se ve.",
                "timestamp": "01:22:59"
              },
              {
                "reference": "Mateo 17:20",
                "text": "Si tuviereis fe como un grano de mostaza, diréis a este monte: Pásate de aquí allá: y se pasará: y nada os será imposible.",
                "note": "Con fe, nada es imposible: fíjate una meta en tu carrera, tu negocio o el avance del reino, y alcánzala.",
                "timestamp": "01:25:14"
              },
              {
                "reference": "Santiago 2:18",
                "text": "Pero alguno dirá: Tú tienes fe, y yo tengo obras: muéstrame tu fe sin tus obras, y yo te mostraré mi fe por mis obras.",
                "note": "La fe es poner la Palabra de Dios en acción; se remonta vuelo practicando lo enseñado, no solo asistiendo al summit.",
                "timestamp": "01:21:55"
              },
              {
                "reference": "Hebreos 11:8",
                "text": "Por la fe Abraham, siendo llamado, obedeció para salir al lugar que había de recibir por heredad; y salió sin saber dónde iba.",
                "note": "La fe impulsa a actuar con valentía; Abraham partió de inmediato sin saber a dónde iba.",
                "timestamp": "01:28:26"
              },
              {
                "reference": "Números 23:19",
                "text": "Dios no es hombre, para que mienta; ni hijo de hombre para que se arrepienta: él dijo, ¿y no hará?; habló, ¿y no lo ejecutará?",
                "note": "Cree las promesas de Dios; Dios no puede mentir porque no tiene reputación que defender ante nadie.",
                "timestamp": "01:32:44"
              }
            ],
            "quotes": [
              {
                "quote": "La fe no se edifica ni se desarrolla el día de la batalla. La fe se desarrolla durante el tiempo de espera.",
                "timestamp": "01:15:35"
              },
              {
                "quote": "Si tú y yo hemos de volar alto en la vida, la fe no es opcional. Es una virtud indispensable, porque las tormentas de la vida son constantes.",
                "timestamp": "01:17:41"
              },
              {
                "quote": "No importa lo que diga el médico. No importa lo que diga el experto en inmigración. La fe dice: Dios lo dijo, yo lo creo, y eso lo resuelve todo.",
                "timestamp": "01:20:51"
              },
              {
                "quote": "La fe no es un hecho. De hecho, si es un hecho, no es fe. La fe suele ser contraria a los hechos.",
                "timestamp": "01:24:04"
              },
              {
                "quote": "La fe no ignora los problemas, sino que los ve como oportunidades para que Dios despliegue su poder.",
                "timestamp": "01:42:25"
              }
            ]
          },
          "fr": {
            "title": "Engager la puissance de la foi pour s'élever comme l'aigle",
            "preacher": "Prédicateur invité",
            "summary": "S'appuyant sur l'enseignement du sommet au sujet des caractéristiques de l'aigle, cette première parole de la matinée montre comment engager la foi afin que ces caractéristiques se manifestent dans nos vies. À partir d'Ésaïe 40:30-31, le prédicateur souligne que la promesse de prendre le vol comme l'aigle a une condition : se confier en l'Éternel, et que la foi se développe dans la saison d'attente, jamais le jour de la bataille. La foi n'est pas un cours optionnel mais une vertu indispensable, car les tempêtes de la vie sont constantes. Il définit la foi comme le fait d'être pleinement persuadé de la vérité de la Parole de Dieu, quelles que soient les circonstances, et comme la mise en pratique de la Parole de Dieu, puisque la foi sans les œuvres est morte. La foi n'est ni un vœu pieux, ni un simple optimisme, ni de la pensée positive; elle est souvent contraire aux faits. Il explique pourquoi la foi est essentielle, comment l'engager par la Parole, la confession, l'action, la patience et la prière, et présente Abraham, David, la femme atteinte d'une perte de sang, Daniel et Pierre comme des croyants qui se sont élevés par la foi, avant de conclure avec ce cri : « Seigneur, viens au secours de mon incrédulité. »",
            "keyPoints": [
              "La promesse d'Ésaïe 40:31 a une condition : se confier en l'Éternel. La foi se bâtit pendant le temps d'attente, pas le jour de la bataille (Proverbes 21:31).",
              "La foi n'est pas un cours optionnel; c'est une vertu indispensable, car les tempêtes de la vie sont constantes.",
              "La foi, c'est être pleinement persuadé de la vérité de la Parole de Dieu, malgré les circonstances présentes, et mettre la Parole de Dieu en pratique par l'obéissance.",
              "La foi n'est ni un vœu pieux, ni un simple optimisme, ni de la pensée positive; elle est généralement contraire aux faits, regardant aux choses éternelles invisibles plutôt qu'aux faits temporels.",
              "La foi rend toutes choses possibles, détourne notre attention des défis vers Dieu, rend capable d'agir avec audace et provoque l'intervention surnaturelle.",
              "Comment engager la puissance de la foi : étudier et écouter continuellement la Parole de Dieu, croire aux promesses de Dieu, prononcer des paroles remplies de foi, agir selon la Parole, demeurer patient et vivre une vie de prière.",
              "Exemples bibliques d'envol par la foi : Abraham a espéré contre toute espérance, David a vaincu Goliath avec une pierre, la femme atteinte d'une perte de sang s'est frayé un chemin, Daniel a choisi la fosse aux lions, Pierre est sorti de la barque.",
              "Obstacles à l'envol par la foi : l'incrédulité, le doute, la peur de l'échec, le péché, les mauvaises fréquentations, le manque de nourriture spirituelle et l'impatience."
            ],
            "scriptures": [
              {
                "reference": "Marc 9:23-24",
                "text": "Jésus lui dit : Si tu peux!... Tout est possible à celui qui croit. Aussitôt le père de l'enfant s'écria : Je crois! viens au secours de mon incrédulité!",
                "note": "Texte d'ancrage du message; la prière de clôture reprenait le cri du père : « Seigneur, viens au secours de mon incrédulité. »",
                "timestamp": "01:10:14"
              },
              {
                "reference": "Ésaïe 40:30-31",
                "text": "Les adolescents se fatiguent et se lassent, et les jeunes hommes chancellent; mais ceux qui se confient en l'Éternel renouvellent leur force. Ils prennent le vol comme les aigles.",
                "note": "Texte d'ouverture; même la force de la jeunesse s'épuise, mais se confier en l'Éternel est la condition de l'envol.",
                "timestamp": "01:09:08"
              },
              {
                "reference": "Jean 16:33",
                "text": "Je vous ai dit ces choses, afin que vous ayez la paix en moi. Vous aurez des tribulations dans le monde; mais prenez courage, j'ai vaincu le monde.",
                "note": "Les tempêtes viendront, mais dès que nous courons vers Christ, nous courons vers la paix.",
                "timestamp": "01:12:22"
              },
              {
                "reference": "Proverbes 21:31",
                "text": "Le cheval est équipé pour le jour de la bataille, mais la délivrance appartient à l'Éternel.",
                "note": "Le cheval est équipé pour le jour de la bataille, pas le jour même; la foi se développe pendant le temps d'attente.",
                "timestamp": "01:16:38"
              },
              {
                "reference": "Marc 11:23",
                "text": "Si quelqu'un dit à cette montagne : Ôte-toi de là et jette-toi dans la mer, et s'il ne doute point en son cœur, mais croit que ce qu'il dit arrive, il le verra s'accomplir.",
                "note": "La foi est un dur travail, pas un discours facile; les paroles remplies de foi jettent les montagnes dans la mer.",
                "timestamp": "01:21:55"
              },
              {
                "reference": "2 Corinthiens 4:18",
                "text": "Parce que nous regardons, non point aux choses visibles, mais à celles qui sont invisibles; car les choses visibles sont passagères, et les invisibles sont éternelles.",
                "note": "Les faits sont temporels et peuvent changer; la foi s'attache à l'éternel invisible.",
                "timestamp": "01:22:59"
              },
              {
                "reference": "Matthieu 17:20",
                "text": "Si vous aviez de la foi comme un grain de sénevé, vous diriez à cette montagne : Transporte-toi d'ici là, et elle se transporterait; rien ne vous serait impossible.",
                "note": "Avec la foi, rien n'est impossible : fixez-vous un objectif de carrière, d'entreprise ou d'avancement du royaume, et atteignez-le.",
                "timestamp": "01:25:14"
              },
              {
                "reference": "Jacques 2:18",
                "text": "Mais quelqu'un dira : Toi, tu as la foi; et moi, j'ai les œuvres. Montre-moi ta foi sans les œuvres, et moi, je te montrerai la foi par mes œuvres.",
                "note": "La foi, c'est mettre la Parole de Dieu en pratique; l'envol vient en pratiquant ce qui a été enseigné, pas seulement en assistant au sommet.",
                "timestamp": "01:21:55"
              },
              {
                "reference": "Hébreux 11:8",
                "text": "C'est par la foi qu'Abraham, lors de sa vocation, obéit et partit pour un lieu qu'il devait recevoir en héritage, et qu'il partit sans savoir où il allait.",
                "note": "La foi rend capable d'agir avec audace; Abraham partit aussitôt sans savoir où il allait.",
                "timestamp": "01:28:26"
              },
              {
                "reference": "Nombres 23:19",
                "text": "Dieu n'est point un homme pour mentir, ni fils d'un homme pour se repentir. Ce qu'il a dit, ne le fera-t-il pas? Ce qu'il a déclaré, ne l'exécutera-t-il pas?",
                "note": "Croyez aux promesses de Dieu; Dieu ne peut pas mentir, car il n'a de réputation à défendre devant personne.",
                "timestamp": "01:32:44"
              }
            ],
            "quotes": [
              {
                "quote": "On ne bâtit pas et on ne développe pas sa foi le jour de la bataille. La foi se développe pendant le temps d'attente.",
                "timestamp": "01:15:35"
              },
              {
                "quote": "Si vous et moi devons nous élever haut dans la vie, la foi n'est pas une option. C'est une vertu indispensable, car les tempêtes de la vie sont constantes.",
                "timestamp": "01:17:41"
              },
              {
                "quote": "Peu importe ce que dit le médecin. Peu importe ce que dit l'expert en immigration. La foi déclare : Dieu l'a dit, je le crois, l'affaire est réglée.",
                "timestamp": "01:20:51"
              },
              {
                "quote": "La foi n'est pas un fait. En réalité, si c'est un fait, ce n'est pas la foi. La foi est généralement contraire aux faits.",
                "timestamp": "01:24:04"
              },
              {
                "quote": "La foi n'ignore pas les défis, mais elle les voit comme des occasions pour Dieu de déployer sa puissance.",
                "timestamp": "01:42:25"
              }
            ]
          }
        }
      },
      {
        "slug": "day-3-morning-obedience-and-stewardship-for-soaring",
        "day": 3,
        "session": "morning",
        "date": "2026-07-23",
        "videoId": "1yiqpDwJTH0",
        "startTimestamp": "02:13:35",
        "title": "Obedience and Stewardship for Soaring",
        "preacher": "Ministering",
        "summary": "The second word of the morning addresses the strong desire in this generation to be famous, and shows that God offers a clearer path to exploits than the world's: seek first the kingdom of God, and that path is powered by two factors, obedience to God's Word and faithfulness in stewardship. Reading Deuteronomy 28:1-12, the preacher shows that all the blessings hang on hearkening to God's voice; when obedience is in place, blessings come upon us and overtake us. Obedience is prompt, complete, and joyous response to divine instruction, and partial obedience, as King Saul learned, equals disobedience. Stewardship is using God-given time, talents, and treasures to advance His kingdom, and it qualifies us for divine promotion, for whoever is faithful in little will be trusted with much. Obedience connects us to divine direction and secrets, builds an enviable life like Isaac's, and guarantees breakthroughs, protection, promotion, capacity, and divine appointment. The message closed with an altar call to salvation, rededication, and grace for complete obedience.",
        "keyPoints": [
          "Everyone wants to be famous, but God offers a clear path to exploits: seek first the kingdom of God and His righteousness, and all these things shall be added (Matthew 6:33).",
          "God's path to greatness is powered by two factors: obedience to God's Word and faithfulness in stewardship.",
          "All the blessings of Deuteronomy 28 hang on obedience; when you hearken to God's voice, the blessings come upon you and overtake you.",
          "Obedience is responding to divine instruction promptly, completely, and joyfully; partial obedience is equal to disobedience, as King Saul discovered.",
          "To obey is better than sacrifice; nothing you offer is acceptable to God while you live in disobedience.",
          "Stewardship is using God-given time, talents, and treasures to advance His kingdom, and it qualifies you for divine promotion; God finds people to elevate through service.",
          "Obedience and stewardship give you divine instruction, access to divine secrets, higher authority, good success, and make you a trustworthy vessel.",
          "Benefits of a life of obedience and stewardship: endless breakthroughs, divine protection, divine promotion, increased capacity and resources, and divine appointment."
        ],
        "scriptures": [
          {
            "reference": "Deuteronomy 28:1-2",
            "text": "And it shall come to pass, if thou shalt hearken diligently unto the voice of the LORD thy God, to observe and to do all his commandments... all these blessings shall come on thee, and overtake thee, if thou shalt hearken unto the voice of the LORD thy God.",
            "note": "Anchor text, read congregationally through verse 12; every blessing hangs on obedience.",
            "timestamp": "02:18:54"
          },
          {
            "reference": "Matthew 6:32-33",
            "text": "For after all these things do the Gentiles seek... But seek ye first the kingdom of God, and his righteousness; and all these things shall be added unto you.",
            "note": "The world chases fame, but if you give God first place you cannot miss your place in the race of life.",
            "timestamp": "02:16:47"
          },
          {
            "reference": "1 Samuel 15:22",
            "text": "Behold, to obey is better than sacrifice, and to hearken than the fat of rams.",
            "note": "King Saul's partial obedience cost him the throne; give, but obey first.",
            "timestamp": "02:26:38"
          },
          {
            "reference": "Isaiah 1:19",
            "text": "If ye be willing and obedient, ye shall eat the good of the land.",
            "note": "There is good in every land, but willingness and obedience must be in place first.",
            "timestamp": "02:31:01"
          },
          {
            "reference": "Luke 16:10",
            "text": "He that is faithful in that which is least is faithful also in much: and he that is unjust in the least is unjust also in much.",
            "note": "God starts you small; faithfulness in little qualifies you for divine promotion.",
            "timestamp": "02:33:07"
          },
          {
            "reference": "John 14:21",
            "text": "He that hath my commandments, and keepeth them, he it is that loveth me: and he that loveth me shall be loved of my Father, and I will love him, and will manifest myself to him.",
            "note": "Obedience keeps you walking in divine instruction, which empowers you for exploits.",
            "timestamp": "02:37:23"
          },
          {
            "reference": "Joshua 1:8",
            "text": "This book of the law shall not depart out of thy mouth; but thou shalt meditate therein day and night... for then thou shalt make thy way prosperous, and then thou shalt have good success.",
            "note": "Doing all God instructs is the route to good success in everything you do.",
            "timestamp": "02:40:34"
          },
          {
            "reference": "Luke 19:17",
            "text": "And he said unto him, Well, thou good servant: because thou hast been faithful in a very little, have thou authority over ten cities.",
            "note": "Faithful stewardship grants access to higher authority; tested vessels get more committed to them.",
            "timestamp": "02:39:29"
          }
        ],
        "quotes": [
          {
            "quote": "Until you are tested, you cannot be trusted. And when you are tested and you pass the test, more can be committed into your hands.",
            "timestamp": "02:22:21"
          },
          {
            "quote": "God will always start you small, but He never leaves you small.",
            "timestamp": "02:23:25"
          },
          {
            "quote": "Partial obedience is equal to disobedience. Your obedience must be complete.",
            "timestamp": "02:26:38"
          },
          {
            "quote": "You are only safe to the degree to which you obey God. If you move outside of God's instruction, you are on your own.",
            "timestamp": "02:42:41"
          },
          {
            "quote": "No anointing is a substitute for obedience. No matter the prayers that have gone ahead, if you refuse to obey, you are on your own.",
            "timestamp": "02:45:50"
          }
        ],
        "translations": {
          "creole": {
            "title": "Obeyisans ak Jesyon Fidèl pou Vole Wo",
            "preacher": "Predikatè envite",
            "summary": "Dezyèm pawòl maten an pale sou gwo anvi jenerasyon sa a genyen pou vin selèb, epi li montre Bondye ofri yon chemen ki pi klè pou gwo reyalizasyon pase chemen lemonn nan: chèche wayòm Bondye a an premye. Chemen sa a mache sou de faktè: obeyisans a Pawòl Bondye a ak fidelite nan jesyon. Pandan li t ap li Detewonòm 28:1-12, predikatè a montre tout benediksyon yo kanpe sou koute vwa Bondye; lè obeyisans lan an plas, benediksyon yo vini sou nou epi yo kouri dèyè nou jouk yo jwenn nou. Obeyisans se reponn a enstriksyon Bondye san pran reta, nèt ale, ak kè kontan; obeyisans a mwatye, jan wa Sayil te aprann sa, egal dezobeyisans. Jesyon fidèl se sèvi ak tan, talan ak trezò Bondye ban nou pou fè wayòm li an avanse, epi li kalifye nou pou pwomosyon diven, paske moun ki fidèl nan ti bagay, y ap konfye l gwo bagay. Obeyisans konekte nou ak direksyon diven ak sekrè Bondye yo, li bati yon lavi moun anvi genyen tankou lavi Izarak, epi li garanti gwo pèse, pwoteksyon, pwomosyon, kapasite ak randevou diven. Mesaj la te fèmen ak yon apèl sou lotèl pou sali, rededikasyon, ak gras pou obeyisans total.",
            "keyPoints": [
              "Tout moun vle selèb, men Bondye ofri yon chemen klè pou gwo reyalizasyon: chèche wayòm Bondye a ak jistis li an premye, epi tout lòt bagay yo ap vin ajoute (Matye 6:33).",
              "Chemen Bondye a pou grandè mache sou de faktè: obeyisans a Pawòl Bondye a ak fidelite nan jesyon.",
              "Tout benediksyon Detewonòm 28 yo kanpe sou obeyisans; lè ou koute vwa Bondye, benediksyon yo vini sou ou epi yo kouri dèyè ou jouk yo jwenn ou.",
              "Obeyisans se reponn a enstriksyon Bondye san pran reta, nèt ale, ak kè kontan; obeyisans a mwatye egal dezobeyisans, jan wa Sayil te dekouvri sa.",
              "Obeyi pi bon pase sakrifis; anyen ou ofri pa akseptab devan Bondye pandan w ap viv nan dezobeyisans.",
              "Jesyon fidèl se sèvi ak tan, talan ak trezò Bondye ban nou pou fè wayòm li an avanse, epi li kalifye ou pou pwomosyon diven; se atravè sèvis Bondye jwenn moun pou li elve.",
              "Obeyisans ak jesyon fidèl ba ou enstriksyon diven, aksè a sekrè Bondye yo, otorite ki pi wo, bon siksè, epi yo fè ou tounen yon veso Bondye ka fè konfyans.",
              "Benefis yon lavi obeyisans ak jesyon fidèl: pèse san rete, pwoteksyon diven, pwomosyon diven, plis kapasite ak resous, ak randevou diven."
            ],
            "scriptures": [
              {
                "reference": "Detewonòm 28:1-2",
                "text": "Si ou koute vwa Seyè a, Bondye ou a, tout bon vre, pou ou swiv epi fè tout kòmandman li yo... tout benediksyon sa yo ap vini sou ou, y ap kouri dèyè ou jouk yo jwenn ou, si ou koute vwa Seyè a, Bondye ou a.",
                "note": "Tèks prensipal la, kongregasyon an li li ansanm jouk vèsè 12; chak benediksyon kanpe sou obeyisans.",
                "timestamp": "02:18:54"
              },
              {
                "reference": "Matye 6:32-33",
                "text": "Se tout bagay sa yo moun lòt nasyon yo ap chèche... Men nou menm, chèche wayòm Bondye a ak jistis li an premye, epi tout lòt bagay sa yo ap vin ajoute pou nou.",
                "note": "Lemonn ap kouri dèyè laglwa, men si ou bay Bondye premye plas la, ou pa ka rate plas ou nan kous lavi a.",
                "timestamp": "02:16:47"
              },
              {
                "reference": "1 Samyèl 15:22",
                "text": "Gade: obeyi pi bon pase sakrifis, epi koute pi bon pase grès belye mouton.",
                "note": "Obeyisans a mwatye wa Sayil la te koute li fotèy wa a; bay, men obeyi anvan.",
                "timestamp": "02:26:38"
              },
              {
                "reference": "Ezayi 1:19",
                "text": "Si nou vle epi nou obeyi, n a manje pi bon bagay peyi a bay.",
                "note": "Gen bon bagay nan chak peyi, men fòk volonte ak obeyisans lan an plas anvan.",
                "timestamp": "02:31:01"
              },
              {
                "reference": "Lik 16:10",
                "text": "Moun ki fidèl nan ti bagay ap fidèl tou nan gwo bagay; moun ki malonèt nan ti bagay ap malonèt tou nan gwo bagay.",
                "note": "Bondye kòmanse ou piti; fidelite nan ti bagay kalifye ou pou pwomosyon diven.",
                "timestamp": "02:33:07"
              },
              {
                "reference": "Jan 14:21",
                "text": "Moun ki gen kòmandman mwen yo epi ki kenbe yo, se li menm ki renmen mwen. Papa m ap renmen moun ki renmen mwen an; mwen menm tou m ap renmen li, epi m ap fè li wè ki moun mwen ye.",
                "note": "Obeyisans kenbe ou nan chemen enstriksyon diven, e se sa ki ba ou pouvwa pou gwo reyalizasyon.",
                "timestamp": "02:37:23"
              },
              {
                "reference": "Jozye 1:8",
                "text": "Liv lalwa sa a pa dwe janm kite bouch ou; medite li lajounen kou lannwit... Se konsa w ap fè chemen ou pwospere, se konsa w ap gen bon siksè.",
                "note": "Fè tout sa Bondye mande, se wout la pou bon siksè nan tout sa w ap fè.",
                "timestamp": "02:40:34"
              },
              {
                "reference": "Lik 19:17",
                "text": "Li di li: Byen, ou se yon bon sèvitè. Paske ou te fidèl nan yon ti bagay tou piti, m ap ba ou otorite sou dis vil.",
                "note": "Jesyon fidèl ouvri pòt otorite ki pi wo; veso ki pase tès la resevwa plis nan men Bondye.",
                "timestamp": "02:39:29"
              }
            ],
            "quotes": [
              {
                "quote": "Toutotan ou poko pase tès, yo pa ka fè ou konfyans. Epi lè ou pase tès la, yo ka konfye plis nan men ou.",
                "timestamp": "02:22:21"
              },
              {
                "quote": "Bondye ap toujou kòmanse ou piti, men li pa janm kite ou piti.",
                "timestamp": "02:23:25"
              },
              {
                "quote": "Obeyisans a mwatye egal dezobeyisans. Obeyisans ou dwe konplè.",
                "timestamp": "02:26:38"
              },
              {
                "quote": "Ou an sekirite sèlman nan mezi ou obeyi Bondye. Si ou soti deyò enstriksyon Bondye a, ou pou kont ou.",
                "timestamp": "02:42:41"
              },
              {
                "quote": "Pa gen onksyon ki ka ranplase obeyisans. Kèlkeswa lapriyè ki fèt pou ou deja, si ou refize obeyi, ou pou kont ou.",
                "timestamp": "02:45:50"
              }
            ]
          },
          "es": {
            "title": "Obediencia y mayordomía para remontar vuelo",
            "preacher": "Predicador invitado",
            "summary": "La segunda palabra de la mañana aborda el fuerte deseo de esta generación de ser famosa, y muestra que Dios ofrece un camino más claro hacia las proezas que el del mundo: buscar primero el reino de Dios, un camino impulsado por dos factores, la obediencia a la Palabra de Dios y la fidelidad en la mayordomía. Leyendo Deuteronomio 28:1-12, el predicador muestra que todas las bendiciones dependen de oír la voz de Dios; cuando la obediencia está en su lugar, las bendiciones vienen sobre nosotros y nos alcanzan. La obediencia es responder a la instrucción divina con prontitud, por completo y con gozo, y la obediencia parcial, como aprendió el rey Saúl, equivale a desobediencia. La mayordomía es usar el tiempo, los talentos y los tesoros que Dios nos ha dado para avanzar su reino, y nos califica para la promoción divina, porque el que es fiel en lo poco recibirá mucho. La obediencia nos conecta con la dirección y los secretos divinos, edifica una vida envidiable como la de Isaac, y garantiza avances, protección, promoción, capacidad y cita divina. El mensaje cerró con un llamado al altar a la salvación, la reconsagración y la gracia para una obediencia completa.",
            "keyPoints": [
              "Todos quieren ser famosos, pero Dios ofrece un camino claro hacia las proezas: buscad primero el reino de Dios y su justicia, y todas estas cosas serán añadidas (Mateo 6:33).",
              "El camino de Dios hacia la grandeza está impulsado por dos factores: la obediencia a la Palabra de Dios y la fidelidad en la mayordomía.",
              "Todas las bendiciones de Deuteronomio 28 dependen de la obediencia; cuando oyes la voz de Dios, las bendiciones vienen sobre ti y te alcanzan.",
              "La obediencia es responder a la instrucción divina con prontitud, por completo y con gozo; la obediencia parcial equivale a desobediencia, como lo descubrió el rey Saúl.",
              "El obedecer es mejor que los sacrificios; nada de lo que ofreces es aceptable a Dios mientras vives en desobediencia.",
              "La mayordomía es usar el tiempo, los talentos y los tesoros que Dios te ha dado para avanzar su reino, y te califica para la promoción divina; Dios busca personas a quienes elevar a través del servicio.",
              "La obediencia y la mayordomía te dan instrucción divina, acceso a los secretos divinos, mayor autoridad, buen éxito, y te convierten en un vaso digno de confianza.",
              "Beneficios de una vida de obediencia y mayordomía: avances sin fin, protección divina, promoción divina, mayor capacidad y recursos, y cita divina."
            ],
            "scriptures": [
              {
                "reference": "Deuteronomio 28:1-2",
                "text": "Y será que, si oyeres diligente la voz de Jehová tu Dios, para guardar, para poner por obra todos sus mandamientos... y vendrán sobre ti todas estas bendiciones, y te alcanzarán, cuando oyeres la voz de Jehová tu Dios.",
                "note": "Texto ancla, leído en congregación hasta el versículo 12; toda bendición depende de la obediencia.",
                "timestamp": "02:18:54"
              },
              {
                "reference": "Mateo 6:32-33",
                "text": "Porque los Gentiles buscan todas estas cosas... Mas buscad primeramente el reino de Dios y su justicia, y todas estas cosas os serán añadidas.",
                "note": "El mundo persigue la fama, pero si le das a Dios el primer lugar no puedes perder tu lugar en la carrera de la vida.",
                "timestamp": "02:16:47"
              },
              {
                "reference": "1 Samuel 15:22",
                "text": "Ciertamente el obedecer es mejor que los sacrificios; y el prestar atención que el sebo de los carneros.",
                "note": "La obediencia parcial del rey Saúl le costó el trono; da, pero primero obedece.",
                "timestamp": "02:26:38"
              },
              {
                "reference": "Isaías 1:19",
                "text": "Si quisiereis y oyereis, comeréis el bien de la tierra.",
                "note": "Hay bien en toda tierra, pero primero deben estar en su lugar la disposición y la obediencia.",
                "timestamp": "02:31:01"
              },
              {
                "reference": "Lucas 16:10",
                "text": "El que es fiel en lo muy poco, también en lo más es fiel: y el que en lo muy poco es injusto, también en lo más es injusto.",
                "note": "Dios te inicia en lo pequeño; la fidelidad en lo poco te califica para la promoción divina.",
                "timestamp": "02:33:07"
              },
              {
                "reference": "Juan 14:21",
                "text": "El que tiene mis mandamientos, y los guarda, aquél es el que me ama; y el que me ama, será amado de mi Padre, y yo le amaré, y me manifestaré a él.",
                "note": "La obediencia te mantiene caminando en instrucción divina, lo cual te capacita para las proezas.",
                "timestamp": "02:37:23"
              },
              {
                "reference": "Josué 1:8",
                "text": "El libro de aquesta ley nunca se apartará de tu boca: antes de día y de noche meditarás en él... porque entonces harás prosperar tu camino, y todo te saldrá bien.",
                "note": "Hacer todo lo que Dios instruye es la ruta al buen éxito en todo lo que emprendes.",
                "timestamp": "02:40:34"
              },
              {
                "reference": "Lucas 19:17",
                "text": "Y él le dice: Está bien, buen siervo; pues que en lo poco has sido fiel, tendrás potestad sobre diez ciudades.",
                "note": "La mayordomía fiel concede acceso a mayor autoridad; a los vasos probados se les confía más.",
                "timestamp": "02:39:29"
              }
            ],
            "quotes": [
              {
                "quote": "Hasta que no seas probado, no se te puede confiar. Y cuando eres probado y pasas la prueba, más puede ser puesto en tus manos.",
                "timestamp": "02:22:21"
              },
              {
                "quote": "Dios siempre te iniciará en lo pequeño, pero nunca te deja pequeño.",
                "timestamp": "02:23:25"
              },
              {
                "quote": "La obediencia parcial equivale a desobediencia. Tu obediencia debe ser completa.",
                "timestamp": "02:26:38"
              },
              {
                "quote": "Solo estás seguro en la medida en que obedeces a Dios. Si te sales de la instrucción de Dios, quedas por tu cuenta.",
                "timestamp": "02:42:41"
              },
              {
                "quote": "Ninguna unción sustituye la obediencia. No importa cuántas oraciones hayan ido delante, si te niegas a obedecer, quedas por tu cuenta.",
                "timestamp": "02:45:50"
              }
            ]
          },
          "fr": {
            "title": "L'obéissance et l'intendance pour s'élever",
            "preacher": "Prédicateur invité",
            "summary": "Cette seconde parole de la matinée aborde le fort désir de cette génération de devenir célèbre, et montre que Dieu offre un chemin vers les exploits plus clair que celui du monde : chercher premièrement le royaume de Dieu, un chemin propulsé par deux facteurs, l'obéissance à la Parole de Dieu et la fidélité dans l'intendance. En lisant Deutéronome 28:1-12, le prédicateur montre que toutes les bénédictions dépendent de l'obéissance à la voix de Dieu; quand l'obéissance est en place, les bénédictions viennent sur nous et nous atteignent. L'obéissance est une réponse prompte, complète et joyeuse à l'instruction divine, et l'obéissance partielle, comme le roi Saül l'a appris, équivaut à la désobéissance. L'intendance consiste à utiliser le temps, les talents et les trésors reçus de Dieu pour faire avancer son royaume, et elle nous qualifie pour la promotion divine, car celui qui est fidèle dans les petites choses se verra confier davantage. L'obéissance nous connecte à la direction et aux secrets divins, bâtit une vie enviable comme celle d'Isaac, et garantit percées, protection, promotion, capacité et rendez-vous divins. Le message s'est conclu par un appel au salut, à la reconsécration et à la grâce d'une obéissance complète.",
            "keyPoints": [
              "Tout le monde veut être célèbre, mais Dieu offre un chemin clair vers les exploits : cherchez premièrement le royaume de Dieu et sa justice, et toutes ces choses vous seront données par-dessus (Matthieu 6:33).",
              "Le chemin de Dieu vers la grandeur est propulsé par deux facteurs : l'obéissance à la Parole de Dieu et la fidélité dans l'intendance.",
              "Toutes les bénédictions de Deutéronome 28 dépendent de l'obéissance; quand vous obéissez à la voix de Dieu, les bénédictions viennent sur vous et vous atteignent.",
              "L'obéissance, c'est répondre à l'instruction divine promptement, complètement et joyeusement; l'obéissance partielle équivaut à la désobéissance, comme le roi Saül l'a découvert.",
              "L'obéissance vaut mieux que le sacrifice; rien de ce que vous offrez n'est agréable à Dieu tant que vous vivez dans la désobéissance.",
              "L'intendance consiste à utiliser le temps, les talents et les trésors reçus de Dieu pour faire avancer son royaume, et elle vous qualifie pour la promotion divine; Dieu trouve, à travers le service, des personnes à élever.",
              "L'obéissance et l'intendance vous donnent l'instruction divine, l'accès aux secrets divins, une autorité plus grande, un bon succès, et font de vous un vase digne de confiance.",
              "Bénéfices d'une vie d'obéissance et d'intendance : des percées sans fin, la protection divine, la promotion divine, des capacités et des ressources accrues, et des rendez-vous divins."
            ],
            "scriptures": [
              {
                "reference": "Deutéronome 28:1-2",
                "text": "Si tu obéis à la voix de l'Éternel, ton Dieu, en observant et en mettant en pratique tous ses commandements... voici toutes les bénédictions qui se répandront sur toi et qui seront ton partage, lorsque tu obéiras à la voix de l'Éternel, ton Dieu.",
                "note": "Texte d'ancrage, lu ensemble jusqu'au verset 12; chaque bénédiction dépend de l'obéissance.",
                "timestamp": "02:18:54"
              },
              {
                "reference": "Matthieu 6:32-33",
                "text": "Car toutes ces choses, ce sont les païens qui les recherchent... Cherchez premièrement le royaume et la justice de Dieu; et toutes ces choses vous seront données par-dessus.",
                "note": "Le monde court après la célébrité, mais si vous donnez à Dieu la première place, vous ne pouvez pas manquer votre place dans la course de la vie.",
                "timestamp": "02:16:47"
              },
              {
                "reference": "1 Samuel 15:22",
                "text": "Voici, l'obéissance vaut mieux que les sacrifices, et l'observation de sa parole vaut mieux que la graisse des béliers.",
                "note": "L'obéissance partielle du roi Saül lui a coûté le trône; donnez, mais obéissez d'abord.",
                "timestamp": "02:26:38"
              },
              {
                "reference": "Ésaïe 1:19",
                "text": "Si vous avez de la bonne volonté et si vous êtes dociles, vous mangerez les meilleures productions du pays.",
                "note": "Il y a du bon dans chaque pays, mais la bonne volonté et l'obéissance doivent d'abord être en place.",
                "timestamp": "02:31:01"
              },
              {
                "reference": "Luc 16:10",
                "text": "Celui qui est fidèle dans les moindres choses l'est aussi dans les grandes, et celui qui est injuste dans les moindres choses l'est aussi dans les grandes.",
                "note": "Dieu vous fait commencer petit; la fidélité dans les petites choses vous qualifie pour la promotion divine.",
                "timestamp": "02:33:07"
              },
              {
                "reference": "Jean 14:21",
                "text": "Celui qui a mes commandements et qui les garde, c'est celui qui m'aime; et celui qui m'aime sera aimé de mon Père, je l'aimerai, et je me ferai connaître à lui.",
                "note": "L'obéissance vous garde dans l'instruction divine, ce qui vous rend capable d'exploits.",
                "timestamp": "02:37:23"
              },
              {
                "reference": "Josué 1:8",
                "text": "Que ce livre de la loi ne s'éloigne point de ta bouche; médite-le jour et nuit... car c'est alors que tu auras du succès dans tes entreprises, c'est alors que tu réussiras.",
                "note": "Faire tout ce que Dieu ordonne est la voie du bon succès dans tout ce que vous entreprenez.",
                "timestamp": "02:40:34"
              },
              {
                "reference": "Luc 19:17",
                "text": "Il lui dit : C'est bien, bon serviteur; parce que tu as été fidèle en peu de chose, reçois le gouvernement de dix villes.",
                "note": "L'intendance fidèle donne accès à une autorité plus grande; on confie davantage aux vases éprouvés.",
                "timestamp": "02:39:29"
              }
            ],
            "quotes": [
              {
                "quote": "Tant que vous n'êtes pas éprouvé, on ne peut pas vous faire confiance. Et quand vous êtes éprouvé et que vous réussissez l'épreuve, davantage peut être remis entre vos mains.",
                "timestamp": "02:22:21"
              },
              {
                "quote": "Dieu vous fait toujours commencer petit, mais il ne vous laisse jamais petit.",
                "timestamp": "02:23:25"
              },
              {
                "quote": "L'obéissance partielle équivaut à la désobéissance. Votre obéissance doit être complète.",
                "timestamp": "02:26:38"
              },
              {
                "quote": "Vous n'êtes en sécurité que dans la mesure où vous obéissez à Dieu. Si vous sortez de l'instruction de Dieu, vous êtes livré à vous-même.",
                "timestamp": "02:42:41"
              },
              {
                "quote": "Aucune onction ne remplace l'obéissance. Peu importe les prières faites pour vous, si vous refusez d'obéir, vous êtes livré à vous-même.",
                "timestamp": "02:45:50"
              }
            ]
          }
        }
      },
      {
        "slug": "day-3-evening-purity-in-relationships",
        "day": 3,
        "session": "evening",
        "date": "2026-07-23",
        "videoId": "Eozkd7nW7Mk",
        "startTimestamp": "00:22:54",
        "title": "Purity in Relationships",
        "preacher": "Panel Session",
        "summary": "Day 3 evening took the form of a moderated panel on purity in relationships, whether dating, married, or simple friendship. The panelists laid a foundation bigger than the usual talk about sex: purity reflects who God is, showing up in our thoughts, words, motives, speech, and conduct. From Proverbs 4:23 they taught that guarding the heart means keeping God at its very center, and that the mind is a gateway, so we must go to social media with purpose instead of leaving the door open to endless scrolling. They insisted purity is never outdated because God's standard never changes, and that it protects what is valuable and brings freedom, not bondage. Practical questions from the youth followed: warning signs that a relationship is pulling you from God, accountability to yourself and to godly mentors, the difference between love and lust, restoration after moral failure through the Word, prayer, and the Holy Spirit, and whether high schoolers should date. The National Pastor closed by charging every young person, male and female alike, to stand alone like the eagle rather than follow the crowd.",
        "keyPoints": [
          "Purity is far more than sexual purity; it reflects God's character in our thoughts, words, motives, speech, and conduct.",
          "Guard your heart by placing God at the center of your decisions and emotions (Proverbs 4:23).",
          "The mind is a gateway: go to social media with a purpose, then step out, rather than leaving the door open to endless scrolling.",
          "Purity is never outdated because God's word and standard never change; purity protects what is valuable and brings freedom, not bondage.",
          "You are drifting when you substitute spiritual appointments for a relationship and start compromising standards you once held dearly.",
          "Accountability means being responsible to yourself, honest and communicative with your partner, and answerable to a godly mentor.",
          "There is no such thing as a white lie; every lie is a lie, and all sin is sin before God.",
          "Restoration after failure comes through the Word, prayer, and the Holy Spirit; purity can never be achieved in the power of the flesh."
        ],
        "scriptures": [
          {
            "reference": "Proverbs 4:23",
            "text": "Keep thy heart with all diligence; for out of it are the issues of life.",
            "note": "Anchor for guarding the heart: purity of heart means God is at the center of your decisions and emotions.",
            "timestamp": "00:30:26"
          },
          {
            "reference": "Philippians 4:8",
            "text": "Finally, brethren, whatsoever things are true, whatsoever things are honest, whatsoever things are just, whatsoever things are pure... think on these things.",
            "note": "Cited as the biblical standard for self-accountability and filtering what we accept into our minds.",
            "timestamp": "00:44:38"
          },
          {
            "reference": "Hebrews 13:8",
            "text": "Jesus Christ the same yesterday, and to day, and for ever.",
            "note": "Used to show that because God's character cannot change, His standard of purity never goes out of fashion.",
            "timestamp": "00:36:56"
          },
          {
            "reference": "James 4:7",
            "text": "Submit yourselves therefore to God. Resist the devil, and he will flee from you.",
            "note": "Purity is not bondage; submission to God is the pathway to freedom.",
            "timestamp": "00:38:00"
          },
          {
            "reference": "Hebrews 13:4",
            "text": "Marriage is honourable in all, and the bed undefiled: but whoremongers and adulterers God will judge.",
            "note": "Referenced on keeping the bed undefiled; defilement begins with small compromises, not only the final act.",
            "timestamp": "00:41:17"
          },
          {
            "reference": "John 8:32",
            "text": "And ye shall know the truth, and the truth shall make you free.",
            "note": "For anyone recovering from a fall: make the word of God your companion, because the truth sets free.",
            "timestamp": "01:02:46"
          },
          {
            "reference": "2 Corinthians 5:17",
            "text": "Therefore if any man be in Christ, he is a new creature: old things are passed away; behold, all things are become new.",
            "note": "Encouragement to a youth who had fallen and walked away from sin: old things have passed away, get up and build again.",
            "timestamp": "01:01:42"
          },
          {
            "reference": "John 8:3-11",
            "text": "They say unto him, Master, this woman was taken in adultery, in the very act... And Jesus said unto her, Neither do I condemn thee: go, and sin no more.",
            "note": "The National Pastor used the woman caught in adultery to teach that both genders suffer the consequences of immorality, even when only one is called out.",
            "timestamp": "01:14:29"
          }
        ],
        "quotes": [
          {
            "quote": "Purity reflects who God is. It is representing God in our thoughts, in our words, in our motives, in our relationships, and in our actions.",
            "timestamp": "00:28:16"
          },
          {
            "quote": "Culture can change, and it is changing daily. But God's word does not change, and God's standard does not change.",
            "timestamp": "00:36:56"
          },
          {
            "quote": "Lust asks, what can I get from this person? Love asks, how does this person complement what I have, and how do I serve this person?",
            "timestamp": "00:54:19"
          },
          {
            "quote": "Purity can never be achieved in the power of the flesh.",
            "timestamp": "01:03:48"
          },
          {
            "quote": "Your destiny is far greater than the deceit that the devil is placing before you today.",
            "timestamp": "01:09:06"
          }
        ],
        "translations": {
          "creole": {
            "title": "Pite nan Relasyon yo",
            "preacher": "Sesyon Panèl",
            "summary": "Aswè jou 3 a te pran fòm yon panèl ak yon moderatè sou pite nan relasyon, kit se frekantasyon, maryaj, oswa yon senp zanmitay. Panelis yo te poze yon fondasyon ki pi laj pase koze sèks la sèlman: pite reflete kiyès Bondye ye; li parèt nan panse nou, pawòl nou, motivasyon nou, jan nou pale ak jan nou mennen tèt nou. Apati Pwovèb 4:23, yo te anseye veye kè ou vle di kenbe Bondye nan mitan kè a nèt, epi lespri a se yon pòtay, kidonk nou dwe ale sou rezo sosyal yo ak yon objektif olye nou kite pòt la ouvè pou woule ekran san rete. Yo te ensiste pite pa janm demode paske estanda Bondye a pa janm chanje, epi li pwoteje sa ki gen valè e li pote libète, pa esklavaj. Apre sa, jèn yo te poze kesyon pratik: siy ki montre yon relasyon ap rale ou lwen Bondye, responsablite devan tèt ou ak devan bon mantò, diferans ant lanmou ak konvwatiz, restorasyon apre yon chit moral atravè Pawòl la, lapriyè ak Lespri Sen an, ak èske elèv lekòl segondè ta dwe frekante. Pastè Nasyonal la te fèmen ak yon egzòtasyon pou chak jèn, gason kou fi, kanpe pou kont yo tankou malfini olye yo swiv foul la.",
            "keyPoints": [
              "Pite se pi plis lontan pase pite seksyèl; li reflete karaktè Bondye nan panse nou, pawòl nou, motivasyon nou, jan nou pale ak jan nou mennen tèt nou.",
              "Veye kè ou: mete Bondye nan mitan desizyon ou yo ak emosyon ou yo (Pwovèb 4:23).",
              "Lespri a se yon pòtay: ale sou rezo sosyal yo ak yon objektif, epi soti, olye ou kite pòt la ouvè pou woule ekran san rete.",
              "Pite pa janm demode paske pawòl Bondye ak estanda li pa janm chanje; pite pwoteje sa ki gen valè epi li pote libète, pa esklavaj.",
              "Ou ap deraye lè ou ranplase randevou espirityèl ou yo ak yon relasyon, epi ou kòmanse lage estanda ou te konn kenbe fèm.",
              "Responsablite vle di reskonsab devan tèt ou, onèt e klè ak patnè ou, epi gen yon bon mantò ou reponn devan li.",
              "Pa gen bagay ki rele ti manti san danje; tout manti se manti, epi tout peche se peche devan Bondye.",
              "Restorasyon apre yon chit vini atravè Pawòl la, lapriyè ak Lespri Sen an; pite pa janm ka reyalize ak fòs lachè."
            ],
            "scriptures": [
              {
                "reference": "Pwovèb 4:23",
                "text": "Veye kè ou pase tout lòt bagay, paske se nan kè a tout sous lavi a soti.",
                "note": "Baz pou veye kè a: yon kè ki pwòp vle di Bondye nan mitan desizyon ou yo ak emosyon ou yo.",
                "timestamp": "00:30:26"
              },
              {
                "reference": "Filipyen 4:8",
                "text": "Pou fini, frè m yo, tout sa ki vre, tout sa ki onèt, tout sa ki jis, tout sa ki pwòp... se sou bagay sa yo pou nou kenbe panse nou.",
                "note": "Yo site li kòm estanda biblik pou responsablite devan tèt ou ak pou filtre sa nou kite antre nan lespri nou.",
                "timestamp": "00:44:38"
              },
              {
                "reference": "Ebre 13:8",
                "text": "Jezikri se menm lan ayè, jodi a, e pou tout tan.",
                "note": "Yo sèvi avè li pou montre paske karaktè Bondye pa ka chanje, estanda pite li a pa janm demode.",
                "timestamp": "00:36:56"
              },
              {
                "reference": "Jak 4:7",
                "text": "Se poutèt sa, soumèt nou devan Bondye. Kenbe tèt ak dyab la, epi l ap kouri kite nou.",
                "note": "Pite se pa esklavaj; soumisyon devan Bondye se chemen libète a.",
                "timestamp": "00:38:00"
              },
              {
                "reference": "Ebre 13:4",
                "text": "Maryaj merite respè nan tout bagay, epi kabann maryaj la dwe rete san tach; men Bondye ap jije moun k ap viv nan imoralite ak moun k ap fè adiltè.",
                "note": "Yo site li sou kenbe kabann nan san tach; salte a kòmanse ak ti konpwomi, pa sèlman ak dènye zak la.",
                "timestamp": "00:41:17"
              },
              {
                "reference": "Jan 8:32",
                "text": "N a konnen verite a, epi verite a va ban nou libète.",
                "note": "Pou nenpòt moun k ap refè apre yon chit: fè pawòl Bondye a konpayon ou, paske verite a bay libète.",
                "timestamp": "01:02:46"
              },
              {
                "reference": "2 Korentyen 5:17",
                "text": "Se sa k fè, si yon moun nan Kris, li se yon kreyati tounèf: ansyen bagay yo pase; gade, tout bagay vin nèf.",
                "note": "Ankourajman pou yon jèn ki te tonbe epi ki te vire do bay peche: ansyen bagay yo pase, leve epi rekòmanse bati.",
                "timestamp": "01:01:42"
              },
              {
                "reference": "Jan 8:3-11",
                "text": "Yo di li: Mèt, yo kenbe fanm sa a nan adiltè, nan zak la menm... Epi Jezi di li: Mwen menm tou, mwen pa kondane ou; ale, epi pa fè peche ankò.",
                "note": "Pastè Nasyonal la te sèvi ak istwa fanm yo te kenbe nan adiltè a pou anseye toude sèks yo sibi konsekans imoralite, menm lè se yon sèl yo denonse.",
                "timestamp": "01:14:29"
              }
            ],
            "quotes": [
              {
                "quote": "Pite reflete kiyès Bondye ye. Se reprezante Bondye nan panse nou, nan pawòl nou, nan motivasyon nou, nan relasyon nou yo ak nan aksyon nou yo.",
                "timestamp": "00:28:16"
              },
              {
                "quote": "Kilti ka chanje, e l ap chanje chak jou. Men pawòl Bondye pa chanje, epi estanda Bondye pa chanje.",
                "timestamp": "00:36:56"
              },
              {
                "quote": "Konvwatiz mande: kisa m ka jwenn nan men moun sa a? Lanmou mande: kijan moun sa a konplete sa m genyen, e kijan m ka sèvi moun sa a?",
                "timestamp": "00:54:19"
              },
              {
                "quote": "Pite pa janm ka reyalize ak fòs lachè.",
                "timestamp": "01:03:48"
              },
              {
                "quote": "Destine ou pi gran lontan pase pyèj dyab la ap mete devan ou jodi a.",
                "timestamp": "01:09:06"
              }
            ]
          },
          "es": {
            "title": "Pureza en las relaciones",
            "preacher": "Sesión de panel",
            "summary": "La noche del día 3 tomó la forma de un panel moderado sobre la pureza en las relaciones, ya sea en el noviazgo, el matrimonio o la simple amistad. Los panelistas pusieron un fundamento más grande que la típica charla sobre el sexo: la pureza refleja quién es Dios, y se manifiesta en nuestros pensamientos, palabras, motivaciones, manera de hablar y conducta. Desde Proverbios 4:23 enseñaron que guardar el corazón significa mantener a Dios en su mismo centro, y que la mente es una puerta de entrada, por lo que debemos entrar a las redes sociales con propósito en lugar de dejar la puerta abierta al scroll interminable. Insistieron en que la pureza nunca pasa de moda porque el estándar de Dios nunca cambia, y que protege lo valioso y trae libertad, no esclavitud. Siguieron preguntas prácticas de los jóvenes: las señales de alerta de que una relación te está alejando de Dios, la rendición de cuentas ante ti mismo y ante mentores piadosos, la diferencia entre el amor y la lujuria, la restauración después de una caída moral a través de la Palabra, la oración y el Espíritu Santo, y si los estudiantes de secundaria deberían tener novio o novia. El Pastor Nacional cerró exhortando a cada joven, hombre o mujer por igual, a mantenerse firme en solitario como el águila en lugar de seguir a la multitud.",
            "keyPoints": [
              "La pureza es mucho más que pureza sexual; refleja el carácter de Dios en nuestros pensamientos, palabras, motivaciones, manera de hablar y conducta.",
              "Guarda tu corazón poniendo a Dios en el centro de tus decisiones y emociones (Proverbios 4:23).",
              "La mente es una puerta de entrada: entra a las redes sociales con un propósito y luego sal, en lugar de dejar la puerta abierta al scroll interminable.",
              "La pureza nunca pasa de moda porque la palabra y el estándar de Dios nunca cambian; la pureza protege lo valioso y trae libertad, no esclavitud.",
              "Estás a la deriva cuando sustituyes tus citas espirituales por una relación y empiezas a comprometer estándares que antes atesorabas.",
              "La rendición de cuentas significa ser responsable ante ti mismo, honesto y comunicativo con tu pareja, y dar cuentas a un mentor piadoso.",
              "No existe la mentira piadosa; toda mentira es mentira, y todo pecado es pecado delante de Dios.",
              "La restauración después de una caída viene por la Palabra, la oración y el Espíritu Santo; la pureza jamás puede lograrse con la fuerza de la carne."
            ],
            "scriptures": [
              {
                "reference": "Proverbios 4:23",
                "text": "Sobre toda cosa guardada guarda tu corazón; porque de él mana la vida.",
                "note": "Ancla para guardar el corazón: la pureza de corazón significa que Dios está en el centro de tus decisiones y emociones.",
                "timestamp": "00:30:26"
              },
              {
                "reference": "Filipenses 4:8",
                "text": "Por lo demás, hermanos, todo lo que es verdadero, todo lo honesto, todo lo justo, todo lo puro... en esto pensad.",
                "note": "Citado como el estándar bíblico para la rendición de cuentas ante uno mismo y para filtrar lo que dejamos entrar en la mente.",
                "timestamp": "00:44:38"
              },
              {
                "reference": "Hebreos 13:8",
                "text": "Jesucristo es el mismo ayer, y hoy, y por los siglos.",
                "note": "Usado para mostrar que, como el carácter de Dios no puede cambiar, su estándar de pureza nunca pasa de moda.",
                "timestamp": "00:36:56"
              },
              {
                "reference": "Santiago 4:7",
                "text": "Someteos pues a Dios; resistid al diablo, y de vosotros huirá.",
                "note": "La pureza no es esclavitud; la sumisión a Dios es el camino a la libertad.",
                "timestamp": "00:38:00"
              },
              {
                "reference": "Hebreos 13:4",
                "text": "Honroso es en todos el matrimonio, y el lecho sin mancilla; mas a los fornicarios y a los adúlteros juzgará Dios.",
                "note": "Citado sobre mantener el lecho sin mancilla; la contaminación comienza con pequeñas concesiones, no solo con el acto final.",
                "timestamp": "00:41:17"
              },
              {
                "reference": "Juan 8:32",
                "text": "Y conoceréis la verdad, y la verdad os libertará.",
                "note": "Para quien se recupera de una caída: haz de la palabra de Dios tu compañera, porque la verdad hace libre.",
                "timestamp": "01:02:46"
              },
              {
                "reference": "2 Corintios 5:17",
                "text": "De modo que si alguno está en Cristo, nueva criatura es: las cosas viejas pasaron; he aquí todas son hechas nuevas.",
                "note": "Palabra de aliento para un joven que había caído y se apartó del pecado: las cosas viejas pasaron, levántate y vuelve a edificar.",
                "timestamp": "01:01:42"
              },
              {
                "reference": "Juan 8:3-11",
                "text": "Dícenle: Maestro, esta mujer ha sido tomada en el mismo hecho, adulterando... Y Jesús le dijo: Ni yo te condeno: vete, y no peques más.",
                "note": "El Pastor Nacional usó a la mujer sorprendida en adulterio para enseñar que ambos géneros sufren las consecuencias de la inmoralidad, aun cuando solo uno sea señalado.",
                "timestamp": "01:14:29"
              }
            ],
            "quotes": [
              {
                "quote": "La pureza refleja quién es Dios. Es representar a Dios en nuestros pensamientos, en nuestras palabras, en nuestras motivaciones, en nuestras relaciones y en nuestras acciones.",
                "timestamp": "00:28:16"
              },
              {
                "quote": "La cultura puede cambiar, y está cambiando a diario. Pero la palabra de Dios no cambia, y el estándar de Dios no cambia.",
                "timestamp": "00:36:56"
              },
              {
                "quote": "La lujuria pregunta: ¿qué puedo obtener de esta persona? El amor pregunta: ¿cómo complementa esta persona lo que tengo, y cómo puedo servirle?",
                "timestamp": "00:54:19"
              },
              {
                "quote": "La pureza jamás puede lograrse con la fuerza de la carne.",
                "timestamp": "01:03:48"
              },
              {
                "quote": "Tu destino es mucho más grande que el engaño que el diablo pone hoy delante de ti.",
                "timestamp": "01:09:06"
              }
            ]
          },
          "fr": {
            "title": "La pureté dans les relations",
            "preacher": "Session de panel",
            "summary": "La soirée du jour 3 a pris la forme d'un panel animé sur la pureté dans les relations, qu'il s'agisse de fréquentations, de mariage ou de simple amitié. Les panélistes ont posé un fondement plus large que le discours habituel sur la sexualité : la pureté reflète qui Dieu est, et se manifeste dans nos pensées, nos paroles, nos motivations, notre langage et notre conduite. À partir de Proverbes 4:23, ils ont enseigné que garder son cœur, c'est garder Dieu en son centre, et que l'esprit est une porte d'entrée : il faut aller sur les réseaux sociaux avec un but précis plutôt que de laisser la porte ouverte au défilement sans fin. Ils ont insisté sur le fait que la pureté n'est jamais démodée parce que le standard de Dieu ne change jamais, et qu'elle protège ce qui est précieux et apporte la liberté, non l'esclavage. Des questions pratiques des jeunes ont suivi : les signes qu'une relation vous éloigne de Dieu, la responsabilité envers soi-même et envers des mentors pieux, la différence entre l'amour et la convoitise, la restauration après une chute morale par la Parole, la prière et le Saint-Esprit, et la question de savoir si les élèves du secondaire devraient sortir ensemble. Le pasteur national a conclu en exhortant chaque jeune, garçon comme fille, à se tenir seul comme l'aigle plutôt que de suivre la foule.",
            "keyPoints": [
              "La pureté est bien plus que la pureté sexuelle; elle reflète le caractère de Dieu dans nos pensées, nos paroles, nos motivations, notre langage et notre conduite.",
              "Gardez votre cœur en plaçant Dieu au centre de vos décisions et de vos émotions (Proverbes 4:23).",
              "L'esprit est une porte d'entrée : allez sur les réseaux sociaux avec un but, puis ressortez, plutôt que de laisser la porte ouverte au défilement sans fin.",
              "La pureté n'est jamais démodée, car la parole et le standard de Dieu ne changent jamais; la pureté protège ce qui est précieux et apporte la liberté, non l'esclavage.",
              "Vous dérivez quand vous remplacez vos rendez-vous spirituels par une relation et que vous commencez à compromettre des standards qui vous étaient chers.",
              "Être redevable, c'est être responsable envers soi-même, honnête et communicatif avec son partenaire, et redevable devant un mentor pieux.",
              "Le pieux mensonge n'existe pas; tout mensonge est un mensonge, et tout péché est péché devant Dieu.",
              "La restauration après une chute vient par la Parole, la prière et le Saint-Esprit; la pureté ne peut jamais s'obtenir par la force de la chair."
            ],
            "scriptures": [
              {
                "reference": "Proverbes 4:23",
                "text": "Garde ton cœur plus que toute autre chose, car de lui viennent les sources de la vie.",
                "note": "Texte d'ancrage sur la garde du cœur : la pureté du cœur signifie que Dieu est au centre de vos décisions et de vos émotions.",
                "timestamp": "00:30:26"
              },
              {
                "reference": "Philippiens 4:8",
                "text": "Au reste, frères, que tout ce qui est vrai, tout ce qui est honorable, tout ce qui est juste, tout ce qui est pur... soit l'objet de vos pensées.",
                "note": "Cité comme le standard biblique de la responsabilité envers soi-même et du tri de ce que nous laissons entrer dans notre esprit.",
                "timestamp": "00:44:38"
              },
              {
                "reference": "Hébreux 13:8",
                "text": "Jésus-Christ est le même hier, aujourd'hui, et éternellement.",
                "note": "Utilisé pour montrer que, le caractère de Dieu ne pouvant changer, son standard de pureté ne se démode jamais.",
                "timestamp": "00:36:56"
              },
              {
                "reference": "Jacques 4:7",
                "text": "Soumettez-vous donc à Dieu; résistez au diable, et il fuira loin de vous.",
                "note": "La pureté n'est pas un esclavage; la soumission à Dieu est le chemin de la liberté.",
                "timestamp": "00:38:00"
              },
              {
                "reference": "Hébreux 13:4",
                "text": "Que le mariage soit honoré de tous, et le lit conjugal exempt de souillure, car Dieu jugera les impudiques et les adultères.",
                "note": "Cité au sujet du lit conjugal sans souillure; la souillure commence par de petits compromis, pas seulement par l'acte final.",
                "timestamp": "00:41:17"
              },
              {
                "reference": "Jean 8:32",
                "text": "Vous connaîtrez la vérité, et la vérité vous affranchira.",
                "note": "Pour quiconque se relève d'une chute : faites de la parole de Dieu votre compagne, car la vérité affranchit.",
                "timestamp": "01:02:46"
              },
              {
                "reference": "2 Corinthiens 5:17",
                "text": "Si quelqu'un est en Christ, il est une nouvelle créature. Les choses anciennes sont passées; voici, toutes choses sont devenues nouvelles.",
                "note": "Encouragement à un jeune tombé puis détourné du péché : les choses anciennes sont passées, relève-toi et rebâtis.",
                "timestamp": "01:01:42"
              },
              {
                "reference": "Jean 8:3-11",
                "text": "Ils dirent à Jésus : Maître, cette femme a été surprise en flagrant délit d'adultère... Et Jésus lui dit : Je ne te condamne pas non plus; va, et ne pèche plus.",
                "note": "Le pasteur national a utilisé la femme surprise en adultère pour enseigner que les deux sexes subissent les conséquences de l'immoralité, même quand un seul est pointé du doigt.",
                "timestamp": "01:14:29"
              }
            ],
            "quotes": [
              {
                "quote": "La pureté reflète qui Dieu est. C'est représenter Dieu dans nos pensées, nos paroles, nos motivations, nos relations et nos actions.",
                "timestamp": "00:28:16"
              },
              {
                "quote": "La culture peut changer, et elle change chaque jour. Mais la parole de Dieu ne change pas, et le standard de Dieu ne change pas.",
                "timestamp": "00:36:56"
              },
              {
                "quote": "La convoitise demande : que puis-je obtenir de cette personne? L'amour demande : en quoi cette personne complète-t-elle ce que j'ai, et comment puis-je la servir?",
                "timestamp": "00:54:19"
              },
              {
                "quote": "La pureté ne peut jamais s'obtenir par la force de la chair.",
                "timestamp": "01:03:48"
              },
              {
                "quote": "Votre destinée est bien plus grande que la tromperie que le diable place devant vous aujourd'hui.",
                "timestamp": "01:09:06"
              }
            ]
          }
        }
      }
    ]
  },
  {
    "day": 4,
    "label": "Day 4",
    "dateLabel": "Friday, July 24",
    "date": "2026-07-24",
    "messages": [
      {
        "slug": "day-4-morning-passion-for-god-for-soaring",
        "day": 4,
        "session": "morning",
        "date": "2026-07-24",
        "videoId": "GYtUz98nmzI",
        "startTimestamp": "01:37:12",
        "title": "Passion for God for Soaring",
        "preacher": "Ministering",
        "summary": "Isaiah 40 does not promise young people an easy run. It says plainly that even the youth shall faint, so the question is not whether you will be drained but what you will do when you are. The minister answered it out of his own story: a first generation believer prayed into Christ by an elderly mother, disinherited by his family, sleeping on someone's floor, yet something kept him going. That something is passion, an intense desire that drives you toward a purpose, an inner fire that refuses to settle for mediocrity. Champions are not the people who never fall, he said, but the people who refuse to stay on the ground when they do. Passion on its own is not enough, though. It has to be directed, and passion for God has to sit above every other passion, because a vision that does not align with divine purpose will not carry divine energy. He proved love for God by love for what God loves most, souls, the way Jesus pressed Peter three times to feed the sheep. Then he named four arenas to burn in, passion for God, for souls, for fellowship, and to please God as a lifestyle, before giving four ways to keep the fire alive: build the altar of prayer the way Elijah did, search the scriptures for fresh revelation, stay in the assembly, and be a blessing to somebody.",
        "keyPoints": [
          "Life will deplete you spiritually, mentally and physically; Isaiah 40:30 says even the youth shall faint, so refuelling is not optional.",
          "Passion is an intense desire that drives a person toward a purpose, an inner fire that refuses to settle for mediocrity.",
          "Champions are not those who never fall; they are the ones who refuse to settle on the ground when they do.",
          "Passion alone is not enough. It must be directed, and passion for God must sit above every other passion for your vision to be carried by divine energy.",
          "Your love for God is proved by love for what He loves most, which is souls; that is what Jesus was pressing when He told Peter to feed His sheep.",
          "Four arenas to burn in: passion for God, passion for souls, passion for fellowship, and passion to please God as a lifestyle.",
          "Fellowship is the refuelling station; anyone who is not fellowshipping is ignorantly blowing out their own candle.",
          "Keep the fire alive by building the altar of prayer as Elijah did, searching the scriptures for fresh revelation, staying in the assembly, and being a blessing to others."
        ],
        "scriptures": [
          {
            "reference": "Isaiah 40:30-31",
            "text": "Even the youths shall faint and be weary, and the young men shall utterly fall: but they that wait upon the LORD shall renew their strength; they shall mount up with wings as eagles.",
            "note": "Anchor text; life did not promise you a run without depletion, but there is something to do to be refuelled.",
            "timestamp": "01:38:15"
          },
          {
            "reference": "Matthew 5:14-16",
            "text": "Ye are the light of the world. A city that is set on an hill cannot be hid... Let your light so shine before men, that they may see your good works.",
            "note": "The lamp shines only as long as it has fuel in it; a candle in the wind has to be protected to keep glowing.",
            "timestamp": "01:42:42"
          },
          {
            "reference": "Lamentations 3:27",
            "text": "It is good for a man that he bear the yoke in his youth.",
            "note": "The yoke stands for responsibility, discipline, training and submission; do not waste the season, embrace it all.",
            "timestamp": "01:47:12"
          },
          {
            "reference": "Romans 8:28",
            "text": "And we know that all things work together for good to them that love God, to them who are the called according to his purpose.",
            "note": "Passion for God is what turns all things to work together for your good; your love for God must be at the apex of your purpose.",
            "timestamp": "01:51:52"
          },
          {
            "reference": "John 21:15-17",
            "text": "Simon, son of Jonas, lovest thou me more than these?... Feed my sheep.",
            "note": "Jesus placed a demand on Peter's claim of love; if you put anything above what He loves most, you do not love Him as much as you claim.",
            "timestamp": "01:52:55"
          },
          {
            "reference": "Jeremiah 20:9",
            "text": "Then I said, I will not make mention of him, nor speak any more in his name. But his word was in mine heart as a burning fire shut up in my bones.",
            "note": "Jeremiah had been beaten and put in stocks and wanted to quit, but the fire inside would not let him stay down.",
            "timestamp": "01:58:20"
          },
          {
            "reference": "Romans 5:5",
            "text": "And hope maketh not ashamed; because the love of God is shed abroad in our hearts by the Holy Ghost which is given unto us.",
            "note": "Passion is a product of the love of God poured out by the Spirit; willpower alone is not enough to keep you moving.",
            "timestamp": "01:58:20"
          },
          {
            "reference": "Psalm 84:1-7",
            "text": "How amiable are thy tabernacles, O LORD of hosts! My soul longeth, yea, even fainteth for the courts of the LORD... They go from strength to strength.",
            "note": "Zion is the refuelling station; every new day brings its own evil, so yesterday's strength will not carry today.",
            "timestamp": "02:05:57"
          },
          {
            "reference": "1 Kings 18:30-38",
            "text": "And he put the wood in order... and he made a trench about the altar... Then the fire of the LORD fell.",
            "note": "Do not look only at the fire that fell; look at the preparation that preceded it, and prepare your own heart for the altar.",
            "timestamp": "02:09:12"
          }
        ],
        "quotes": [
          {
            "quote": "Passion is an intense desire that drives a person towards a purpose. It is an inner fire that refuses to settle down for mediocrity.",
            "timestamp": "01:49:34"
          },
          {
            "quote": "Champions are not those who don't fall to the ground. Champions are those who, when they fall, refuse to settle on the ground.",
            "timestamp": "01:50:43"
          },
          {
            "quote": "If you give up a man with a vision for a man with a television, tomorrow you will watch the man with a vision on your husband's television.",
            "timestamp": "01:46:04"
          },
          {
            "quote": "Anyone who is not fellowshipping is ignorantly blowing away their own candle.",
            "timestamp": "02:07:03"
          },
          {
            "quote": "Don't just look at the fire that came down. What preceded the fire?",
            "timestamp": "02:10:19"
          }
        ],
        "translations": {
          "fr": {
            "title": "La passion pour Dieu, carburant du vol",
            "preacher": "Prédicateur invité",
            "summary": "Ésaïe 40 ne promet pas aux jeunes une course facile. Il dit clairement que même les adolescents se fatiguent, donc la question n'est pas de savoir si tu seras épuisé mais ce que tu feras quand tu le seras. Le prédicateur a répondu par sa propre histoire: croyant de première génération, amené à Christ par les prières d'une mère âgée, déshérité par sa famille, dormant par terre chez quelqu'un, et pourtant quelque chose le tenait debout. Ce quelque chose, c'est la passion, un désir intense qui pousse vers un but, un feu intérieur qui refuse de se contenter de la médiocrité. Les champions, a-t-il dit, ne sont pas ceux qui ne tombent jamais, mais ceux qui refusent de rester à terre quand ils tombent. La passion seule ne suffit pourtant pas. Elle doit être dirigée, et la passion pour Dieu doit passer avant toutes les autres, car une vision qui ne s'aligne pas sur le dessein divin ne sera pas portée par l'énergie divine. Il a démontré que l'amour pour Dieu se prouve par l'amour de ce que Dieu aime le plus, les âmes, comme Jésus a pressé Pierre trois fois de paître ses brebis. Puis il a nommé quatre domaines où brûler: la passion pour Dieu, pour les âmes, pour la communion fraternelle et pour plaire à Dieu comme mode de vie, avant de donner quatre moyens d'entretenir le feu: bâtir l'autel de la prière comme Élie, sonder les Écritures pour une révélation fraîche, rester dans l'assemblée, et être une bénédiction pour quelqu'un.",
            "keyPoints": [
              "La vie t'épuisera spirituellement, mentalement et physiquement; Ésaïe 40:30 dit que même les jeunes se fatiguent, donc se ravitailler n'est pas facultatif.",
              "La passion est un désir intense qui pousse une personne vers un but, un feu intérieur qui refuse la médiocrité.",
              "Les champions ne sont pas ceux qui ne tombent jamais; ce sont ceux qui refusent de rester à terre quand ils tombent.",
              "La passion seule ne suffit pas. Elle doit être dirigée, et la passion pour Dieu doit primer pour que ta vision soit portée par l'énergie divine.",
              "Ton amour pour Dieu se prouve par l'amour de ce qu'il aime le plus, les âmes; c'est ce que Jésus pressait quand il a dit à Pierre de paître ses brebis.",
              "Quatre domaines où brûler: la passion pour Dieu, pour les âmes, pour la communion fraternelle, et pour plaire à Dieu comme mode de vie.",
              "La communion fraternelle est la station de ravitaillement; celui qui ne se rassemble plus souffle lui-même sur sa propre bougie.",
              "Entretiens le feu en bâtissant l'autel de la prière comme Élie, en sondant les Écritures pour une révélation fraîche, en restant dans l'assemblée et en étant une bénédiction pour les autres."
            ],
            "scriptures": [
              {
                "reference": "Ésaïe 40:30-31",
                "text": "Les adolescents se fatiguent et se lassent, et les jeunes hommes chancellent; mais ceux qui se confient en l'Éternel renouvellent leur force. Ils prennent le vol comme les aigles.",
                "note": "Texte d'appui; la vie ne t'a pas promis une course sans épuisement, mais il y a quelque chose à faire pour être ravitaillé.",
                "timestamp": "01:38:15"
              },
              {
                "reference": "Matthieu 5:14-16",
                "text": "Vous êtes la lumière du monde. Une ville située sur une montagne ne peut être cachée... Que votre lumière luise ainsi devant les hommes, afin qu'ils voient vos bonnes oeuvres.",
                "note": "La lampe ne brille que tant qu'il y a du combustible dedans; une bougie dans le vent doit être protégée pour continuer de luire.",
                "timestamp": "01:42:42"
              },
              {
                "reference": "Lamentations 3:27",
                "text": "Il est bon pour l'homme de porter le joug dans sa jeunesse.",
                "note": "Le joug représente la responsabilité, la discipline, la formation et la soumission; ne gâche pas cette saison, embrasse-la.",
                "timestamp": "01:47:12"
              },
              {
                "reference": "Romains 8:28",
                "text": "Nous savons, du reste, que toutes choses concourent au bien de ceux qui aiment Dieu, de ceux qui sont appelés selon son dessein.",
                "note": "La passion pour Dieu est ce qui fait concourir toutes choses à ton bien; ton amour pour lui doit être au sommet de ton but dans la vie.",
                "timestamp": "01:51:52"
              },
              {
                "reference": "Jean 21:15-17",
                "text": "Simon, fils de Jonas, m'aimes-tu plus que ne m'aiment ceux-ci?... Pais mes brebis.",
                "note": "Jésus a mis à l'épreuve la déclaration d'amour de Pierre; si tu places autre chose au-dessus de ce qu'il aime le plus, tu ne l'aimes pas autant que tu le prétends.",
                "timestamp": "01:52:55"
              },
              {
                "reference": "Jérémie 20:9",
                "text": "Si je dis: Je ne ferai plus mention de lui, je ne parlerai plus en son nom, il y a dans mon coeur comme un feu dévorant qui est renfermé dans mes os.",
                "note": "Jérémie avait été battu et mis aux ceps et voulait tout arrêter, mais le feu au dedans ne l'a pas laissé rester à terre.",
                "timestamp": "01:58:20"
              },
              {
                "reference": "Romains 5:5",
                "text": "Or, l'espérance ne trompe point, parce que l'amour de Dieu est répandu dans nos coeurs par le Saint-Esprit qui nous a été donné.",
                "note": "La passion est un fruit de l'amour de Dieu répandu par l'Esprit; la seule volonté ne suffit pas à te faire avancer.",
                "timestamp": "01:58:20"
              },
              {
                "reference": "Psaume 84:1-7",
                "text": "Que tes demeures sont aimables, Éternel des armées! Mon âme soupire et languit après les parvis de l'Éternel... Leur force augmente pendant la marche.",
                "note": "Sion est la station de ravitaillement; chaque jour a sa propre peine, donc la force d'hier ne portera pas aujourd'hui.",
                "timestamp": "02:05:57"
              },
              {
                "reference": "1 Rois 18:30-38",
                "text": "Il rétablit l'autel de l'Éternel... et il fit un fossé autour de l'autel... Et le feu de l'Éternel tomba.",
                "note": "Ne regarde pas seulement le feu qui est tombé; regarde la préparation qui l'a précédé, et prépare ton coeur pour l'autel.",
                "timestamp": "02:09:12"
              }
            ],
            "quotes": [
              {
                "quote": "La passion est un désir intense qui pousse une personne vers un but. C'est un feu intérieur qui refuse de se contenter de la médiocrité.",
                "timestamp": "01:49:34"
              },
              {
                "quote": "Les champions ne sont pas ceux qui ne tombent jamais. Les champions sont ceux qui, quand ils tombent, refusent de rester à terre.",
                "timestamp": "01:50:43"
              },
              {
                "quote": "Si tu laisses un homme qui a une vision pour un homme qui a une télévision, demain tu regarderas l'homme à la vision sur la télévision de ton mari.",
                "timestamp": "01:46:04"
              },
              {
                "quote": "Celui qui ne va plus à la communion fraternelle souffle sans le savoir sur sa propre bougie.",
                "timestamp": "02:07:03"
              },
              {
                "quote": "Ne regarde pas seulement le feu qui est descendu. Qu'est-ce qui a précédé le feu?",
                "timestamp": "02:10:19"
              }
            ]
          },
          "es": {
            "title": "Pasión por Dios para volar alto",
            "preacher": "Predicador invitado",
            "summary": "Isaías 40 no promete a los jóvenes una carrera fácil. Dice claramente que aun los muchachos se fatigan, así que la pregunta no es si quedarás agotado sino qué harás cuando lo estés. El predicador respondió con su propia historia: creyente de primera generación, llevado a Cristo por las oraciones de una madre anciana, desheredado por su familia, durmiendo en el piso de la casa de otro, y aun así algo lo mantuvo en pie. Ese algo es la pasión, un deseo intenso que impulsa a una persona hacia un propósito, un fuego interior que se niega a conformarse con la mediocridad. Los campeones, dijo, no son los que nunca caen, sino los que se niegan a quedarse en el suelo cuando caen. Sin embargo, la pasión sola no basta. Tiene que ser dirigida, y la pasión por Dios debe estar por encima de cualquier otra, porque una visión que no se alinea con el propósito divino no será sostenida por energía divina. Probó que el amor a Dios se demuestra amando lo que Él más ama, las almas, tal como Jesús presionó a Pedro tres veces para que apacentara sus ovejas. Luego nombró cuatro áreas donde arder: pasión por Dios, por las almas, por la comunión y por agradar a Dios como estilo de vida, antes de dar cuatro formas de mantener el fuego: levantar el altar de oración como Elías, escudriñar las Escrituras por revelación fresca, permanecer en la congregación y ser de bendición a alguien.",
            "keyPoints": [
              "La vida te agotará espiritual, mental y físicamente; Isaías 40:30 dice que aun los jóvenes se fatigan, así que reabastecerse no es opcional.",
              "La pasión es un deseo intenso que impulsa a una persona hacia un propósito, un fuego interior que se niega a conformarse con la mediocridad.",
              "Los campeones no son los que nunca caen; son los que se niegan a quedarse en el suelo cuando caen.",
              "La pasión sola no basta. Debe ser dirigida, y la pasión por Dios debe ir primero para que tu visión sea sostenida por energía divina.",
              "Tu amor por Dios se prueba amando lo que Él más ama, las almas; eso era lo que Jesús buscaba cuando le dijo a Pedro que apacentara sus ovejas.",
              "Cuatro áreas donde arder: pasión por Dios, por las almas, por la comunión y por agradar a Dios como estilo de vida.",
              "La comunión es la estación de reabastecimiento; quien deja de congregarse está apagando su propia vela sin darse cuenta.",
              "Mantén el fuego levantando el altar de oración como Elías, escudriñando las Escrituras por revelación fresca, permaneciendo en la congregación y siendo de bendición a otros."
            ],
            "scriptures": [
              {
                "reference": "Isaías 40:30-31",
                "text": "Los mancebos se fatigan y se cansan, los mozos flaquean y caen: mas los que esperan á Jehová tendrán nuevas fuerzas; levantarán las alas como águilas.",
                "note": "Texto base; la vida no te prometió una carrera sin desgaste, pero hay algo que hacer para ser reabastecido.",
                "timestamp": "01:38:15"
              },
              {
                "reference": "Mateo 5:14-16",
                "text": "Vosotros sois la luz del mundo: una ciudad asentada sobre un monte no se puede esconder... Así alumbre vuestra luz delante de los hombres, para que vean vuestras obras buenas.",
                "note": "La lámpara alumbra solo mientras tenga combustible; una vela en el viento hay que protegerla para que siga ardiendo.",
                "timestamp": "01:42:42"
              },
              {
                "reference": "Lamentaciones 3:27",
                "text": "Bueno le es al hombre, si llevare el yugo desde su mocedad.",
                "note": "El yugo representa responsabilidad, disciplina, formación y sujeción; no desperdicies esta temporada, abrázala.",
                "timestamp": "01:47:12"
              },
              {
                "reference": "Romanos 8:28",
                "text": "Y sabemos que á los que á Dios aman, todas las cosas les ayudan á bien, es á saber, á los que conforme al propósito son llamados.",
                "note": "La pasión por Dios es lo que hace que todo obre para tu bien; tu amor por Él debe estar en la cima de tu propósito.",
                "timestamp": "01:51:52"
              },
              {
                "reference": "Juan 21:15-17",
                "text": "Simón, hijo de Jonás, ¿me amas más que estos?... Apacienta mis ovejas.",
                "note": "Jesús puso a prueba la declaración de amor de Pedro; si pones cualquier otra cosa por encima de lo que Él más ama, no lo amas tanto como dices.",
                "timestamp": "01:52:55"
              },
              {
                "reference": "Jeremías 20:9",
                "text": "Y dije: No me acordaré más de él, ni hablaré más en su nombre: empero fué en mi corazón como un fuego ardiente metido en mis huesos.",
                "note": "Jeremías había sido azotado y puesto en el cepo y quería renunciar, pero el fuego adentro no lo dejó quedarse en el suelo.",
                "timestamp": "01:58:20"
              },
              {
                "reference": "Romanos 5:5",
                "text": "Y la esperanza no avergüenza; porque el amor de Dios está derramado en nuestros corazones por el Espíritu Santo que nos es dado.",
                "note": "La pasión es fruto del amor de Dios derramado por el Espíritu; la fuerza de voluntad sola no basta para seguir avanzando.",
                "timestamp": "01:58:20"
              },
              {
                "reference": "Salmo 84:1-7",
                "text": "¡Cuán amables son tus moradas, oh Jehová de los ejércitos! Codicia y aun ardientemente desea mi alma los atrios de Jehová... Irán de fortaleza en fortaleza.",
                "note": "Sion es la estación de reabastecimiento; cada día trae su propio mal, así que la fuerza de ayer no sostiene el hoy.",
                "timestamp": "02:05:57"
              },
              {
                "reference": "1 Reyes 18:30-38",
                "text": "Y reparó el altar de Jehová... é hizo una reguera alrededor del altar... Entonces cayó fuego de Jehová.",
                "note": "No mires solo el fuego que cayó; mira la preparación que lo precedió, y prepara tu corazón para el altar.",
                "timestamp": "02:09:12"
              }
            ],
            "quotes": [
              {
                "quote": "La pasión es un deseo intenso que impulsa a una persona hacia un propósito. Es un fuego interior que se niega a conformarse con la mediocridad.",
                "timestamp": "01:49:34"
              },
              {
                "quote": "Los campeones no son los que no caen al suelo. Los campeones son los que, cuando caen, se niegan a quedarse en el suelo.",
                "timestamp": "01:50:43"
              },
              {
                "quote": "Si cambias a un hombre con visión por un hombre con televisión, mañana verás al hombre con visión en la televisión de tu esposo.",
                "timestamp": "01:46:04"
              },
              {
                "quote": "El que ya no se congrega está apagando su propia vela sin darse cuenta.",
                "timestamp": "02:07:03"
              },
              {
                "quote": "No mires solo el fuego que descendió. ¿Qué precedió al fuego?",
                "timestamp": "02:10:19"
              }
            ]
          },
          "creole": {
            "title": "Pasyon pou Bondye pou Vole Wo",
            "preacher": "Predikatè envite",
            "summary": "Ezayi 40 pa pwomèt jèn yo yon kous fasil. Li di klè menm jèn gason yo ap bouke, kidonk kesyon an se pa si w ap epwize men sa w ap fè lè sa rive. Predikatè a reponn ak pwòp istwa pa l: yon kwayan premye jenerasyon, yon manman ki te gen katrevendis ane te lapriyè pou l vin nan Kris, fanmi l te retire eritaj li, li t ap dòmi atè lakay yon moun, epi malgre sa gen yon bagay ki te kenbe l kanpe. Bagay sa a se pasyon, yon dezi entans ki pouse yon moun al nan yon objektif, yon dife anndan ki refize kontante l ak mediyokrite. Chanpyon yo, li di, se pa moun ki pa janm tonbe, men moun ki refize rete atè lè yo tonbe. Men pasyon pou kont li pa ase. Li dwe gen yon direksyon, epi pasyon pou Bondye dwe pase anvan tout lòt, paske yon vizyon ki pa aliyen ak plan Bondye p ap pote pa enèji Bondye. Li montre lanmou pou Bondye pwouve tèt li nan lanmou pou sa Bondye renmen plis la, nanm yo, jan Jezi te peze Pyè twa fwa pou l bay mouton l yo manje. Apre sa li nonmen kat domèn pou boule: pasyon pou Bondye, pou nanm yo, pou rasanbleman an, ak pou fè Bondye plezi kòm yon fason viv, anvan li bay kat mwayen pou kenbe dife a: bati lotèl lapriyè a jan Eli te fè l, chèche nan Ekriti yo pou revelasyon fre, rete nan asanble a, epi vin yon benediksyon pou yon moun.",
            "keyPoints": [
              "Lavi ap epwize ou espirityèlman, mantalman ak fizikman; Ezayi 40:30 di menm jèn yo ap bouke, kidonk refè fòs ou pa opsyonèl.",
              "Pasyon se yon dezi entans ki pouse yon moun al nan yon objektif, yon dife anndan ki refize mediyokrite.",
              "Chanpyon yo se pa moun ki pa janm tonbe; se moun ki refize rete atè lè yo tonbe.",
              "Pasyon pou kont li pa ase. Li dwe gen yon direksyon, epi pasyon pou Bondye dwe pase anvan pou vizyon ou ka pote pa enèji Bondye.",
              "Lanmou ou pou Bondye pwouve tèt li nan lanmou pou sa li renmen plis la, nanm yo; se sa Jezi t ap peze lè li di Pyè bay mouton l yo manje.",
              "Kat domèn pou boule: pasyon pou Bondye, pou nanm yo, pou rasanbleman an, ak pou fè Bondye plezi kòm yon fason viv.",
              "Rasanbleman an se estasyon pou refè fòs; moun ki pa rasanble ankò ap soufle sou pwòp balèn pa l san l pa konnen.",
              "Kenbe dife a lè w bati lotèl lapriyè a jan Eli te fè l, lè w chèche nan Ekriti yo pou revelasyon fre, lè w rete nan asanble a, epi lè w vin yon benediksyon pou lòt moun."
            ],
            "scriptures": [
              {
                "reference": "Ezayi 40:30-31",
                "text": "Menm jèn gason yo ap bouke, y ap fatige, jèn moun yo ap tonbe; men moun ki mete espwa yo nan Senyè a ap jwenn fòs tou nèf. Y ap vole tankou malfini.",
                "note": "Tèks debaz la; lavi pa pwomèt ou yon kous san epwizman, men gen yon bagay pou fè pou w ka refè fòs ou.",
                "timestamp": "01:38:15"
              },
              {
                "reference": "Matye 5:14-16",
                "text": "Se nou menm ki limyè lemonn. Yon vil ki bati sou yon mòn pa ka kache... Se pou limyè nou klere devan tout moun, pou yo ka wè bon zèv nou yo.",
                "note": "Lanp lan klere sèlman toutotan gen gaz ladan l; yon balèn nan van dwe pwoteje pou l kontinye limen.",
                "timestamp": "01:42:42"
              },
              {
                "reference": "Plent 3:27",
                "text": "Sa bon pou yon moun pote jouk la depi nan jenès li.",
                "note": "Jouk la reprezante responsablite, disiplin, fòmasyon ak soumisyon; pa gaspiye sezon sa a, anbrase l.",
                "timestamp": "01:47:12"
              },
              {
                "reference": "Women 8:28",
                "text": "Nou konnen tou bagay travay ansanm pou byen moun ki renmen Bondye, moun li rele dapre plan li.",
                "note": "Pasyon pou Bondye se sa ki fè tout bagay travay ansanm pou byen ou; lanmou ou pou li dwe nan tèt objektif ou.",
                "timestamp": "01:51:52"
              },
              {
                "reference": "Jan 21:15-17",
                "text": "Simon, pitit Jan, èske ou renmen m plis pase moun sa yo?... Okipe mouton m yo.",
                "note": "Jezi te mete deklarasyon lanmou Pyè a a leprèv; si w mete nenpòt lòt bagay pi wo pase sa li renmen plis la, ou pa renmen l otan ou di.",
                "timestamp": "01:52:55"
              },
              {
                "reference": "Jeremi 20:9",
                "text": "Lè m di: Mwen p ap pale de li ankò, mwen p ap pale nan non li ankò, pawòl li a tounen yon dife k ap boule anndan kè m, fèmen nan zo m yo.",
                "note": "Yo te bat Jeremi epi mete l nan sèp epi li te vle kite, men dife anndan an pa t kite l rete atè.",
                "timestamp": "01:58:20"
              },
              {
                "reference": "Women 5:5",
                "text": "Espwa sa a p ap desevwa nou, paske Bondye vide lanmou li nan kè nou ak Sentespri li ban nou an.",
                "note": "Pasyon se yon fwi lanmou Bondye Lespri a vide a; senp volonte pa ase pou fè w kontinye avanse.",
                "timestamp": "01:58:20"
              },
              {
                "reference": "Sòm 84:1-7",
                "text": "Ala bèl kay ou yo bèl, Senyè ki gen tout pouvwa! Nanm mwen anvi, li menm fennen pou lakou Senyè a... Y ap mache soti nan fòs pou al nan fòs.",
                "note": "Siyon se estasyon pou refè fòs; chak jou vini ak pwòp pwoblèm pa l, kidonk fòs ayè a p ap pote jodi a.",
                "timestamp": "02:05:57"
              },
              {
                "reference": "1 Wa 18:30-38",
                "text": "Li repare lotèl Senyè a... li fouye yon kanal toutotou lotèl la... Lè sa a dife Senyè a tonbe.",
                "note": "Pa gade sèlman dife ki te tonbe a; gade preparasyon ki te vin anvan l lan, epi prepare pwòp kè ou pou lotèl la.",
                "timestamp": "02:09:12"
              }
            ],
            "quotes": [
              {
                "quote": "Pasyon se yon dezi entans ki pouse yon moun al nan yon objektif. Se yon dife anndan ki refize kontante l ak mediyokrite.",
                "timestamp": "01:49:34"
              },
              {
                "quote": "Chanpyon yo se pa moun ki pa janm tonbe atè. Chanpyon yo se moun ki, lè yo tonbe, refize rete atè.",
                "timestamp": "01:50:43"
              },
              {
                "quote": "Si w lage yon nonm ki gen yon vizyon pou yon nonm ki gen yon televizyon, demen w ap gade nonm ki gen vizyon an nan televizyon mari ou.",
                "timestamp": "01:46:04"
              },
              {
                "quote": "Moun ki pa nan rasanbleman ankò ap soufle sou pwòp balèn pa l san l pa konnen.",
                "timestamp": "02:07:03"
              },
              {
                "quote": "Pa gade sèlman dife ki te desann lan. Kisa ki te vin anvan dife a?",
                "timestamp": "02:10:19"
              }
            ]
          }
        }
      },
      {
        "slug": "day-4-morning-engaging-the-power-of-praise",
        "day": 4,
        "session": "morning",
        "date": "2026-07-24",
        "videoId": "GYtUz98nmzI",
        "startTimestamp": "02:33:53",
        "title": "Engaging the Power of Praise for Soaring High",
        "preacher": "Ministering",
        "summary": "The second message of the morning started from Psalm 67, where the people praise and then the earth yields her increase. If God's blessing is what you came for, the minister said, praise has to be what you want. He built the whole message on atmosphere. God made water before fish and air before birds, and Eden before man, and man is the only creature that lost its atmosphere. Praise is how you get back in, because you enter His gates with thanksgiving and His courts with praise. He defined praise as appreciating God for His past acts, prevailing circumstances notwithstanding, and pointed to Habakkuk's stubborn yet: no fruit on the vine, nothing in the stalls, yet I will rejoice. From there came seven things praise does. It invites God's presence, since in prayer angels show up but in praise God Himself comes down. It hands your battle over to God, as with Jehoshaphat, who did not know what to do and praised anyway while God set ambushes. It irrigates a stalled vision, opens the atmosphere for direction and revelation, breaks chains and barriers, releases the joy that is your strength, and preserves what God has given, because nothing decays in His presence. He closed by naming murmuring, pride and ingratitude as the enemies of praise, and gave two practical habits: start every day with praise, and fill your ears with godly songs.",
        "keyPoints": [
          "If God's blessing is what you came for, praise has to be what you desire; the people praise, then the earth yields her increase.",
          "God built an atmosphere before every living thing, and man is the only creature that lost his; praise is what carries you back into the presence.",
          "Praise is appreciating God for His past acts, prevailing circumstances notwithstanding, like Habakkuk's yet will I rejoice.",
          "In prayer angels show up, but in praise God Himself comes down; when praise is off, His presence turns off with it.",
          "Praise hands the conduct of the battle over to God, as with Jehoshaphat, whose singers went out first while the Lord set ambushes.",
          "Praise opens the atmosphere for direction and revelation, because it moves you close enough to hear God clearly.",
          "Praise breaks chains and barriers, releases the joy that is your strength, and preserves the blessing, because nothing decays in God's presence.",
          "The enemies of praise are murmuring, pride and ingratitude; make praise a lifestyle by starting the day with it and guarding what you listen to."
        ],
        "scriptures": [
          {
            "reference": "Psalm 67:3-7",
            "text": "Let the people praise thee, O God; let all the people praise thee. Then shall the earth yield her increase; and God, even our own God, shall bless us.",
            "note": "Anchor text; praise comes first and increase follows, so if you want the blessing you must want the praise.",
            "timestamp": "02:33:53"
          },
          {
            "reference": "Exodus 19:4",
            "text": "Ye have seen what I did unto the Egyptians, and how I bare you on eagles' wings, and brought you unto myself.",
            "note": "Used with John 20:21 and Deuteronomy 18:15 to show that Christ came as the eagle and sent us out as eagles.",
            "timestamp": "02:37:23"
          },
          {
            "reference": "Psalm 100:4",
            "text": "Enter into his gates with thanksgiving, and into his courts with praise: be thankful unto him, and bless his name.",
            "note": "Praise is the access back into the atmosphere man lost in Eden, and no one soars outside that atmosphere.",
            "timestamp": "02:41:59"
          },
          {
            "reference": "Habakkuk 3:17-19",
            "text": "Although the fig tree shall not blossom, neither shall fruit be in the vines... yet I will rejoice in the LORD, I will joy in the God of my salvation.",
            "note": "The stubborn yet: no situation should be strong enough to damage your praise, because praise is the air you fly in.",
            "timestamp": "02:44:17"
          },
          {
            "reference": "Psalm 22:3",
            "text": "But thou art holy, O thou that inhabitest the praises of Israel.",
            "note": "Praise commands and guarantees the divine presence; take away praise and you have taken away God.",
            "timestamp": "02:46:38"
          },
          {
            "reference": "2 Chronicles 20:15-22",
            "text": "The battle is not yours, but God's... And when they began to sing and to praise, the LORD set ambushments against the children of Ammon, Moab, and mount Seir.",
            "note": "Jehoshaphat did not know what to do, so he praised, and God took over the fighting.",
            "timestamp": "02:49:10"
          },
          {
            "reference": "Nehemiah 8:10",
            "text": "Neither be ye sorry; for the joy of the LORD is your strength.",
            "note": "Praise releases the joy that becomes the strength you need to pray, to study and to meditate.",
            "timestamp": "02:56:52"
          },
          {
            "reference": "Acts 16:25-26",
            "text": "And at midnight Paul and Silas prayed, and sang praises unto God... and suddenly there was a great earthquake, so that the foundations of the prison were shaken.",
            "note": "When they prayed angels were dispatched, but when they praised God Himself entered the prison.",
            "timestamp": "03:03:53"
          },
          {
            "reference": "Ephesians 5:19-20",
            "text": "Speaking to yourselves in psalms and hymns and spiritual songs, singing and making melody in your heart to the Lord.",
            "note": "Songs are door openers; what fills your ears fills your life, so stop poisoning your environment with ungodly music.",
            "timestamp": "03:08:01"
          }
        ],
        "quotes": [
          {
            "quote": "No situation should be strong enough to damage your praise, because that is the conducive atmosphere for you to fly.",
            "timestamp": "02:45:24"
          },
          {
            "quote": "In prayer, angels show up. But in praise, God comes down.",
            "timestamp": "02:47:50"
          },
          {
            "quote": "When praise is on, God is on. When your praise is off, God turns off from you.",
            "timestamp": "02:47:50"
          },
          {
            "quote": "Praise is what you do when you are confused.",
            "timestamp": "03:02:39"
          },
          {
            "quote": "Nothing spoils in the presence of God. Nothing decays in God's presence. Hide your destiny in praise.",
            "timestamp": "02:59:43"
          }
        ],
        "translations": {
          "fr": {
            "title": "Engager la puissance de la louange pour voler haut",
            "preacher": "Prédicateur invité",
            "summary": "Le deuxième message de la matinée est parti du Psaume 67, où les peuples louent et ensuite la terre donne ses produits. Si c'est la bénédiction de Dieu que tu es venu chercher, a dit le prédicateur, alors c'est la louange que tu dois désirer. Il a construit tout le message sur l'atmosphère. Dieu a fait l'eau avant les poissons, l'air avant les oiseaux, et Éden avant l'homme, et l'homme est la seule créature à avoir perdu son atmosphère. La louange est le chemin du retour, car on entre dans ses portes avec des actions de grâces et dans ses parvis avec des louanges. Il a défini la louange comme l'appréciation de Dieu pour ce qu'il a déjà fait, malgré les circonstances présentes, et a montré le toutefois obstiné d'Habacuc: pas de fruit à la vigne, rien dans les étables, toutefois je me réjouirai. De là sont venues sept choses que fait la louange. Elle attire la présence de Dieu, car dans la prière les anges se déplacent, mais dans la louange c'est Dieu lui-même qui descend. Elle remet le combat entre les mains de Dieu, comme pour Josaphat, qui ne savait pas quoi faire et a loué quand même pendant que Dieu dressait des embuscades. Elle irrigue une vision bloquée, ouvre l'atmosphère de la direction et de la révélation, brise les chaînes et les barrières, libère la joie qui est ta force, et préserve ce que Dieu a donné, car rien ne se corrompt dans sa présence. Il a terminé en nommant le murmure, l'orgueil et l'ingratitude comme ennemis de la louange, et a donné deux habitudes pratiques: commencer chaque journée par la louange et remplir ses oreilles de chants pieux.",
            "keyPoints": [
              "Si c'est la bénédiction de Dieu que tu es venu chercher, la louange doit être ton désir; les peuples louent, puis la terre donne ses produits.",
              "Dieu a créé une atmosphère avant chaque être vivant, et l'homme est la seule créature à avoir perdu la sienne; la louange te ramène dans la présence.",
              "La louange, c'est apprécier Dieu pour ce qu'il a déjà fait, malgré les circonstances présentes, comme le toutefois d'Habacuc.",
              "Dans la prière les anges se déplacent, mais dans la louange Dieu lui-même descend; quand la louange s'éteint, sa présence s'éteint avec elle.",
              "La louange remet la conduite du combat à Dieu, comme pour Josaphat, dont les chantres sont sortis les premiers pendant que l'Éternel dressait des embuscades.",
              "La louange ouvre l'atmosphère de la direction et de la révélation, parce qu'elle te rapproche assez pour entendre Dieu clairement.",
              "La louange brise les chaînes et les barrières, libère la joie qui est ta force, et préserve la bénédiction, car rien ne se corrompt dans la présence de Dieu.",
              "Les ennemis de la louange sont le murmure, l'orgueil et l'ingratitude; fais de la louange un mode de vie en commençant ta journée par elle et en surveillant ce que tu écoutes."
            ],
            "scriptures": [
              {
                "reference": "Psaume 67:3-7",
                "text": "Les peuples te louent, ô Dieu! Tous les peuples te louent... La terre donne ses produits; Dieu, notre Dieu, nous bénit.",
                "note": "Texte d'appui; la louange vient d'abord et l'accroissement suit, donc si tu veux la bénédiction tu dois vouloir la louange.",
                "timestamp": "02:33:53"
              },
              {
                "reference": "Exode 19:4",
                "text": "Vous avez vu ce que j'ai fait à l'Égypte, et comment je vous ai portés sur des ailes d'aigle et amenés vers moi.",
                "note": "Utilisé avec Jean 20:21 et Deutéronome 18:15 pour montrer que Christ est venu comme l'aigle et nous a envoyés comme des aigles.",
                "timestamp": "02:37:23"
              },
              {
                "reference": "Psaume 100:4",
                "text": "Entrez dans ses portes avec des louanges, dans ses parvis avec des cantiques! Célébrez-le, bénissez son nom!",
                "note": "La louange est l'accès à l'atmosphère que l'homme a perdue en Éden, et nul ne s'élève hors de cette atmosphère.",
                "timestamp": "02:41:59"
              },
              {
                "reference": "Habacuc 3:17-19",
                "text": "Car le figuier ne fleurira pas, la vigne ne produira rien... Toutefois, je veux me réjouir en l'Éternel, je veux me réjouir dans le Dieu de mon salut.",
                "note": "Le toutefois obstiné: aucune situation ne doit être assez forte pour abîmer ta louange, car la louange est l'air dans lequel tu voles.",
                "timestamp": "02:44:17"
              },
              {
                "reference": "Psaume 22:3",
                "text": "Pourtant tu es le Saint, tu sièges au milieu des louanges d'Israël.",
                "note": "La louange commande et garantit la présence divine; enlève la louange et tu as enlevé Dieu.",
                "timestamp": "02:46:38"
              },
              {
                "reference": "2 Chroniques 20:15, 22",
                "text": "Ce ne sera pas vous qui combattrez, ce sera Dieu... Au moment où l'on commençait les chants et les louanges, l'Éternel plaça une embuscade contre les fils d'Ammon et de Moab.",
                "note": "Josaphat ne savait pas quoi faire, alors il a loué, et Dieu a pris le combat en main.",
                "timestamp": "02:49:10"
              },
              {
                "reference": "Néhémie 8:10",
                "text": "Ne vous affligez pas, car la joie de l'Éternel sera votre force.",
                "note": "La louange libère la joie qui devient la force dont tu as besoin pour prier, pour étudier et pour méditer.",
                "timestamp": "02:56:52"
              },
              {
                "reference": "Actes 16:25-26",
                "text": "Vers le milieu de la nuit, Paul et Silas priaient et chantaient les louanges de Dieu... Tout à coup il se fit un grand tremblement de terre, en sorte que les fondements de la prison furent ébranlés.",
                "note": "Quand ils priaient, des anges étaient envoyés, mais quand ils ont loué, Dieu lui-même est entré dans la prison.",
                "timestamp": "03:03:53"
              },
              {
                "reference": "Éphésiens 5:19-20",
                "text": "Entretenez-vous par des psaumes, par des hymnes, et par des cantiques spirituels, chantant et célébrant de tout votre coeur les louanges du Seigneur.",
                "note": "Les chants sont des portes; ce qui remplit tes oreilles remplit ta vie, alors cesse d'empoisonner ton environnement avec une musique impie.",
                "timestamp": "03:08:01"
              }
            ],
            "quotes": [
              {
                "quote": "Aucune situation ne doit être assez forte pour abîmer ta louange, parce que c'est l'atmosphère qui te permet de voler.",
                "timestamp": "02:45:24"
              },
              {
                "quote": "Dans la prière, les anges se déplacent. Mais dans la louange, c'est Dieu qui descend.",
                "timestamp": "02:47:50"
              },
              {
                "quote": "Quand la louange est allumée, Dieu est allumé. Quand ta louange s'éteint, Dieu s'éteint pour toi.",
                "timestamp": "02:47:50"
              },
              {
                "quote": "La louange, c'est ce que tu fais quand tu ne sais plus quoi faire.",
                "timestamp": "03:02:39"
              },
              {
                "quote": "Rien ne s'abîme dans la présence de Dieu. Rien ne se corrompt dans sa présence. Cache ta destinée dans la louange.",
                "timestamp": "02:59:43"
              }
            ]
          },
          "es": {
            "title": "Activar el poder de la alabanza para volar alto",
            "preacher": "Predicador invitado",
            "summary": "El segundo mensaje de la mañana partió del Salmo 67, donde los pueblos alaban y entonces la tierra da su fruto. Si viniste por la bendición de Dios, dijo el predicador, entonces la alabanza tiene que ser tu deseo. Construyó todo el mensaje sobre la atmósfera. Dios hizo el agua antes que los peces, el aire antes que las aves y el Edén antes que el hombre, y el hombre es la única criatura que perdió su atmósfera. La alabanza es el camino de regreso, porque se entra por sus puertas con acción de gracias y por sus atrios con alabanza. Definió la alabanza como apreciar a Dios por lo que ya hizo, a pesar de las circunstancias presentes, y señaló el terco con todo de Habacuc: sin fruto en la vid, nada en los establos, con todo yo me alegraré. De ahí salieron siete cosas que hace la alabanza. Atrae la presencia de Dios, porque en la oración los ángeles acuden, pero en la alabanza baja Dios mismo. Entrega tu batalla a Dios, como con Josafat, que no sabía qué hacer y alabó de todos modos mientras Dios ponía emboscadas. Riega una visión estancada, abre la atmósfera para la dirección y la revelación, rompe cadenas y barreras, libera el gozo que es tu fortaleza, y preserva lo que Dios ha dado, porque nada se corrompe en su presencia. Cerró nombrando la murmuración, el orgullo y la ingratitud como enemigos de la alabanza, y dio dos hábitos prácticos: comenzar cada día con alabanza y llenar los oídos de canciones piadosas.",
            "keyPoints": [
              "Si viniste por la bendición de Dios, la alabanza tiene que ser tu deseo; los pueblos alaban y entonces la tierra da su fruto.",
              "Dios construyó una atmósfera antes de cada ser vivo, y el hombre es la única criatura que perdió la suya; la alabanza te devuelve a la presencia.",
              "Alabar es apreciar a Dios por lo que ya hizo, a pesar de las circunstancias presentes, como el con todo me alegraré de Habacuc.",
              "En la oración acuden los ángeles, pero en la alabanza baja Dios mismo; cuando la alabanza se apaga, su presencia se apaga con ella.",
              "La alabanza entrega la conducción de la batalla a Dios, como con Josafat, cuyos cantores salieron primero mientras el Señor ponía emboscadas.",
              "La alabanza abre la atmósfera para la dirección y la revelación, porque te acerca lo suficiente para oír a Dios con claridad.",
              "La alabanza rompe cadenas y barreras, libera el gozo que es tu fortaleza y preserva la bendición, porque nada se corrompe en la presencia de Dios.",
              "Los enemigos de la alabanza son la murmuración, el orgullo y la ingratitud; haz de la alabanza un estilo de vida comenzando el día con ella y cuidando lo que escuchas."
            ],
            "scriptures": [
              {
                "reference": "Salmo 67:3-7",
                "text": "Alábente los pueblos, oh Dios; alábente los pueblos todos... La tierra dará su fruto: nos bendecirá Dios, el Dios nuestro.",
                "note": "Texto base; primero viene la alabanza y después el aumento, así que si quieres la bendición tienes que querer la alabanza.",
                "timestamp": "02:33:53"
              },
              {
                "reference": "Éxodo 19:4",
                "text": "Vosotros visteis lo que hice á los Egipcios, y cómo os tomé sobre alas de águilas, y os he traído á mí.",
                "note": "Usado con Juan 20:21 y Deuteronomio 18:15 para mostrar que Cristo vino como el águila y nos envió como águilas.",
                "timestamp": "02:37:23"
              },
              {
                "reference": "Salmo 100:4",
                "text": "Entrad por sus puertas con reconocimiento, por sus atrios con alabanza: alabadle, bendecid su nombre.",
                "note": "La alabanza es el acceso a la atmósfera que el hombre perdió en el Edén, y nadie vuela fuera de esa atmósfera.",
                "timestamp": "02:41:59"
              },
              {
                "reference": "Habacuc 3:17-19",
                "text": "Aunque la higuera no florecerá, ni en las vides habrá frutos... con todo yo me alegraré en Jehová, y me gozaré en el Dios de mi salud.",
                "note": "El terco con todo: ninguna situación debe ser lo bastante fuerte para dañar tu alabanza, porque la alabanza es el aire en que vuelas.",
                "timestamp": "02:44:17"
              },
              {
                "reference": "Salmo 22:3",
                "text": "Y tú, santo tú, que habitas entre las alabanzas de Israel.",
                "note": "La alabanza ordena y garantiza la presencia divina; quita la alabanza y has quitado a Dios.",
                "timestamp": "02:46:38"
              },
              {
                "reference": "2 Crónicas 20:15, 22",
                "text": "No es vuestra la guerra, sino de Dios... Y como comenzaron con clamor y con alabanza, puso Jehová contra los hijos de Ammón, de Moab, y del monte de Seir, las emboscadas.",
                "note": "Josafat no sabía qué hacer, así que alabó, y Dios tomó la batalla.",
                "timestamp": "02:49:10"
              },
              {
                "reference": "Nehemías 8:10",
                "text": "No os entristezcáis, porque el gozo de Jehová es vuestra fortaleza.",
                "note": "La alabanza libera el gozo que se convierte en la fuerza que necesitas para orar, estudiar y meditar.",
                "timestamp": "02:56:52"
              },
              {
                "reference": "Hechos 16:25-26",
                "text": "Mas á media noche, orando Pablo y Silas, cantaban himnos á Dios... entonces fué hecho de repente un gran terremoto, de tal manera que los cimientos de la cárcel se movían.",
                "note": "Cuando oraban se enviaban ángeles, pero cuando alabaron, Dios mismo entró en la cárcel.",
                "timestamp": "03:03:53"
              },
              {
                "reference": "Efesios 5:19-20",
                "text": "Hablando entre vosotros con salmos, y con himnos, y canciones espirituales, cantando y alabando al Señor en vuestros corazones.",
                "note": "Las canciones son puertas; lo que llena tus oídos llena tu vida, así que deja de envenenar tu ambiente con música impía.",
                "timestamp": "03:08:01"
              }
            ],
            "quotes": [
              {
                "quote": "Ninguna situación debe ser lo bastante fuerte para dañar tu alabanza, porque esa es la atmósfera que te permite volar.",
                "timestamp": "02:45:24"
              },
              {
                "quote": "En la oración acuden los ángeles. Pero en la alabanza baja Dios.",
                "timestamp": "02:47:50"
              },
              {
                "quote": "Cuando la alabanza está encendida, Dios está encendido. Cuando tu alabanza se apaga, Dios se apaga para ti.",
                "timestamp": "02:47:50"
              },
              {
                "quote": "La alabanza es lo que haces cuando ya no sabes qué hacer.",
                "timestamp": "03:02:39"
              },
              {
                "quote": "Nada se echa a perder en la presencia de Dios. Nada se corrompe en su presencia. Esconde tu destino en la alabanza.",
                "timestamp": "02:59:43"
              }
            ]
          },
          "creole": {
            "title": "Angaje Pouvwa Louwanj lan pou Vole Wo",
            "preacher": "Predikatè envite",
            "summary": "Dezyèm mesaj maten an te kòmanse nan Sòm 67, kote pèp yo louwe epi apre sa tè a bay rekòt li. Si se benediksyon Bondye ou te vin chèche, predikatè a di, se louwanj ki dwe dezi ou. Li bati tout mesaj la sou atmosfè. Bondye te fè dlo anvan pwason, lè anvan zwazo, epi Edenn anvan lòm, epi lòm se sèl kreyati ki pèdi atmosfè l. Louwanj se chemen pou retounen an, paske ou antre nan pòtay li ak remèsiman epi nan lakou li ak louwanj. Li defini louwanj kòm apresye Bondye pou sa li deja fè, kèlkeswa sikonstans yo, epi li montre men mwen tèt di Abakouk la: pa gen fwi sou pye rezen an, anyen nan pak yo, men mwen menm m ap rejwi. Depi la vin sèt bagay louwanj fè. Li rele prezans Bondye, paske nan lapriyè zanj yo vini, men nan louwanj Bondye li menm desann. Li remèt batay ou nan men Bondye, tankou Jozafa, ki pa t konnen sa pou l fè epi ki louwe kanmenm pandan Bondye t ap tann pèlen. Li wouze yon vizyon ki bloke, li louvri atmosfè direksyon ak revelasyon, li kase chenn ak baryè, li lage lajwa ki se fòs ou, epi li konsève sa Bondye bay, paske anyen pa gate nan prezans li. Li fèmen lè li nonmen babyay, ògèy ak engratitid kòm lènmi louwanj lan, epi li bay de abitid pratik: kòmanse chak jou ak louwanj, epi plen zòrèy ou ak chan ki sòti nan Bondye.",
            "keyPoints": [
              "Si se benediksyon Bondye ou te vin chèche, louwanj dwe dezi ou; pèp yo louwe, epi apre sa tè a bay rekòt li.",
              "Bondye te bati yon atmosfè anvan chak bagay vivan, epi lòm se sèl kreyati ki pèdi pa l; louwanj se sa ki mennen ou tounen nan prezans lan.",
              "Louwe se apresye Bondye pou sa li deja fè, kèlkeswa sikonstans yo, tankou men m ap rejwi Abakouk la.",
              "Nan lapriyè zanj yo vini, men nan louwanj Bondye li menm desann; lè louwanj lan etenn, prezans li etenn ansanm avè l.",
              "Louwanj remèt kondwit batay la nan men Bondye, tankou Jozafa, ki te voye chantè yo devan pandan Senyè a t ap tann pèlen.",
              "Louwanj louvri atmosfè direksyon ak revelasyon, paske li mennen ou pre ase pou ou tande Bondye klè.",
              "Louwanj kase chenn ak baryè, li lage lajwa ki se fòs ou, epi li konsève benediksyon an, paske anyen pa gate nan prezans Bondye.",
              "Lènmi louwanj lan se babyay, ògèy ak engratitid; fè louwanj tounen yon fason viv lè w kòmanse jounen an avè l epi lè w veye sa w ap koute."
            ],
            "scriptures": [
              {
                "reference": "Sòm 67:3-7",
                "text": "Se pou pèp yo louwe ou, o Bondye; se pou tout pèp yo louwe ou... Tè a bay rekòt li; Bondye, Bondye pa nou an, ap beni nou.",
                "note": "Tèks debaz la; louwanj vini anvan epi ogmantasyon swiv, kidonk si w vle benediksyon an fòk ou vle louwanj lan.",
                "timestamp": "02:33:53"
              },
              {
                "reference": "Egzòd 19:4",
                "text": "Nou wè sa m te fè moun peyi Lejip yo, epi jan m te pote nou sou zèl malfini epi mennen nou vin jwenn mwen.",
                "note": "Sèvi ansanm ak Jan 20:21 ak Detewonòm 18:15 pou montre Kris te vini kòm malfini an epi li voye nou tankou malfini.",
                "timestamp": "02:37:23"
              },
              {
                "reference": "Sòm 100:4",
                "text": "Antre nan pòtay li yo ak remèsiman, nan lakou li yo ak louwanj. Di l mèsi, beni non li.",
                "note": "Louwanj se aksè nan atmosfè lòm te pèdi nan Edenn nan, epi pèsonn pa vole andeyò atmosfè sa a.",
                "timestamp": "02:41:59"
              },
              {
                "reference": "Abakouk 3:17-19",
                "text": "Menm si pye fig la pa fleri, menm si pye rezen an pa bay fwi... men mwen menm m ap rejwi nan Senyè a, m ap fè kè m kontan nan Bondye ki sove m nan.",
                "note": "Men an tèt di: okenn sitiyasyon pa dwe fò ase pou l gate louwanj ou, paske louwanj se lè kote w ap vole a.",
                "timestamp": "02:44:17"
              },
              {
                "reference": "Sòm 22:3",
                "text": "Men ou menm, ou se Bondye ki apa a, ou chita nan mitan louwanj pèp Izrayèl la.",
                "note": "Louwanj kòmande epi garanti prezans Bondye; retire louwanj epi ou retire Bondye.",
                "timestamp": "02:46:38"
              },
              {
                "reference": "2 Kwonik 20:15, 22",
                "text": "Batay la se pa pou nou li ye, se pou Bondye... Epi lè yo kòmanse chante epi louwe, Senyè a tann pèlen pou moun Amon, Moab ak mòn Seyi yo.",
                "note": "Jozafa pa t konnen sa pou l fè, kidonk li louwe, epi Bondye pran batay la nan men l.",
                "timestamp": "02:49:10"
              },
              {
                "reference": "Neyemi 8:10",
                "text": "Pa kite lapenn pran nou, paske lajwa Senyè a se fòs nou.",
                "note": "Louwanj lage lajwa ki vin tounen fòs ou bezwen pou priye, pou etidye epi pou medite.",
                "timestamp": "02:56:52"
              },
              {
                "reference": "Travay 16:25-26",
                "text": "Vè minwi Pòl ak Silas t ap lapriyè epi t ap chante louwanj pou Bondye... epi yon sèl gwo tranblemanntè fèt, jouk fondasyon prizon an tranble.",
                "note": "Lè yo t ap lapriyè zanj yo te voye, men lè yo louwe, Bondye li menm antre nan prizon an.",
                "timestamp": "03:03:53"
              },
              {
                "reference": "Efezyen 5:19-20",
                "text": "Pale youn ak lòt ak sòm, ak kantik ak chan espirityèl, chante epi fè mizik nan kè nou pou Senyè a.",
                "note": "Chan yo se pòt; sa ki plen zòrèy ou plen lavi ou, kidonk sispann anpwazonnen anviwònman ou ak mizik ki pa sòti nan Bondye.",
                "timestamp": "03:08:01"
              }
            ],
            "quotes": [
              {
                "quote": "Okenn sitiyasyon pa dwe fò ase pou l gate louwanj ou, paske se atmosfè sa a ki pèmèt ou vole.",
                "timestamp": "02:45:24"
              },
              {
                "quote": "Nan lapriyè, zanj yo vini. Men nan louwanj, se Bondye ki desann.",
                "timestamp": "02:47:50"
              },
              {
                "quote": "Lè louwanj lan limen, Bondye limen. Lè louwanj ou etenn, Bondye etenn pou ou.",
                "timestamp": "02:47:50"
              },
              {
                "quote": "Louwanj se sa ou fè lè ou pa konnen sa pou ou fè.",
                "timestamp": "03:02:39"
              },
              {
                "quote": "Anyen pa gate nan prezans Bondye. Anyen pa pouri nan prezans li. Kache desten ou nan louwanj.",
                "timestamp": "02:59:43"
              }
            ]
          }
        }
      },
      {
        "slug": "day-4-evening-the-sacrifice-of-praise",
        "day": 4,
        "session": "evening",
        "date": "2026-07-24",
        "videoId": "0YSXhqnaR78",
        "startTimestamp": "01:31:55",
        "title": "The Sacrifice of Praise",
        "preacher": "Ministering",
        "summary": "All Nations Praise Night was mostly worship, ministrations and testimonies, but before the final praise the minister paused everything to open 2 Samuel 6 and explain what the church was about to do. David's first attempt to bring the ark home ended in disaster because Uzzah reached out to steady it, and the charge to the youth was blunt: when praise is in place, do not try to help God. David abandoned the ark at the house of Obed-Edom, a man nobody had heard of, and in three months that unknown household was so blessed that the king came back for it. The second time, the ark came up differently, with sacrifice, with dancing and with singing. That, he said, is the sacrifice of praise, and it is why every delegate had been handed an expectation card. Write what only God can do, then sing it in advance the way Hezekiah sang his songs. When Michal despised David for dancing without dignity, David answered that he would be even more vile than that, and she was barren to her death. Engage in the sacrifice of praise, he charged, and watch God deal with your enemies. The service closed from 1 Kings 8 with a declaration that every request dropped in the ark would return as a testimony.",
        "keyPoints": [
          "When praise is in place, do not try to help God; Uzzah reached out to steady the ark and it cost him his life.",
          "God is still involved even when it looks like everything is falling apart, so keep the praise going.",
          "Obed-Edom was a nobody, yet three months with the presence of God in his house changed his whole story.",
          "The second time the ark came up it came with three things: sacrifice, dancing and singing. That is the sacrifice of praise.",
          "Write your expectation down, then bring it to life by praising, dancing and singing in advance, the way Hezekiah said we will sing my songs.",
          "David chose to be more vile than his critics thought decent; praise God so freely that nobody can tell you are royalty.",
          "Michal despised the dance and was barren to her death; engage in the sacrifice of praise and watch God deal with your enemies.",
          "This praise is not ordinary: thank God for what He did last year, and most of all for what He is about to do."
        ],
        "scriptures": [
          {
            "reference": "2 Samuel 6:1-5",
            "text": "And David and all the house of Israel played before the LORD on all manner of instruments made of fir wood, even on harps, and on psalteries, and on timbrels, and on cornets, and on cymbals.",
            "note": "Anchor text; the whole message walked verse by verse through David's two attempts to bring up the ark.",
            "timestamp": "01:34:06"
          },
          {
            "reference": "2 Samuel 6:6-7",
            "text": "And when they came to Nachon's threshingfloor, Uzzah put forth his hand to the ark of God, and took hold of it; for the oxen shook it. And the anger of the LORD was kindled against Uzzah.",
            "note": "The hinge of the message: when praise is in place, no one should try to help God, because it can backfire.",
            "timestamp": "01:36:13"
          },
          {
            "reference": "2 Samuel 6:11-12",
            "text": "And the ark of the LORD continued in the house of Obededom the Gittite three months: and the LORD blessed Obededom, and all his household.",
            "note": "An unknown man given the ark as a burden ended up so blessed that the king came looking for him.",
            "timestamp": "01:37:18"
          },
          {
            "reference": "2 Samuel 6:13-14",
            "text": "And it was so, that when they that bare the ark of the LORD had gone six paces, he sacrificed oxen and fatlings. And David danced before the LORD with all his might.",
            "note": "The three things that changed the outcome the second time: sacrifice, dancing and singing.",
            "timestamp": "01:38:23"
          },
          {
            "reference": "Isaiah 38:20",
            "text": "The LORD was ready to save me: therefore we will sing my songs to the stringed instruments all the days of our life in the house of the LORD.",
            "note": "Hezekiah's response to his sickness; used to teach the delegates to sing their expectation in advance.",
            "timestamp": "01:41:39"
          },
          {
            "reference": "2 Samuel 6:16, 21-23",
            "text": "Michal Saul's daughter looked through a window, and saw king David leaping and dancing before the LORD; and she despised him in her heart... Therefore Michal the daughter of Saul had no child unto the day of her death.",
            "note": "Used to charge the youth to praise without regard for dignity, and as a warning to those who despise the dance.",
            "timestamp": "01:42:44"
          },
          {
            "reference": "1 Kings 8:38-39",
            "text": "What prayer and supplication soever be made by any man... which shall know every man the plague of his own heart, and spread forth his hands toward this house: then hear thou in heaven thy dwelling place, and forgive.",
            "note": "Read at the close over the expectation cards, with the charge to fill them at the hotel and bring them back at 7am.",
            "timestamp": "02:46:56"
          }
        ],
        "quotes": [
          {
            "quote": "When praise is in place, don't try to help God. The reason for praise is for God to come through. You and I cannot come through for God.",
            "timestamp": "01:36:13"
          },
          {
            "quote": "When it looks like everything is falling apart, God is still involved in praise.",
            "timestamp": "01:36:13"
          },
          {
            "quote": "First of all, I'm thanking you for what you did last year. Most importantly, I'm thanking you for what you are about to do.",
            "timestamp": "01:45:07"
          },
          {
            "quote": "David said I will be more vile. In other words, I will be more dishonourable. The way I will praise my King, you will not know that I'm a king.",
            "timestamp": "01:46:11"
          },
          {
            "quote": "Engage in this praise of sacrifice and watch God deal with your enemies.",
            "timestamp": "01:43:47"
          }
        ],
        "translations": {
          "fr": {
            "title": "Le sacrifice de louange",
            "preacher": "Prédicateur invité",
            "summary": "La nuit de louange de toutes les nations fut surtout faite d'adoration, de ministrations et de témoignages, mais avant la louange finale le prédicateur a tout arrêté pour ouvrir 2 Samuel 6 et expliquer ce que l'assemblée s'apprêtait à faire. La première tentative de David pour ramener l'arche s'est terminée en désastre parce qu'Uzza a étendu la main pour la retenir, et l'exhortation aux jeunes fut sans détour: quand la louange est en place, n'essaie pas d'aider Dieu. David a laissé l'arche chez Obed-Édom, un homme dont personne n'avait entendu parler, et en trois mois cette maison inconnue fut si bénie que le roi est revenu la chercher. La seconde fois, l'arche est montée autrement, avec sacrifice, avec danse et avec chant. Voilà, a-t-il dit, le sacrifice de louange, et c'est pourquoi chaque délégué avait reçu une carte d'attente. Écris ce que Dieu seul peut faire, puis chante-le d'avance comme Ézéchias a chanté ses cantiques. Quand Mical a méprisé David pour avoir dansé sans dignité, David a répondu qu'il s'abaisserait encore plus, et elle est restée stérile jusqu'à sa mort. Engage-toi dans le sacrifice de louange, a-t-il conclu, et regarde Dieu régler le compte de tes ennemis. Le service s'est achevé sur 1 Rois 8, avec la déclaration que chaque requête déposée dans l'arche reviendrait en témoignage.",
            "keyPoints": [
              "Quand la louange est en place, n'essaie pas d'aider Dieu; Uzza a étendu la main pour retenir l'arche et cela lui a coûté la vie.",
              "Dieu est toujours à l'oeuvre même quand tout semble s'effondrer, alors continue de louer.",
              "Obed-Édom n'était personne, pourtant trois mois avec la présence de Dieu chez lui ont changé toute son histoire.",
              "La seconde fois, l'arche est montée avec trois choses: le sacrifice, la danse et le chant. C'est cela le sacrifice de louange.",
              "Écris ton attente, puis donne-lui vie en louant, en dansant et en chantant d'avance, comme Ézéchias a dit: nous chanterons mes cantiques.",
              "David a choisi de s'abaisser plus que ses critiques ne le jugeaient convenable; loue Dieu si librement que personne ne devine que tu es de sang royal.",
              "Mical a méprisé la danse et est restée stérile jusqu'à sa mort; engage-toi dans le sacrifice de louange et regarde Dieu régler le compte de tes ennemis.",
              "Cette louange n'est pas ordinaire: remercie Dieu pour ce qu'il a fait l'an dernier, et surtout pour ce qu'il est sur le point de faire."
            ],
            "scriptures": [
              {
                "reference": "2 Samuel 6:1-5",
                "text": "David et toute la maison d'Israël jouaient devant l'Éternel de toutes sortes d'instruments de bois de cyprès, des harpes, des luths, des tambourins, des sistres et des cymbales.",
                "note": "Texte d'appui; tout le message a parcouru verset par verset les deux tentatives de David pour faire monter l'arche.",
                "timestamp": "01:34:06"
              },
              {
                "reference": "2 Samuel 6:6-7",
                "text": "Lorsqu'ils furent arrivés à l'aire de Nacon, Uzza étendit la main vers l'arche de Dieu et la saisit, parce que les boeufs la faisaient pencher. La colère de l'Éternel s'enflamma contre Uzza.",
                "note": "Le pivot du message: quand la louange est en place, personne ne doit essayer d'aider Dieu, car cela peut se retourner contre nous.",
                "timestamp": "01:36:13"
              },
              {
                "reference": "2 Samuel 6:11-12",
                "text": "L'arche de l'Éternel resta trois mois dans la maison d'Obed-Édom de Gath, et l'Éternel bénit Obed-Édom et toute sa maison.",
                "note": "Un inconnu à qui l'on avait confié l'arche comme un fardeau s'est retrouvé si béni que le roi est venu le chercher.",
                "timestamp": "01:37:18"
              },
              {
                "reference": "2 Samuel 6:13-14",
                "text": "Quand ceux qui portaient l'arche de l'Éternel eurent fait six pas, on sacrifia un boeuf et un veau gras. David dansait de toute sa force devant l'Éternel.",
                "note": "Les trois choses qui ont changé l'issue la seconde fois: le sacrifice, la danse et le chant.",
                "timestamp": "01:38:23"
              },
              {
                "reference": "Ésaïe 38:20",
                "text": "L'Éternel m'a sauvé! Nous ferons résonner les cordes de nos instruments, tous les jours de notre vie, dans la maison de l'Éternel.",
                "note": "La réponse d'Ézéchias à sa maladie; utilisée pour enseigner aux délégués à chanter leur attente d'avance.",
                "timestamp": "01:41:39"
              },
              {
                "reference": "2 Samuel 6:16, 21-23",
                "text": "Mical, fille de Saül, regardait par la fenêtre, et, voyant le roi David sauter et danser devant l'Éternel, elle le méprisa dans son coeur... Or Mical, fille de Saül, n'eut point d'enfants jusqu'au jour de sa mort.",
                "note": "Utilisé pour exhorter les jeunes à louer sans souci de dignité, et comme avertissement à ceux qui méprisent la danse.",
                "timestamp": "01:42:44"
              },
              {
                "reference": "1 Rois 8:38-39",
                "text": "Quelle que soit la prière, quelle que soit la supplication que fera tout homme... s'il étend les mains vers cette maison, exauce-le des cieux, du lieu de ta demeure, et pardonne.",
                "note": "Lu à la clôture sur les cartes d'attente, avec la consigne de les remplir à l'hôtel et de les rapporter à 7 h.",
                "timestamp": "02:46:56"
              }
            ],
            "quotes": [
              {
                "quote": "Quand la louange est en place, n'essaie pas d'aider Dieu. Le but de la louange, c'est que Dieu intervienne. Toi et moi ne pouvons pas intervenir à la place de Dieu.",
                "timestamp": "01:36:13"
              },
              {
                "quote": "Quand tout semble s'effondrer, Dieu est toujours à l'oeuvre dans la louange.",
                "timestamp": "01:36:13"
              },
              {
                "quote": "D'abord, je te remercie pour ce que tu as fait l'an dernier. Et surtout, je te remercie pour ce que tu es sur le point de faire.",
                "timestamp": "01:45:07"
              },
              {
                "quote": "David a dit: je m'abaisserai encore plus. Autrement dit, je serai encore moins digne. À la façon dont je louerai mon Roi, tu ne devineras pas que je suis roi.",
                "timestamp": "01:46:11"
              },
              {
                "quote": "Engage-toi dans ce sacrifice de louange et regarde Dieu régler le compte de tes ennemis.",
                "timestamp": "01:43:47"
              }
            ]
          },
          "es": {
            "title": "El sacrificio de alabanza",
            "preacher": "Predicador invitado",
            "summary": "La noche de alabanza de todas las naciones fue sobre todo adoración, ministraciones y testimonios, pero antes de la alabanza final el predicador detuvo todo para abrir 2 Samuel 6 y explicar lo que la iglesia estaba a punto de hacer. El primer intento de David de traer el arca terminó en desastre porque Uza extendió la mano para sostenerla, y la exhortación a los jóvenes fue directa: cuando la alabanza está en su lugar, no trates de ayudar a Dios. David dejó el arca en casa de Obed-edom, un hombre del que nadie había oído hablar, y en tres meses aquella casa desconocida quedó tan bendecida que el rey volvió a buscarla. La segunda vez el arca subió de otra manera, con sacrificio, con danza y con canto. Eso, dijo, es el sacrificio de alabanza, y por eso a cada delegado se le había entregado una tarjeta de expectativa. Escribe lo que solo Dios puede hacer, y luego cántalo por adelantado como Ezequías cantó sus cánticos. Cuando Mical menospreció a David por danzar sin dignidad, David respondió que se abatiría aún más, y ella quedó sin hijos hasta el día de su muerte. Entrégate al sacrificio de alabanza, concluyó, y verás a Dios ocuparse de tus enemigos. El servicio cerró con 1 Reyes 8 y la declaración de que toda petición depositada en el arca volvería como testimonio.",
            "keyPoints": [
              "Cuando la alabanza está en su lugar, no trates de ayudar a Dios; Uza extendió la mano para sostener el arca y le costó la vida.",
              "Dios sigue obrando aunque todo parezca derrumbarse, así que no dejes de alabar.",
              "Obed-edom no era nadie, y sin embargo tres meses con la presencia de Dios en su casa cambiaron toda su historia.",
              "La segunda vez el arca subió con tres cosas: sacrificio, danza y canto. Eso es el sacrificio de alabanza.",
              "Escribe tu expectativa y luego dale vida alabando, danzando y cantando por adelantado, como dijo Ezequías: cantaremos mis cánticos.",
              "David escogió abatirse más de lo que sus críticos consideraban decente; alaba a Dios con tanta libertad que nadie adivine que eres realeza.",
              "Mical menospreció la danza y quedó sin hijos hasta su muerte; entrégate al sacrificio de alabanza y verás a Dios ocuparse de tus enemigos.",
              "Esta alabanza no es ordinaria: da gracias a Dios por lo que hizo el año pasado, y sobre todo por lo que está a punto de hacer."
            ],
            "scriptures": [
              {
                "reference": "2 Samuel 6:1-5",
                "text": "Y David y toda la casa de Israel danzaban delante de Jehová con toda suerte de instrumentos de madera de haya, con arpas, salterios, adufes, flautas y címbalos.",
                "note": "Texto base; todo el mensaje recorrió versículo por versículo los dos intentos de David de subir el arca.",
                "timestamp": "01:34:06"
              },
              {
                "reference": "2 Samuel 6:6-7",
                "text": "Y cuando llegaron á la era de Nachôn, Uzza extendió la mano al arca de Dios, y túvola; porque los bueyes daban sacudidas. Y el furor de Jehová se encendió contra Uzza.",
                "note": "El eje del mensaje: cuando la alabanza está en su lugar, nadie debe intentar ayudar a Dios, porque puede volverse en su contra.",
                "timestamp": "01:36:13"
              },
              {
                "reference": "2 Samuel 6:11-12",
                "text": "Y estuvo el arca de Jehová en casa de Obed-edom Getheo tres meses: y bendijo Jehová á Obed-edom y á toda su casa.",
                "note": "Un desconocido que recibió el arca como una carga terminó tan bendecido que el rey vino a buscarlo.",
                "timestamp": "01:37:18"
              },
              {
                "reference": "2 Samuel 6:13-14",
                "text": "Y como los que llevaban el arca de Dios habían andado seis pasos, sacrificaban un buey y un carnero grueso. Y David saltaba con toda su fuerza delante de Jehová.",
                "note": "Las tres cosas que cambiaron el desenlace la segunda vez: sacrificio, danza y canto.",
                "timestamp": "01:38:23"
              },
              {
                "reference": "Isaías 38:20",
                "text": "Jehová para salvarme; por tanto cantaremos nuestros salmos en la casa de Jehová todos los días de nuestra vida.",
                "note": "La respuesta de Ezequías a su enfermedad; usada para enseñar a los delegados a cantar su expectativa por adelantado.",
                "timestamp": "01:41:39"
              },
              {
                "reference": "2 Samuel 6:16, 21-23",
                "text": "Michâl hija de Saúl miró desde una ventana, y vió al rey David que saltaba con toda su fuerza delante de Jehová, y menospreciólo en su corazón... Y Michâl hija de Saúl nunca tuvo hijos hasta el día de su muerte.",
                "note": "Usado para animar a los jóvenes a alabar sin cuidar la dignidad, y como advertencia a quienes menosprecian la danza.",
                "timestamp": "01:42:44"
              },
              {
                "reference": "1 Reyes 8:38-39",
                "text": "Toda oración y todo ruego que hiciere cualquier hombre... si extendiere sus manos á esta casa, tú oirás en los cielos, en la habitación de tu morada, y perdonarás.",
                "note": "Leído al cierre sobre las tarjetas de expectativa, con el encargo de llenarlas en el hotel y traerlas a las 7 de la mañana.",
                "timestamp": "02:46:56"
              }
            ],
            "quotes": [
              {
                "quote": "Cuando la alabanza está en su lugar, no trates de ayudar a Dios. La razón de la alabanza es que Dios responda. Ni tú ni yo podemos responder por Dios.",
                "timestamp": "01:36:13"
              },
              {
                "quote": "Cuando parece que todo se derrumba, Dios sigue obrando en la alabanza.",
                "timestamp": "01:36:13"
              },
              {
                "quote": "Primero te doy gracias por lo que hiciste el año pasado. Y sobre todo, te doy gracias por lo que estás a punto de hacer.",
                "timestamp": "01:45:07"
              },
              {
                "quote": "David dijo: me abatiré aún más. Es decir, seré aún menos digno. Por la forma en que alabe a mi Rey, no sabrás que soy rey.",
                "timestamp": "01:46:11"
              },
              {
                "quote": "Entrégate a este sacrificio de alabanza y verás a Dios ocuparse de tus enemigos.",
                "timestamp": "01:43:47"
              }
            ]
          },
          "creole": {
            "title": "Sakrifis Louwanj lan",
            "preacher": "Predikatè envite",
            "summary": "Nwit Louwanj Tout Nasyon an te sitou adorasyon, ministrasyon ak temwayaj, men anvan dènye louwanj lan predikatè a te kanpe tout bagay pou l louvri 2 Samyèl 6 epi esplike sa asanble a t apral fè. Premye fwa David te eseye pote lach la lakay li, sa te fini nan malè paske Ouza te lonje men l pou kenbe l, epi egzòtasyon an bay jèn yo te klè: lè louwanj lan nan plas li, pa eseye ede Bondye. David te kite lach la lakay Obèd-Edòm, yon nonm pèsonn pa t janm tande pale de li, epi nan twa mwa kay enkoni sa a te tèlman beni ke wa a te tounen vin chèche l. Dezyèm fwa a, lach la te monte yon lòt jan, ak sakrifis, ak danse epi ak chante. Se sa, li di, ki sakrifis louwanj lan, epi se poutèt sa yo te bay chak delege yon kat atant. Ekri sa Bondye sèl kapab fè, epi chante l davans jan Ezekyas te chante chan pa l yo. Lè Mikal te meprize David paske li t ap danse san diyite, David te reponn li t ap desann pi ba toujou, epi Mikal pa t janm gen pitit jouk jou li mouri. Antre nan sakrifis louwanj lan, li konkli, epi gade Bondye regle ak lènmi ou yo. Sèvis la te fèmen sou 1 Wa 8, ak deklarasyon ke chak demann yo te depoze nan lach la t ap tounen kòm temwayaj.",
            "keyPoints": [
              "Lè louwanj lan nan plas li, pa eseye ede Bondye; Ouza te lonje men l pou kenbe lach la epi sa te koute l lavi l.",
              "Bondye toujou nan travay menm lè tout bagay sanble ap tonbe, kidonk kontinye louwe.",
              "Obèd-Edòm pa t anyen, men twa mwa ak prezans Bondye lakay li chanje tout istwa l.",
              "Dezyèm fwa a, lach la monte ak twa bagay: sakrifis, danse ak chante. Se sa ki sakrifis louwanj lan.",
              "Ekri atant ou, epi ba li lavi lè w louwe, danse epi chante davans, jan Ezekyas te di: n ap chante chan pa m yo.",
              "David te chwazi desann pi ba pase sa kritik li yo te jwenn konvenab; louwe Bondye tèlman lib ke pèsonn pa ka devine ou se wa.",
              "Mikal te meprize dans lan epi li pa t janm gen pitit jouk li mouri; antre nan sakrifis louwanj lan epi gade Bondye regle ak lènmi ou yo.",
              "Louwanj sa a pa ògdinè: di Bondye mèsi pou sa l te fè ane pase a, epi sitou pou sa l pral fè a."
            ],
            "scriptures": [
              {
                "reference": "2 Samyèl 6:1-5",
                "text": "David ak tout moun pèp Izrayèl yo t ap fè fèt devan Senyè a ak tout kalite enstriman fèt an bwa, ak gita, ak bandjo, ak tanbouren, ak sistr ak senbal.",
                "note": "Tèks debaz la; tout mesaj la te pase vèsè pa vèsè sou de fwa David te eseye fè lach la monte.",
                "timestamp": "01:34:06"
              },
              {
                "reference": "2 Samyèl 6:6-7",
                "text": "Lè yo rive bò glasi Nakon an, Ouza lonje men l sou lach Bondye a pou kenbe l, paske bèf yo t ap souke l. Kòlè Senyè a te limen sou Ouza.",
                "note": "Pwen chanjman mesaj la: lè louwanj lan nan plas li, pèsonn pa dwe eseye ede Bondye, paske sa ka vire kont ou.",
                "timestamp": "01:36:13"
              },
              {
                "reference": "2 Samyèl 6:11-12",
                "text": "Lach Senyè a rete twa mwa lakay Obèd-Edòm, moun lavil Gat la. Senyè a te beni Obèd-Edòm ak tout fanmi l.",
                "note": "Yon enkoni yo te bay lach la tankou yon chay te vin tèlman beni ke wa a te vin chèche l.",
                "timestamp": "01:37:18"
              },
              {
                "reference": "2 Samyèl 6:13-14",
                "text": "Lè moun ki t ap pote lach Senyè a te fè sis pa, yo te ofri yon towo ak yon jenn bèt gra. David menm t ap danse ak tout fòs li devan Senyè a.",
                "note": "Twa bagay ki te chanje rezilta a dezyèm fwa a: sakrifis, danse ak chante.",
                "timestamp": "01:38:23"
              },
              {
                "reference": "Ezayi 38:20",
                "text": "Senyè a vin sove m! N ap chante chan nou yo ak enstriman nou yo, tout jou lavi nou, nan kay Senyè a.",
                "note": "Repons Ezekyas bay maladi l la; sèvi pou anseye delege yo chante atant yo davans.",
                "timestamp": "01:41:39"
              },
              {
                "reference": "2 Samyèl 6:16, 21-23",
                "text": "Mikal, pitit fi Sayil la, t ap gade nan fenèt la. Lè li wè wa David ap sote epi ap danse devan Senyè a, li meprize l nan kè l... Mikal, pitit fi Sayil la, pa t janm gen pitit jouk jou li mouri.",
                "note": "Sèvi pou egzòte jèn yo louwe san yo pa okipe diyite, epi kòm avètisman pou moun ki meprize dans lan.",
                "timestamp": "01:42:44"
              },
              {
                "reference": "1 Wa 8:38-39",
                "text": "Nenpòt lapriyè, nenpòt siplikasyon nenpòt moun fè... si li lonje men l nan direksyon kay sa a, tande l nan syèl la kote ou rete a, epi padonnen.",
                "note": "Li nan fèmti a sou kat atant yo, ak konsiy pou ranpli yo nan otèl la epi tounen ak yo a 7è nan maten.",
                "timestamp": "02:46:56"
              }
            ],
            "quotes": [
              {
                "quote": "Lè louwanj lan nan plas li, pa eseye ede Bondye. Rezon louwanj lan se pou Bondye entèvni. Ou menm ak mwen menm nou pa ka entèvni nan plas Bondye.",
                "timestamp": "01:36:13"
              },
              {
                "quote": "Lè tout bagay sanble ap tonbe, Bondye toujou nan travay nan louwanj lan.",
                "timestamp": "01:36:13"
              },
              {
                "quote": "Anvan tout bagay, m ap di ou mèsi pou sa ou te fè ane pase a. Men sitou, m ap di ou mèsi pou sa ou pral fè a.",
                "timestamp": "01:45:07"
              },
              {
                "quote": "David te di: m ap desann pi ba toujou. Sa vle di, m ap mwens diy toujou. Jan m pral louwe Wa mwen an, ou p ap konnen se yon wa mwen ye.",
                "timestamp": "01:46:11"
              },
              {
                "quote": "Antre nan sakrifis louwanj sa a epi gade Bondye regle ak lènmi ou yo.",
                "timestamp": "01:43:47"
              }
            ]
          }
        }
      }
    ]
  },
  {
    "day": 5,
    "label": "Day 5",
    "dateLabel": "Saturday, July 25",
    "date": "2026-07-25",
    "messages": [
      {
        "slug": "day-5-impartation-for-revival",
        "day": 5,
        "session": "morning",
        "date": "2026-07-25",
        "videoId": "afOOc7rbkgU",
        "startTimestamp": "00:35:47",
        "title": "Impartation for Revival",
        "preacher": "The National Pastor, Winners Chapel International Canada",
        "summary": "The grand finale opened with a simple promise from Romans 1:11, that Paul longed to come and impart a spiritual gift so the believers would be established. Impartation, the National Pastor explained, is the transference of a spiritual gift from one vessel to another, and there is no self-made man or woman in the kingdom. He told his own story: flying from Toronto to London for Bible school, going straight from the airport to the classroom and back, then kneeling in Pastor David Oyedepo's office to say he loved what the man carried and wanted it. Pride is why most people never receive, because they will not humble themselves before someone who carries what they need, even someone younger. From Leviticus 6 he turned to the fire on the altar that must never go out. Every believer is a priest, and the fire is ordained to burn daily, but where no wood is, the fire goes out. The wood is the word, gathered fresh every morning like manna, because yesterday's manna is poisonous. He gave the practical fuel: pray without ceasing, stay on the word extensively, meditate, listen to the word, and pray much in the Spirit. He closed with the three channels of impartation, God Himself, the spoken word, and carriers of His presence such as the anointing oil, before the oil was passed through the hall and the expectation cards were prayed over.",
        "keyPoints": [
          "Impartation is the transference of a spiritual gift from one vessel to another; there is no self-made man or woman in the kingdom.",
          "Paul's stated purpose for imparting a gift was establishment, so impartation does not just empower you, it establishes you.",
          "Until you are thirsty and desperate, there will be no impartation; pride is why most people never receive.",
          "The person carrying what you need may be younger than you; do not let pride rob you of it.",
          "The fire on the altar is ordained to burn daily, and every believer is a priest, so keeping the fire burning is your own responsibility.",
          "Where no wood is, the fire goes out. The wood is the word of God, and it has to go on the fire every morning.",
          "Yesterday's manna is poisonous; gather fresh word daily, exactly as the eagle refuses stale food.",
          "Keep the fire burning by praying without ceasing, studying and meditating on the word, listening to the word, and praying extensively in the Spirit.",
          "Impartation comes three ways: directly from God, through the spoken word as it is preached, and through carriers of His presence such as the anointing oil."
        ],
        "scriptures": [
          {
            "reference": "Romans 1:11",
            "text": "For I long to see you, that I may impart unto you some spiritual gift, to the end ye may be established.",
            "note": "Anchor text; spiritual gifts are imparted, and the end result of that impartation is establishment.",
            "timestamp": "00:36:53"
          },
          {
            "reference": "Leviticus 6:12-13",
            "text": "And the fire upon the altar shall be burning in it; it shall not be put out: and the priest shall burn wood on it every morning... The fire shall ever be burning upon the altar; it shall never go out.",
            "note": "The fire on the priest is ordained to burn daily, and we are the priests, so keeping it burning is our own responsibility.",
            "timestamp": "00:44:23"
          },
          {
            "reference": "Proverbs 26:20",
            "text": "Where no wood is, there the fire goeth out: so where there is no talebearer, the strife ceaseth.",
            "note": "The wood is the word of God, which is why daily and adequate word intake is what keeps the fire burning.",
            "timestamp": "00:47:39"
          },
          {
            "reference": "Exodus 16:19-21",
            "text": "And they gathered it every morning, every man according to his eating... some of them left of it until the morning, and it bred worms, and stank.",
            "note": "Yesterday's manna expired yesterday; the word has to be gathered fresh every morning.",
            "timestamp": "00:48:44"
          },
          {
            "reference": "1 Kings 18:33-38",
            "text": "And he put the wood in order... Then the fire of the LORD fell, and consumed the burnt sacrifice, and the wood, and the stones, and the dust, and licked up the water that was in the trench.",
            "note": "Elijah had to lay the wood in order to provoke the fire of God to fall, even over water.",
            "timestamp": "00:50:51"
          },
          {
            "reference": "Ephesians 6:18",
            "text": "Praying always with all prayer and supplication in the Spirit, and watching thereunto with all perseverance and supplication for all saints.",
            "note": "Cited with 1 Thessalonians 5:17 as the first thing that keeps the fire burning: pray without ceasing.",
            "timestamp": "00:53:03"
          },
          {
            "reference": "2 Timothy 2:15",
            "text": "Study to shew thyself approved unto God, a workman that needeth not to be ashamed, rightly dividing the word of truth.",
            "note": "Stay on the word: instead of an hour of gossip on the phone, give the hour to the word and watch your spirit man.",
            "timestamp": "00:54:07"
          },
          {
            "reference": "Joshua 1:8",
            "text": "This book of the law shall not depart out of thy mouth; but thou shalt meditate therein day and night... then thou shalt have good success.",
            "note": "Meditation on scripture is how staying on the word turns into prosperity and good success.",
            "timestamp": "00:55:13"
          },
          {
            "reference": "Matthew 7:7-8",
            "text": "Ask, and it shall be given you; seek, and ye shall find; knock, and it shall be opened unto you.",
            "note": "Reversed for emphasis: everyone who does not ask receives nothing, and if you are not knocking, nobody knows you are at the door.",
            "timestamp": "00:57:21"
          },
          {
            "reference": "Luke 4:18",
            "text": "The Spirit of the Lord is upon me, because he hath anointed me to preach the gospel to the poor; he hath sent me to heal the brokenhearted.",
            "note": "The first channel of impartation is God Himself, doing directly what no man can do.",
            "timestamp": "00:58:25"
          },
          {
            "reference": "Ezekiel 2:2",
            "text": "And the spirit entered into me when he spake unto me, and set me upon my feet, that I heard him that spake unto me.",
            "note": "The second channel: the spoken word itself carries impartation as it is being preached.",
            "timestamp": "00:59:30"
          },
          {
            "reference": "1 Samuel 16:13",
            "text": "Then Samuel took the horn of oil, and anointed him in the midst of his brethren: and the Spirit of the LORD came upon David from that day forward.",
            "note": "The third channel: carriers of God's presence, such as the anointing oil that was then passed through the hall.",
            "timestamp": "01:00:34"
          }
        ],
        "quotes": [
          {
            "quote": "There is no self-made man or woman in the kingdom. We are all products of spiritual gifts.",
            "timestamp": "00:37:56"
          },
          {
            "quote": "Until you are thirsty and desperate, there will be no impartation.",
            "timestamp": "00:39:00"
          },
          {
            "quote": "The person carrying what you need may be younger than you. Don't let pride rob you of it.",
            "timestamp": "01:03:50"
          },
          {
            "quote": "You can be close to the king, but don't be too familiar with the throne. It will rob you of the blessings of the king.",
            "timestamp": "01:04:55"
          },
          {
            "quote": "Yesterday's manna expired at the close of the day yesterday. If you don't want to stink, the wood must keep burning daily.",
            "timestamp": "00:48:44"
          }
        ],
        "translations": {
          "fr": {
            "title": "L'impartation pour le réveil",
            "preacher": "Le pasteur national, Winners Chapel International Canada",
            "summary": "La grande finale s'est ouverte sur une promesse toute simple, Romains 1:11, où Paul dit qu'il désire venir communiquer un don spirituel afin que les croyants soient affermis. L'impartation, a expliqué le pasteur national, c'est le transfert d'un don spirituel d'un vase à un autre, et il n'existe pas d'homme ni de femme qui se soit fait tout seul dans le royaume. Il a raconté sa propre histoire: parti de Toronto pour l'école biblique de Londres, allant de l'aéroport directement à la salle de cours puis repartant de la salle de cours à l'aéroport, puis s'agenouillant dans le bureau du pasteur David Oyedepo pour lui dire qu'il aimait ce que cet homme portait et qu'il le voulait. L'orgueil est la raison pour laquelle la plupart ne reçoivent jamais, car ils refusent de s'humilier devant celui qui porte ce dont ils ont besoin, même s'il est plus jeune. De Lévitique 6, il est passé au feu de l'autel qui ne doit jamais s'éteindre. Chaque croyant est sacrificateur, et le feu est destiné à brûler chaque jour, mais faute de bois le feu s'éteint. Le bois, c'est la Parole, ramassée fraîche chaque matin comme la manne, car la manne d'hier est empoisonnée. Il a donné le combustible pratique: prier sans cesse, demeurer dans la Parole, méditer, écouter la Parole et prier abondamment en esprit. Il a conclu par les trois canaux de l'impartation, Dieu lui-même, la parole prêchée, et les porteurs de sa présence comme l'huile d'onction, avant que l'huile ne circule dans la salle et que les cartes d'attente ne soient présentées dans la prière.",
            "keyPoints": [
              "L'impartation, c'est le transfert d'un don spirituel d'un vase à un autre; personne dans le royaume ne s'est fait tout seul.",
              "Le but déclaré de Paul en communiquant un don était l'affermissement, donc l'impartation ne fait pas que t'habiliter, elle t'établit.",
              "Tant que tu n'as pas soif et que tu n'es pas désespéré, il n'y aura pas d'impartation; l'orgueil est la raison pour laquelle la plupart ne reçoivent jamais.",
              "Celui qui porte ce dont tu as besoin peut être plus jeune que toi; ne laisse pas l'orgueil t'en priver.",
              "Le feu de l'autel doit brûler chaque jour, et chaque croyant est sacrificateur, donc l'entretenir est ta propre responsabilité.",
              "Faute de bois, le feu s'éteint. Le bois, c'est la Parole de Dieu, et il faut la mettre sur le feu chaque matin.",
              "La manne d'hier est empoisonnée; ramasse une parole fraîche chaque jour, exactement comme l'aigle refuse une nourriture avariée.",
              "Entretiens le feu en priant sans cesse, en étudiant et méditant la Parole, en écoutant la Parole et en priant abondamment en esprit.",
              "L'impartation vient de trois manières: directement de Dieu, par la parole prêchée, et par des porteurs de sa présence comme l'huile d'onction."
            ],
            "scriptures": [
              {
                "reference": "Romains 1:11",
                "text": "Car je désire vous voir, pour vous communiquer quelque don spirituel, afin que vous soyez affermis.",
                "note": "Texte d'appui; les dons spirituels se communiquent, et le résultat de cette impartation est l'affermissement.",
                "timestamp": "00:36:53"
              },
              {
                "reference": "Lévitique 6:12-13",
                "text": "Le feu brûlera sur l'autel, il ne s'éteindra point; chaque matin, le sacrificateur y allumera du bois... Le feu brûlera continuellement sur l'autel, il ne s'éteindra point.",
                "note": "Le feu sur le sacrificateur est destiné à brûler chaque jour, et c'est nous les sacrificateurs, donc l'entretenir nous revient.",
                "timestamp": "00:44:23"
              },
              {
                "reference": "Proverbes 26:20",
                "text": "Faute de bois, le feu s'éteint; et quand il n'y a point de rapporteur, la querelle s'apaise.",
                "note": "Le bois, c'est la Parole de Dieu, et c'est pourquoi une prise quotidienne et suffisante de la Parole entretient le feu.",
                "timestamp": "00:47:39"
              },
              {
                "reference": "Exode 16:19-21",
                "text": "Ils en laissèrent jusqu'au matin; et il s'y mit des vers, et cela devint infect... Tous les matins, chacun ramassait ce qu'il fallait pour sa nourriture.",
                "note": "La manne d'hier a expiré hier; la Parole doit être ramassée fraîche chaque matin.",
                "timestamp": "00:48:44"
              },
              {
                "reference": "1 Rois 18:33-38",
                "text": "Il arrangea le bois... Et le feu de l'Éternel tomba, et il consuma l'holocauste, le bois, les pierres et la terre, et il absorba l'eau qui était dans le fossé.",
                "note": "Élie a dû disposer le bois pour provoquer la chute du feu de Dieu, même par-dessus l'eau.",
                "timestamp": "00:50:51"
              },
              {
                "reference": "Éphésiens 6:18",
                "text": "Faites en tout temps par l'Esprit toutes sortes de prières et de supplications. Veillez à cela avec une entière persévérance, et priez pour tous les saints.",
                "note": "Cité avec 1 Thessaloniciens 5:17 comme le premier entretien du feu: prier sans cesse.",
                "timestamp": "00:53:03"
              },
              {
                "reference": "2 Timothée 2:15",
                "text": "Efforce-toi de te présenter devant Dieu comme un homme éprouvé, un ouvrier qui n'a point à rougir, qui dispense droitement la parole de la vérité.",
                "note": "Demeure dans la Parole: au lieu d'une heure de bavardage au téléphone, donne cette heure à la Parole.",
                "timestamp": "00:54:07"
              },
              {
                "reference": "Josué 1:8",
                "text": "Que ce livre de la loi ne s'éloigne point de ta bouche; médite-le jour et nuit... c'est alors que tu auras du succès.",
                "note": "La méditation des Écritures est ce qui transforme la fréquentation de la Parole en réussite.",
                "timestamp": "00:55:13"
              },
              {
                "reference": "Matthieu 7:7-8",
                "text": "Demandez, et l'on vous donnera; cherchez, et vous trouverez; frappez, et l'on vous ouvrira.",
                "note": "Retourné pour insister: celui qui ne demande pas ne reçoit rien, et si tu ne frappes pas, personne ne sait que tu es à la porte.",
                "timestamp": "00:57:21"
              },
              {
                "reference": "Luc 4:18",
                "text": "L'Esprit du Seigneur est sur moi, parce qu'il m'a oint pour annoncer une bonne nouvelle aux pauvres; il m'a envoyé pour guérir ceux qui ont le coeur brisé.",
                "note": "Le premier canal de l'impartation, c'est Dieu lui-même, qui fait directement ce qu'aucun homme ne peut faire.",
                "timestamp": "00:58:25"
              },
              {
                "reference": "Ézéchiel 2:2",
                "text": "Dès qu'il m'eut adressé ces mots, l'esprit entra en moi et me fit tenir sur mes pieds; et j'entendis celui qui me parlait.",
                "note": "Le deuxième canal: la parole prêchée porte elle-même l'impartation au moment où elle est annoncée.",
                "timestamp": "00:59:30"
              },
              {
                "reference": "1 Samuel 16:13",
                "text": "Samuel prit la corne d'huile, et l'oignit au milieu de ses frères. L'esprit de l'Éternel saisit David, à partir de ce jour et dans la suite.",
                "note": "Le troisième canal: les porteurs de la présence de Dieu, comme l'huile d'onction qui a ensuite circulé dans la salle.",
                "timestamp": "01:00:34"
              }
            ],
            "quotes": [
              {
                "quote": "Il n'y a pas d'homme ni de femme qui se soit fait tout seul dans le royaume. Nous sommes tous des produits de dons spirituels.",
                "timestamp": "00:37:56"
              },
              {
                "quote": "Tant que tu n'as pas soif et que tu n'es pas désespéré, il n'y aura pas d'impartation.",
                "timestamp": "00:39:00"
              },
              {
                "quote": "Celui qui porte ce dont tu as besoin peut être plus jeune que toi. Ne laisse pas l'orgueil t'en priver.",
                "timestamp": "01:03:50"
              },
              {
                "quote": "Tu peux être proche du roi, mais ne deviens pas trop familier avec le trône. Cela te priverait des bénédictions du roi.",
                "timestamp": "01:04:55"
              },
              {
                "quote": "La manne d'hier a expiré hier soir. Si tu ne veux pas sentir mauvais, le bois doit continuer de brûler chaque jour.",
                "timestamp": "00:48:44"
              }
            ]
          },
          "es": {
            "title": "Impartición para el avivamiento",
            "preacher": "El Pastor Nacional, Winners Chapel International Canada",
            "summary": "La gran final abrió con una promesa sencilla, Romanos 1:11, donde Pablo anhela ir para repartir algún don espiritual y así confirmar a los creyentes. La impartición, explicó el pastor nacional, es la transferencia de un don espiritual de un vaso a otro, y en el reino no existe hombre ni mujer que se haya hecho solo. Contó su propia historia: viajó de Toronto a Londres para el instituto bíblico, fue del aeropuerto directo al aula y del aula directo al aeropuerto, y luego se arrodilló en la oficina del pastor David Oyedepo para decirle que amaba lo que ese hombre llevaba y que lo quería. El orgullo es la razón por la que la mayoría nunca recibe, porque no se humillan ante quien lleva lo que necesitan, aunque sea más joven. De Levítico 6 pasó al fuego del altar que nunca debe apagarse. Todo creyente es sacerdote, y el fuego está ordenado para arder cada día, pero sin leña el fuego se apaga. La leña es la Palabra, recogida fresca cada mañana como el maná, porque el maná de ayer es veneno. Dio el combustible práctico: orar sin cesar, permanecer en la Palabra, meditar, escuchar la Palabra y orar mucho en el Espíritu. Cerró con los tres canales de la impartición, Dios mismo, la palabra predicada y los portadores de su presencia como el aceite de la unción, antes de que el aceite pasara por el auditorio y se orara sobre las tarjetas de expectativa.",
            "keyPoints": [
              "La impartición es la transferencia de un don espiritual de un vaso a otro; en el reino nadie se ha hecho solo.",
              "El propósito declarado de Pablo al repartir un don era confirmar, así que la impartición no solo te empodera, te establece.",
              "Hasta que no tengas sed y desesperación no habrá impartición; el orgullo es la razón por la que la mayoría nunca recibe.",
              "Quien lleva lo que necesitas puede ser más joven que tú; no dejes que el orgullo te lo robe.",
              "El fuego del altar está ordenado para arder cada día, y todo creyente es sacerdote, así que mantenerlo es tu propia responsabilidad.",
              "Sin leña se apaga el fuego. La leña es la Palabra de Dios, y hay que ponerla en el fuego cada mañana.",
              "El maná de ayer es veneno; recoge palabra fresca cada día, igual que el águila rechaza la comida vieja.",
              "Mantén el fuego orando sin cesar, estudiando y meditando la Palabra, escuchando la Palabra y orando mucho en el Espíritu.",
              "La impartición llega de tres maneras: directamente de Dios, por la palabra predicada y por portadores de su presencia como el aceite de la unción."
            ],
            "scriptures": [
              {
                "reference": "Romanos 1:11",
                "text": "Porque deseo veros, para repartir con vosotros algún don espiritual, para confirmaros.",
                "note": "Texto base; los dones espirituales se imparten, y el resultado de esa impartición es el establecimiento.",
                "timestamp": "00:36:53"
              },
              {
                "reference": "Levítico 6:12-13",
                "text": "Y el fuego encendido sobre el altar no ha de apagarse, sino que el sacerdote pondrá en él leña cada mañana... El fuego ha de arder continuamente en el altar; no se apagará.",
                "note": "El fuego sobre el sacerdote está ordenado a arder cada día, y nosotros somos los sacerdotes, así que mantenerlo nos toca a nosotros.",
                "timestamp": "00:44:23"
              },
              {
                "reference": "Proverbios 26:20",
                "text": "Sin leña se apaga el fuego: y donde no hay chismoso, cesa la contienda.",
                "note": "La leña es la Palabra de Dios, por eso una ingesta diaria y suficiente de la Palabra es lo que mantiene el fuego.",
                "timestamp": "00:47:39"
              },
              {
                "reference": "Éxodo 16:19-21",
                "text": "Mas algunos dejaron de ello para otro día, y crió gusanos, y pudrióse... Y recogíanlo cada mañana, cada uno según lo que había de comer.",
                "note": "El maná de ayer expiró ayer; la Palabra hay que recogerla fresca cada mañana.",
                "timestamp": "00:48:44"
              },
              {
                "reference": "1 Reyes 18:33-38",
                "text": "Y compuso la leña... Entonces cayó fuego de Jehová, el cual consumió el holocausto, y la leña, y las piedras, y el polvo, y aun lamió las aguas que estaban en la reguera.",
                "note": "Elías tuvo que poner la leña en orden para provocar que cayera el fuego de Dios, incluso sobre el agua.",
                "timestamp": "00:50:51"
              },
              {
                "reference": "Efesios 6:18",
                "text": "Orando en todo tiempo con toda deprecación y súplica en el Espíritu, y velando en ello con toda instancia y suplicación por todos los santos.",
                "note": "Citado junto a 1 Tesalonicenses 5:17 como lo primero que mantiene el fuego: orar sin cesar.",
                "timestamp": "00:53:03"
              },
              {
                "reference": "2 Timoteo 2:15",
                "text": "Procura con diligencia presentarte á Dios aprobado, como obrero que no tiene de qué avergonzarse, que traza bien la palabra de verdad.",
                "note": "Permanece en la Palabra: en vez de una hora de chismes por teléfono, dale esa hora a la Palabra.",
                "timestamp": "00:54:07"
              },
              {
                "reference": "Josué 1:8",
                "text": "El libro de aquesta ley nunca se apartará de tu boca: antes de día y de noche meditarás en él... y todo te saldrá bien.",
                "note": "La meditación en las Escrituras es lo que convierte el permanecer en la Palabra en prosperidad y buen éxito.",
                "timestamp": "00:55:13"
              },
              {
                "reference": "Mateo 7:7-8",
                "text": "Pedid, y se os dará; buscad, y hallaréis; llamad, y se os abrirá.",
                "note": "Invertido para enfatizar: el que no pide no recibe nada, y si no llamas, nadie sabe que estás a la puerta.",
                "timestamp": "00:57:21"
              },
              {
                "reference": "Lucas 4:18",
                "text": "El Espíritu del Señor es sobre mí, por cuanto me ha ungido para dar buenas nuevas á los pobres: me ha enviado para sanar á los quebrantados de corazón.",
                "note": "El primer canal de la impartición es Dios mismo, haciendo directamente lo que ningún hombre puede hacer.",
                "timestamp": "00:58:25"
              },
              {
                "reference": "Ezequiel 2:2",
                "text": "Y entró espíritu en mí luego que me habló, y afirmóme sobre mis pies, y oía al que me hablaba.",
                "note": "El segundo canal: la palabra predicada lleva impartición en el momento mismo en que se anuncia.",
                "timestamp": "00:59:30"
              },
              {
                "reference": "1 Samuel 16:13",
                "text": "Y Samuel tomó el cuerno del aceite, y ungiólo de entre sus hermanos: y desde aquel día en adelante el espíritu de Jehová tomó á David.",
                "note": "El tercer canal: los portadores de la presencia de Dios, como el aceite de la unción que luego pasó por el auditorio.",
                "timestamp": "01:00:34"
              }
            ],
            "quotes": [
              {
                "quote": "En el reino no hay hombre ni mujer que se haya hecho solo. Todos somos producto de dones espirituales.",
                "timestamp": "00:37:56"
              },
              {
                "quote": "Hasta que no tengas sed y desesperación, no habrá impartición.",
                "timestamp": "00:39:00"
              },
              {
                "quote": "Quien lleva lo que necesitas puede ser más joven que tú. No dejes que el orgullo te lo robe.",
                "timestamp": "01:03:50"
              },
              {
                "quote": "Puedes estar cerca del rey, pero no te familiarices demasiado con el trono. Te robaría las bendiciones del rey.",
                "timestamp": "01:04:55"
              },
              {
                "quote": "El maná de ayer expiró al cerrar el día de ayer. Si no quieres oler mal, la leña tiene que seguir ardiendo cada día.",
                "timestamp": "00:48:44"
              }
            ]
          },
          "creole": {
            "title": "Enpatasyon pou Rèvèy la",
            "preacher": "Pastè Nasyonal la, Winners Chapel International Canada",
            "summary": "Gran final la te louvri sou yon pwomès senp, Women 1:11, kote Pòl di li anvi vin bay yon don espirityèl pou kwayan yo ka afèmi. Enpatasyon, Pastè Nasyonal la esplike, se transfè yon don espirityèl soti nan yon veso pou al nan yon lòt, epi nan wayòm nan pa gen okenn gason ni fanm ki fè tèt li pou kont li. Li rakonte pwòp istwa pa l: li kite Toronto pou lekòl biblik nan Lond, li soti nan ayewopò a dirèk pou sal klas la epi soti nan sal klas la dirèk pou ayewopò a, epi apre sa li mete ajenou nan biwo Pastè David Oyedepo pou di l li renmen sa nonm sa a pote epi li vle l. Ògèy se rezon ki fè pifò moun pa janm resevwa, paske yo pa vle imilye tèt yo devan moun ki pote sa yo bezwen an, menm si moun nan pi jèn pase yo. Depi Levitik 6 li pase sou dife lotèl la ki pa dwe janm etenn. Chak kwayan se yon prèt, epi dife a fèt pou l boule chak jou, men kote pa gen bwa, dife a etenn. Bwa a se Pawòl la, ranmase fre chak maten tankou lamàn nan, paske lamàn ayè a se pwazon. Li bay konbistib pratik la: priye san rete, rete nan Pawòl la, medite, koute Pawòl la, epi priye anpil nan Lespri a. Li fèmen ak twa chemen enpatasyon an, Bondye li menm, pawòl yo preche a, ak moun ki pote prezans li tankou lwil onksyon an, anvan lwil la te pase nan sal la epi yo te lapriyè sou kat atant yo.",
            "keyPoints": [
              "Enpatasyon se transfè yon don espirityèl soti nan yon veso pou al nan yon lòt; nan wayòm nan pèsonn pa fè tèt li pou kont li.",
              "Rezon Pòl te bay pou l bay yon don se te afèmi moun yo, kidonk enpatasyon pa sèlman ba ou pouvwa, li etabli ou.",
              "Toutotan ou pa swaf epi ou pa dezespere, p ap gen enpatasyon; ògèy se rezon ki fè pifò moun pa janm resevwa.",
              "Moun ki pote sa ou bezwen an ka pi jèn pase ou; pa kite ògèy vòlè l nan men ou.",
              "Dife lotèl la fèt pou l boule chak jou, epi chak kwayan se yon prèt, kidonk kenbe l ap boule se responsablite pa ou.",
              "Kote pa gen bwa, dife a etenn. Bwa a se Pawòl Bondye a, epi li dwe mete sou dife a chak maten.",
              "Lamàn ayè a se pwazon; ranmase pawòl fre chak jou, menm jan malfini refize manje ki rasi.",
              "Kenbe dife a ap boule lè w priye san rete, etidye epi medite Pawòl la, koute Pawòl la, epi priye anpil nan Lespri a.",
              "Enpatasyon vini nan twa fason: dirèkteman nan men Bondye, atravè pawòl yo preche a, epi atravè moun ki pote prezans li tankou lwil onksyon an."
            ],
            "scriptures": [
              {
                "reference": "Women 1:11",
                "text": "Paske mwen anvi wè nou, pou m ka separe kèk don espirityèl ak nou, pou nou ka afèmi.",
                "note": "Tèks debaz la; don espirityèl yo bay, epi rezilta enpatasyon sa a se etablisman.",
                "timestamp": "00:36:53"
              },
              {
                "reference": "Levitik 6:12-13",
                "text": "Dife a va boule sou lotèl la, li p ap dwe etenn. Chak maten prèt la va mete bwa sou li... Dife a va toujou ap boule sou lotèl la, li p ap janm etenn.",
                "note": "Dife sou prèt la fèt pou l boule chak jou, epi se nou menm ki prèt yo, kidonk kenbe l se pou nou.",
                "timestamp": "00:44:23"
              },
              {
                "reference": "Pwovèb 26:20",
                "text": "Kote ki pa gen bwa, dife a etenn; epi kote ki pa gen moun k ap fè tripotay, kont lan sispann.",
                "note": "Bwa a se Pawòl Bondye a, se poutèt sa pran Pawòl la chak jou ase se sa ki kenbe dife a ap boule.",
                "timestamp": "00:47:39"
              },
              {
                "reference": "Egzòd 16:19-21",
                "text": "Kèk nan yo te kite ladan l jouk nan denmen maten, epi li te plen vè epi li te santi... Chak maten yo te ranmase l, chak moun dapre sa li te bezwen manje.",
                "note": "Lamàn ayè a te fini ayè; Pawòl la dwe ranmase fre chak maten.",
                "timestamp": "00:48:44"
              },
              {
                "reference": "1 Wa 18:33-38",
                "text": "Li ranje bwa a... Lè sa a dife Senyè a tonbe, li boule ofrann lan, bwa a, wòch yo ak pousyè a, epi li seche dlo ki te nan kanal la.",
                "note": "Eli te dwe ranje bwa a pou l pwovoke dife Bondye a tonbe, menm sou dlo.",
                "timestamp": "00:50:51"
              },
              {
                "reference": "Efezyen 6:18",
                "text": "Priye nan tout tan ak tout kalite lapriyè ak siplikasyon nan Lespri a, epi veye sou sa ak tout pèseverans pou tout moun ki mete apa pou Bondye.",
                "note": "Site ansanm ak 1 Tesalonisyen 5:17 kòm premye bagay ki kenbe dife a: priye san rete.",
                "timestamp": "00:53:03"
              },
              {
                "reference": "2 Timote 2:15",
                "text": "Fè jefò pou prezante tèt ou devan Bondye tankou yon moun ki pase eprèv, yon travayè ki pa bezwen wont, k ap separe pawòl verite a byen.",
                "note": "Rete nan Pawòl la: olye yon èdtan tripotay nan telefòn, bay Pawòl la èdtan sa a.",
                "timestamp": "00:54:07"
              },
              {
                "reference": "Jozye 1:8",
                "text": "Liv lalwa sa a p ap dwe kite bouch ou; men w ap medite sou li lajounen kou lannwit... se lè sa a w ap gen siksè.",
                "note": "Meditasyon sou Ekriti yo se sa ki fè rete nan Pawòl la vin tounen siksè.",
                "timestamp": "00:55:13"
              },
              {
                "reference": "Matye 7:7-8",
                "text": "Mande, y a ba ou; chèche, w a jwenn; frape, y a louvri pou ou.",
                "note": "Vire alanvè pou mete aksan: moun ki pa mande pa resevwa anyen, epi si w pa frape, pèsonn pa konnen ou nan pòt la.",
                "timestamp": "00:57:21"
              },
              {
                "reference": "Lik 4:18",
                "text": "Lespri Senyè a sou mwen, paske li chwazi m pou m anonse bòn nouvèl la bay pòv yo; li voye m geri moun ki gen kè brize.",
                "note": "Premye chemen enpatasyon an se Bondye li menm, k ap fè dirèkteman sa okenn moun pa ka fè.",
                "timestamp": "00:58:25"
              },
              {
                "reference": "Ezekyèl 2:2",
                "text": "Kou li fin pale avè m, Lespri a antre nan mwen, li fè m kanpe sou pye m, epi mwen tande moun ki t ap pale avè m nan.",
                "note": "Dezyèm chemen an: pawòl yo preche a pote enpatasyon nan moman menm y ap anonse l la.",
                "timestamp": "00:59:30"
              },
              {
                "reference": "1 Samyèl 16:13",
                "text": "Samyèl pran kòn lwil la, li vide l sou tèt David nan mitan frè li yo. Depi jou sa a, Lespri Senyè a te desann sou David.",
                "note": "Twazyèm chemen an: moun ki pote prezans Bondye, tankou lwil onksyon an ki te pase nan sal la apre sa.",
                "timestamp": "01:00:34"
              }
            ],
            "quotes": [
              {
                "quote": "Nan wayòm nan pa gen gason ni fanm ki fè tèt li pou kont li. Nou tout se pwodwi don espirityèl.",
                "timestamp": "00:37:56"
              },
              {
                "quote": "Toutotan ou pa swaf epi ou pa dezespere, p ap gen enpatasyon.",
                "timestamp": "00:39:00"
              },
              {
                "quote": "Moun ki pote sa ou bezwen an ka pi jèn pase ou. Pa kite ògèy vòlè l nan men ou.",
                "timestamp": "01:03:50"
              },
              {
                "quote": "Ou ka pre wa a, men pa vin twò abitye ak twòn nan. Sa ap vòlè benediksyon wa a nan men ou.",
                "timestamp": "01:04:55"
              },
              {
                "quote": "Lamàn ayè a te fini depi ayè aswè. Si ou pa vle santi, bwa a dwe kontinye boule chak jou.",
                "timestamp": "00:48:44"
              }
            ]
          }
        }
      }
    ]
  }
];

/** Shareable quote cards per message slug, in the same order as the message's
 * quotes array. Used by the day pages and the homepage quote carousel. */
const summitQuoteImages: Record<string, string[]> = {
  "day-2-morning-divine-wisdom-for-soaring-high": [
    "/images/summit/day-2-quotes/01-soaring-is-intentional.png",
    "/images/summit/day-2-quotes/02-staying-at-the-top-requires-wisdom.png",
    "/images/summit/day-2-quotes/03-wisdom-is-knowing-and-doing.png",
    "/images/summit/day-2-quotes/04-god-income-and-wisdom.png",
    "/images/summit/day-2-quotes/05-increase-your-capacity.png",
  ],
  "day-2-morning-living-the-word-for-soaring": [
    "/images/summit/day-2-quotes/06-what-is-written-is-eternal.png",
    "/images/summit/day-2-quotes/07-make-a-life-by-gods-ways.png",
    "/images/summit/day-2-quotes/08-word-increase.png",
    "/images/summit/day-2-quotes/09-truth-overrides-facts.png",
    "/images/summit/day-2-quotes/10-the-word-is-your-greatest-weapon.png",
  ],
  "day-1-understanding-the-ministry-of-the-holy-spirit-for-soaring": [
    "/yafrs/quotes/day-1/01-vision.png",
    "/yafrs/quotes/day-1/02-storms.png",
    "/yafrs/quotes/day-1/03-commitment.png",
    "/yafrs/quotes/day-1/04-honey-before-the-moon.png",
    "/yafrs/quotes/day-1/05-holy-spirit.png",
  ],
  "day-3-morning-engaging-the-power-of-faith": [
    "/images/summit/day-3/day3-faith-waiting.png",
    "/images/summit/day-3/day3-faith-must-have.png",
    "/images/summit/day-3/day3-faith-settles-it.png",
    "/images/summit/day-3/day3-faith-contrary-to-fact.png",
    "/images/summit/day-3/day3-faith-opportunities.png",
  ],
  "day-3-morning-obedience-and-stewardship-for-soaring": [
    "/images/summit/day-3/day3-obedience-tested.png",
    "/images/summit/day-3/day3-start-small.png",
  ],
  "day-3-evening-purity-in-relationships": [
    "/images/summit/day-3/day3-purity-reflects.png",
    "/images/summit/day-3/day3-standard-unchanged.png",
    "/images/summit/day-3/day3-love-vs-lust.png",
  ],
};

export function getSummitQuoteImage(slug: string, index: number) {
  return summitQuoteImages[slug]?.[index];
}

export type SummitQuoteCard = {
  src: string;
  day: number;
  portrait: boolean;
  quote: string;
};

/** Every quote card for the homepage carousel, round-robined across days so the
 * strip never shows a long run from a single day. */
export const summitQuoteCards: SummitQuoteCard[] = (() => {
  const byDay = summitBreakdownDays.map((day) =>
    day.messages.flatMap((message) =>
      (summitQuoteImages[message.slug] ?? []).map((src, index) => ({
        src,
        day: day.day,
        // Day 2's cards were exported portrait (1003x1568); the rest are square.
        portrait: day.day === 2,
        quote: message.quotes[index]?.quote ?? "",
      })),
    ),
  );

  const longest = Math.max(0, ...byDay.map((cards) => cards.length));
  const mixed: SummitQuoteCard[] = [];
  for (let i = 0; i < longest; i++) {
    for (const cards of byDay) {
      if (cards[i]) mixed.push(cards[i]);
    }
  }
  // ponytail: teaser, not the full set. The marquee duplicates this list into a
  // single very wide row, so every extra card is another full-size image loaded
  // on the homepage. Raise the cap if the strip starts feeling repetitive.
  return mixed.slice(0, 12);
})();

/** "HH:MM:SS" or "MM:SS" to total seconds, for YouTube start/t params. */
export function timestampToSeconds(timestamp: string): number {
  return timestamp
    .split(":")
    .reduce((total, part) => total * 60 + Number(part), 0);
}
